import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, getDocs, getDoc, addDoc, doc, setDoc, deleteDoc, query, orderBy, where, writeBatch, updateDoc, arrayRemove, arrayUnion, Timestamp, limit } from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth, signOut } from 'firebase/auth';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref as storageRef, listAll, getDownloadURL } from 'firebase/storage';
import { useLocation, useNavigate } from 'react-router-dom';
import { db, firebaseConfig, storage } from '../../../shared/storage/firebase';
import { useAuth } from '../../auth/contexts/AuthContext';
import ManageSquadUsersModal from './ManageSquadUsersModal';
import '../styles/AdminPanel.css';
import { PACKS } from '../../../data/packs';
import { User, Squad, TeacherBgCheckType, TeacherFirstClassObserved, TeacherSchoolClearanceItem, UploadedFileMeta } from '../../../types';
import { gameAPI, AchievementDefinition } from '../../games/services/GameAPI';
import { GAME_REGISTRY } from '../../games/models/GameRegistry';
import { MY_FAVORITE_MUSICIANS_ACHIEVEMENTS } from '../../games/data/myFavoriteMusiciansAchievements';
import { RHYTHM_RALLY_ACHIEVEMENTS } from '../../games/data/rhythmRallyAchievements';
import { RHYTHM_PUZZLE_ACHIEVEMENTS } from '../../games/data/rhythmPuzzleAchievements';
import { MELODY_SHAPER_ACHIEVEMENTS } from '../../games/data/melodyShaperAchievements';
import { TUNE_TESTERS_ACHIEVEMENTS } from '../../games/data/tuneTestersAchievements';
import { STEP_SKIP_REPEAT_ACHIEVEMENTS } from '../../games/data/stepSkipRepeatAchievements';
import FormEditor from './FormEditor';
import FeedbackViewer from './FeedbackViewer';
import StatisticsTab from './StatisticsTab';
import CampaignsTab from './CampaignsTab';
import CampaignResults from './CampaignResults';
import { FLAPPY_MUSE_ACHIEVEMENTS } from '../../games/data/flappyMuseAchievements';
import AdminInvoicesPage from '../../billing/components/AdminInvoicesPage';
import SquadCalendar from '../../squads/components/SquadCalendar';
import ScheduleEventModal from '../../squads/components/ScheduleEventModal';
import { createChicagoDate, DEFAULT_TZ } from '../../../shared/utils/timezone';
import { syncSquadCalendarFromDetails } from '../../squads/utils/calendarSync';
import { buildIntakeTemplate } from '../../ops/intake/intakeTemplate';
import StudentInfoModal from './StudentInfoModal';
import ManageParents from './ManageParents';
import ApplicantsAdmin from './ApplicantsAdmin';
import PayrollCalculatorModal from './PayrollCalculatorModal';
import { listCurriculumAdapters } from '../../curriculum/adapters/registry';
import GoogleMapsService from '../../../shared/services/GoogleMapsService';
import { programTermForDate, programTermLabelFromId } from '../../../shared/terms/programTerms';
import { computeTotalClassesFromSchedule } from '../../../shared/billing/squadInvoiceRollup';
import type { Partner, PartnerLocation } from '../../partners/types';
import PartnerListPage from '../../partners/components/PartnerListPage';
import { listPartnerSeasons, listPartners } from '../../partners/services/partnerRepository';
import { inferSeasonFromStartDate } from '../../partners/utils/seasonAssignment';
import { dayCodeForYmd } from '../../squads/utils/singleDaySchedule';
import AdminLearningCenterTab from './AdminLearningCenterTab';
import AdminCurriculumTab from './AdminCurriculumTab';
import AdminExportsTab from './AdminExportsTab';
import AdminTodosPage from './AdminTodosPage';
import CoriBatchExportModal from './CoriBatchExportModal';

type SeederFunction = () => Promise<void>;

// TS sometimes fails to rehydrate props for this large file + HMR; keep this tab resilient.
const AdminInvoicesPageAny = AdminInvoicesPage as unknown as React.FC<any>;

// Default staff user(s) to auto-assign to every newly created squad.
// Uses email lookup (instead of displayName) to avoid name collisions.
const DEFAULT_SQUAD_TEACHER_EMAIL = 'aspen@overtureai.org';

const normalizeSquadLifecycleStatus = (v: unknown): string => String(v || '').trim().toLowerCase();

type CertificationWaitlistEntry = {
  id: string;
  certificationIds?: string[];
  certificationTitles?: string[];
  certificationId?: string;
  certificationTitle?: string;
  certificationType?: 'application' | 'paid' | string;
  name?: string;
  email?: string;
  reason?: string;
  source?: string;
  createdAt?: Timestamp | Date | string | null;
};

const waitlistCertificationTitles = (entry: CertificationWaitlistEntry): string[] => {
  if (Array.isArray(entry.certificationTitles) && entry.certificationTitles.length > 0) {
    return entry.certificationTitles.filter((title): title is string => typeof title === 'string' && title.trim().length > 0);
  }
  return entry.certificationTitle ? [entry.certificationTitle] : entry.certificationId ? [entry.certificationId] : [];
};

// Teachers rarely have a top-level `state`; it usually lives in shippingAddress
// (or as free text in neighborhood/address), so check each in turn.
const deriveTeacherState = (t: unknown): string => {
  const teacher = t as any;
  const direct = String(teacher?.state || '').trim();
  if (direct) return direct.toUpperCase();
  const shipState = String(teacher?.shippingAddress?.state || '').trim();
  if (shipState) return shipState.toUpperCase();
  const loc = String(teacher?.neighborhood ?? teacher?.address ?? '').trim();
  if (!loc) return '';
  // e.g. "Lincoln Park, Chicago, IL" -> IL
  const last = loc.split(',').map(p => p.trim()).filter(Boolean).at(-1) || '';
  const m = last.match(/\b([A-Za-z]{2})\b/);
  return m ? m[1].toUpperCase() : '';
};

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();
  const [activeTab, setActiveTab] = useState<'todos' | 'statistics' | 'invoicing' | 'users' | 'achievements' | 'ab-testing' | 'calendar' | 'learning' | 'curriculum' | 'exports'>('todos');
  const [invoicingSchoolId, setInvoicingSchoolId] = useState<string | null>(null);
  const [activeUserTab, setUserTab] = useState<'partners' | 'squads' | 'students' | 'teachers' | 'parents' | 'waitlist' | 'applicants'>('partners');
  const [activeSubTab, setActiveSubTab] = useState<'forms' | 'feedback' | 'campaigns' | 'results'>('forms');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showCreateSquadForm, setShowCreateSquadForm] = useState(false);
  const [squadCreationPartnerId, setSquadCreationPartnerId] = useState<string | null>(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [hasLoadedOpsFlowsIndex, setHasLoadedOpsFlowsIndex] = useState(false);
  const [hasLoadedInitialAchievements, setHasLoadedInitialAchievements] = useState(false);
  
  // Data states
  const [squads, setSquads] = useState<Squad[]>([]);
  const [opsFlowExistsBySquadId, setOpsFlowExistsBySquadId] = useState<Record<string, boolean>>({});
  const [creatingFlowSquadId, setCreatingFlowSquadId] = useState<string | null>(null);
  const [duplicatingSquadId, setDuplicatingSquadId] = useState<string | null>(null);
  const [students, setStudents] = useState<User[]>([]);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [parents, setParents] = useState<User[]>([]);
  const [certificationWaitlist, setCertificationWaitlist] = useState<CertificationWaitlistEntry[]>([]);
  const [partnerLocations, setPartnerLocations] = useState<PartnerLocation[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [achievements, setAchievements] = useState<{[gameId: string]: AchievementDefinition[]}>({});
  const [selectedGame, setSelectedGame] = useState<string>('');

  const partnerNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const partner of partners) {
      const name = String(partner.name || '').trim();
      if (partner.id && name) map[partner.id] = name;
    }
    return map;
  }, [partners]);

  const squadPlaceLabel = useCallback((squad: Squad) => {
    const locationName = String(squad.locationName || '').trim();
    if (locationName) return locationName;
    const partnerId = String(squad.partnerId || '').trim();
    const partnerName = (partnerId && partnerNameById[partnerId])
      || String((squad as any).partnerName || '').trim();
    if (partnerName) return partnerName;
    return 'No location';
  }, [partnerNameById]);

  const searchParams = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const deepLinkApplicantId = String(
    searchParams.get('applicantId') || ((location.state || {}) as any)?.applicantId || ''
  ).trim();

  // Allow other pages (like Teacher Availability) to deep-link into Admin -> Users -> Teachers.
  useEffect(() => {
    const st = (location.state || {}) as any;
    const tabFromQuery = String(searchParams.get('adminTab') || '').trim();
    const userTabFromQuery = String(searchParams.get('adminUserTab') || '').trim();
    if (st?.adminTab || tabFromQuery) setActiveTab((st?.adminTab || tabFromQuery) as any);
    const nextUserTab = st?.adminUserTab || userTabFromQuery;
    if (nextUserTab) setUserTab(nextUserTab === 'schools' ? 'partners' : nextUserTab);
    if (deepLinkApplicantId) {
      setActiveTab('users');
      setUserTab('applicants');
    }
    if (st?.openCreateSquadForm) {
      setUserTab('squads');
      setShowCreateSquadForm(true);
      setSquadCreationPartnerId(String(st.createSquadPartnerId || '') || null);
      setSquadForm((current) => ({
        ...current,
        locationId: String(st.createSquadLocationId || ''),
        schoolId: String(st.createSquadSchoolId || ''),
        termId: String(st.createSquadSeasonId || current.termId),
      }));
    }
    if (st?.invoicingSchoolId) {
      setInvoicingSchoolId(String(st.invoicingSchoolId));
      setActiveTab('invoicing');
    }
    // Clear one-time navigation state so refreshes don't keep forcing tabs.
    if (st?.adminTab || st?.adminUserTab || st?.invoicingSchoolId || st?.openCreateSquadForm) {
      navigate(location.pathname + (location.search || ''), { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [editingAchievement, setEditingAchievement] = useState<AchievementDefinition | null>(null);
  const [editingStatusSquadId, setEditingStatusSquadId] = useState<string | null>(null);
  const [editingTitleSquadId, setEditingTitleSquadId] = useState<string | null>(null);
  const [editingCurriculumSquadId, setEditingCurriculumSquadId] = useState<string | null>(null);
  const [editingPaymentModeSquadId, setEditingPaymentModeSquadId] = useState<string | null>(null);
  const [editingPriceSquadId, setEditingPriceSquadId] = useState<string | null>(null);
  const [editingPriceSquadValue, setEditingPriceSquadValue] = useState<string>('');
  const [savingPriceSquadId, setSavingPriceSquadId] = useState<string | null>(null);
  const [editingTitleSquadValue, setEditingTitleSquadValue] = useState<string>('');
  const [savingTitleSquadId, setSavingTitleSquadId] = useState<string | null>(null);

  // Modal state
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [selectedSquad, setSelectedSquad] = useState<Squad | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedDateForModal, setSelectedDateForModal] = useState<Date | null>(null);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [isPayrollModalOpen, setIsPayrollModalOpen] = useState(false);
  const [squadToDelete, setSquadToDelete] = useState<Squad | null>(null);
  const [deletingSquadId, setDeletingSquadId] = useState<string | null>(null);
  const [studentToEdit, setStudentToEdit] = useState<User | null>(null);
  const [studentSquadFilter, setStudentSquadFilter] = useState<string>('all');
  const [studentParentFilter, setStudentParentFilter] = useState<string>('');
  const [studentSchoolFilter, setStudentSchoolFilter] = useState<string>('');
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [editingPickupStudentId, setEditingPickupStudentId] = useState<string | null>(null);
  const [pickupDraftOptions, setPickupDraftOptions] = useState<string[]>([]);
  const [pickupDraftOtherText, setPickupDraftOtherText] = useState<string>('');
  const [savingPickup, setSavingPickup] = useState(false);
  const [squadStatusFilter, setSquadStatusFilter] = useState<string>('all');
  const [isCreatingTeacher, setIsCreatingTeacher] = useState(false);
  const [isCreatingStudent, setIsCreatingStudent] = useState(false);
  const [isCreatingPartner, setIsCreatingPartner] = useState(false);
  const [isSendingWeeklyReport, setIsSendingWeeklyReport] = useState(false);
  const [isSendingWeeklyReportNextWeek, setIsSendingWeeklyReportNextWeek] = useState(false);
  const [parentConsents, setParentConsents] = useState<Record<string, boolean | null>>({});
  // Teacher editing state
  const [editingTeacherCell, setEditingTeacherCell] = useState<{ teacherId: string; field: string } | null>(null);
  const [editingTeacherValue, setEditingTeacherValue] = useState<any>('');
  const [savingTeacher, setSavingTeacher] = useState(false);

  // Teachers table filters (Users -> Teachers)
  const [teacherStateFilter, setTeacherStateFilter] = useState<string>('all');
  const [teacherHasCarFilter, setTeacherHasCarFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [teacherSubjectFilter, setTeacherSubjectFilter] = useState<string>('all');
  const [teacherStaffStatusFilter, setTeacherStaffStatusFilter] = useState<'all' | 'active' | 'sub' | 'inactive' | 'applicant' | 'offboarded'>('active');

  // Default to Active when opening Teachers tab.
  useEffect(() => {
    if (activeUserTab === 'teachers') {
      setTeacherStaffStatusFilter('active');
    }
  }, [activeUserTab]);

  const teacherStateOptions = useMemo(() => {
    const set = new Set<string>();
    teachers.forEach(t => {
      const s = deriveTeacherState(t);
      if (s) set.add(s);
    });
    return Array.from(set).sort();
  }, [teachers]);

  const teacherSubjectOptions = useMemo(() => {
    const set = new Set<string>();
    teachers.forEach(t => {
      const subs = Array.isArray((t as any).subjects) ? (t as any).subjects : [];
      subs.forEach((s: any) => {
        const v = String(s || '').trim();
        if (v) set.add(v);
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [teachers]);

  const filteredTeachers = useMemo(() => {
    return teachers.filter(t => {
      const staff = (t.staffStatus as any) || 'active';
      if (teacherStaffStatusFilter !== 'all' && staff !== teacherStaffStatusFilter) return false;

      const state = deriveTeacherState(t);
      if (teacherStateFilter !== 'all' && state !== teacherStateFilter) return false;

      const hasCar = !!(t as any).hasCar;
      if (teacherHasCarFilter === 'yes' && !hasCar) return false;
      if (teacherHasCarFilter === 'no' && hasCar) return false;

      if (teacherSubjectFilter !== 'all') {
        const subs = Array.isArray((t as any).subjects) ? (t as any).subjects : [];
        const match = subs.some((s: any) => String(s || '').trim().toLowerCase() === teacherSubjectFilter.toLowerCase());
        if (!match) return false;
      }

      return true;
    });
  }, [teachers, teacherHasCarFilter, teacherStaffStatusFilter, teacherStateFilter, teacherSubjectFilter]);

  type GooglePlacePrediction = {
    description: string;
    placeId: string;
  };

  const [neighborhoodGooglePredictions, setNeighborhoodGooglePredictions] = useState<GooglePlacePrediction[]>([]);
  const [neighborhoodGoogleLoading, setNeighborhoodGoogleLoading] = useState(false);
  const [neighborhoodGoogleError, setNeighborhoodGoogleError] = useState<string | null>(null);
  const googleMapsLoadPromiseRef = useRef<Promise<void> | null>(null);

  const normalizeGoogleNeighborhoodLabel = useCallback((description: string) => {
    const raw = String(description || '').trim();
    return raw
      .replace(/,\s*USA\s*$/i, '')
      .replace(/,\s*United States\s*$/i, '')
      .replace(/,\s*United States of America\s*$/i, '')
      .trim();
  }, []);

  const ensureGooglePlacesReady = useCallback(async () => {
    if (GoogleMapsService.isApiLoaded()) return;
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey || apiKey === 'YOUR_API_KEY') {
      throw new Error('Google Maps API key not configured (REACT_APP_GOOGLE_MAPS_API_KEY).');
    }
    if (!googleMapsLoadPromiseRef.current) {
      googleMapsLoadPromiseRef.current = GoogleMapsService.load(apiKey);
    }
    await googleMapsLoadPromiseRef.current;
  }, []);

  const isEditingNeighborhood =
    editingTeacherCell?.field === 'neighborhood' && editingTeacherCell?.teacherId != null;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!isEditingNeighborhood) {
        setNeighborhoodGooglePredictions([]);
        setNeighborhoodGoogleLoading(false);
        setNeighborhoodGoogleError(null);
        return;
      }

      const query = String(editingTeacherValue || '').trim();
      const zipDigits = query.replace(/\D/g, '');
      const lookupInputs = (() => {
        const q = String(query || '').trim();
        if (!q) return [] as string[];
        const inputs = [q];
        // If a full ZIP was entered, also query the 3-digit prefix
        // so nearby ZIP suggestions show up (not exact-match only).
        if (zipDigits.length >= 5) {
          const prefix3 = zipDigits.slice(0, 3);
          if (prefix3 && prefix3 !== q) inputs.push(prefix3);
        }
        return Array.from(new Set(inputs.map(s => s.trim()).filter(Boolean)));
      })();
      if (query.length < 2) {
        setNeighborhoodGooglePredictions([]);
        setNeighborhoodGoogleLoading(false);
        setNeighborhoodGoogleError(null);
        return;
      }

      setNeighborhoodGoogleLoading(true);
      setNeighborhoodGoogleError(null);

      try {
        await ensureGooglePlacesReady();
        const g = (window as any).google;

        // Prefer Places API (New): google.maps.places.AutocompleteSuggestion
        const fetchAutocompleteSuggestions =
          g?.maps?.places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions;

        if (typeof fetchAutocompleteSuggestions === 'function') {
          const gathered: any[] = [];
          for (const input of lookupInputs) {
            const resp = await fetchAutocompleteSuggestions({
              input,
              // Restrict to US. (If the library doesn't support this option in your loaded version,
              // it will be ignored or throw; we catch and still allow freeform typing.)
              includedRegionCodes: ['us'],
            });
            if (cancelled) return;
            const suggestions = Array.isArray(resp?.suggestions) ? resp.suggestions : [];
            gathered.push(...suggestions);
          }

          const mapped: GooglePlacePrediction[] = gathered
            .map((s: any) => {
              const pp = s?.placePrediction ?? s?.prediction ?? s;
              const placeId = String(pp?.placeId || pp?.place_id || '');
              const text =
                pp?.text?.text ||
                pp?.text ||
                pp?.description ||
                pp?.structuredFormat?.mainText?.text ||
                pp?.structured_formatting?.main_text ||
                '';
              const secondary =
                pp?.structuredFormat?.secondaryText?.text ||
                pp?.structured_formatting?.secondary_text ||
                '';
              const description = String(
                text && secondary ? `${text}, ${secondary}` : text || secondary || ''
              );
              return { description, placeId };
            })
            .filter((p: GooglePlacePrediction) => p.placeId.length > 0 && p.description.length > 0);

          const byId = new Map<string, GooglePlacePrediction>();
          mapped.forEach((p) => {
            if (!byId.has(p.placeId)) byId.set(p.placeId, p);
          });
          const next = Array.from(byId.values()).slice(0, 8);

          setNeighborhoodGooglePredictions(next);
          setNeighborhoodGoogleLoading(false);
          return;
        }

        // Fallback to legacy AutocompleteService (may be disabled in newer projects).
        const LegacySvc = g?.maps?.places?.AutocompleteService;
        if (typeof LegacySvc === 'function') {
          const svc = new LegacySvc();
          const getPredictionsForInput = (input: string): Promise<GooglePlacePrediction[]> =>
            new Promise((resolve) => {
              svc.getPlacePredictions(
                {
                  input,
                  components: { country: 'us' },
                  // "geocode" is broad enough to include neighborhoods + cities; Google doesn't expose a dedicated "neighborhood" type.
                  types: ['geocode'],
                },
                (preds: any[], status: string) => {
                  const ok = status === g.maps.places.PlacesServiceStatus.OK;
                  if (!ok || !Array.isArray(preds)) {
                    resolve([]);
                    return;
                  }
                  const next: GooglePlacePrediction[] = preds
                    .map((p: any) => ({
                      description: String(p.description || ''),
                      placeId: String(p.place_id || ''),
                    }))
                    .filter(p => p.placeId.length > 0 && p.description.length > 0);
                  resolve(next);
                }
              );
            });

          const gathered: GooglePlacePrediction[] = [];
          for (const input of lookupInputs) {
            const next = await getPredictionsForInput(input);
            if (cancelled) return;
            gathered.push(...next);
          }
          const byId = new Map<string, GooglePlacePrediction>();
          gathered.forEach((p) => {
            if (!byId.has(p.placeId)) byId.set(p.placeId, p);
          });
          setNeighborhoodGooglePredictions(Array.from(byId.values()).slice(0, 8));
          setNeighborhoodGoogleLoading(false);
          return;
        }

        // Neither API available: do nothing (freeform typing still works).
        setNeighborhoodGooglePredictions([]);
        setNeighborhoodGoogleLoading(false);
      } catch (e: any) {
        if (cancelled) return;
        setNeighborhoodGooglePredictions([]);
        setNeighborhoodGoogleLoading(false);
        setNeighborhoodGoogleError(e?.message ? String(e.message) : 'Failed to load Google Places.');
      }
    };

    const t = window.setTimeout(run, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [isEditingNeighborhood, editingTeacherValue, ensureGooglePlacesReady]);
  // Squad filters
  const [squadStateFilter, setSquadStateFilter] = useState<string>('');
  const [squadStartFilter, setSquadStartFilter] = useState<string>(''); // YYYY-MM-DD
  const [squadEndFilter, setSquadEndFilter] = useState<string>(''); // YYYY-MM-DD

  // TEMP: Touch all squads to trigger onSquadWrite and ensure they are public
  const bulkPublishAllSquads = async () => {
    if (squads.length === 0) {
      setMessage('ℹ️ No squads loaded to publish.');
      return;
    }
    if (!await window.appConfirm(`This will update ${squads.length} squads to ensure they are public and trigger onSquadWrite. Continue?`)) {
      return;
    }
    setLoading(true);
    setMessage('');
    let ok = 0;
    let fail = 0;
    try {
      for (const s of squads) {
        try {
          const ref = doc(db, 'squads', s.id);
          await updateDoc(ref, {
            visibility: 'public',
            // touch field to force a write even if already public
            updatedAt: Timestamp.now(),
          } as any);
          ok++;
        } catch (e) {
          console.error('Failed updating squad', s.id, e);
          fail++;
        }
      }
      setMessage(`✅ Bulk publish complete. Updated ${ok}/${squads.length}${fail ? `, failed ${fail}` : ''}.`);
      await loadSquads();
    } catch (err) {
      console.error('Bulk publish error', err);
      setMessage('❌ Bulk publish failed. See console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleSquadTitleUpdate = async (squadId: string, rawTitle: string) => {
    const nextTitle = String(rawTitle || '').trim();
    if (!squadId) return;
    if (!nextTitle) {
      setMessage('❌ Squad name cannot be empty.');
      return;
    }

    const cur = (squads || []).find(s => s.id === squadId);
    if (cur && String(cur.title || '').trim() === nextTitle) {
      setEditingTitleSquadId(null);
      setEditingTitleSquadValue('');
      return;
    }

    setSavingTitleSquadId(squadId);
    setMessage('');
    try {
      const squadRef = doc(db, 'squads', squadId);
      await updateDoc(squadRef, {
        title: nextTitle,
        updatedAt: Timestamp.now(),
      } as any);
      setSquads(prev => (prev || []).map(s => (s.id === squadId ? { ...s, title: nextTitle } : s)));
      setEditingTitleSquadId(null);
      setEditingTitleSquadValue('');
      setMessage('✅ Squad name updated.');
    } catch (error) {
      console.error('Error updating squad title:', error);
      setMessage('❌ Failed to update squad name.');
    } finally {
      setSavingTitleSquadId(null);
    }
  };
  
  const curriculumOptions = useMemo(() => listCurriculumAdapters().map(a => a.id), []);

  const defaultProgramTerm = useMemo(() => programTermForDate(new Date()), []);
  
  const allMuses = useMemo(() => {
    return Object.values(PACKS).flatMap(pack => pack.muses);
  }, []);
  
  // --- State extraction helpers for filtering by school state (e.g., IL, MA) ---
  const US_STATE_CODES = useMemo(() => new Set([
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
    'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
    'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
  ]), []);
  const extractStateFromAddress = useCallback((address?: string | null): string | null => {
    if (!address) return null;
    // Try common pattern "... , ST 60614" or "... , City, ST"
    const parts = address.split(',').map(p => p.trim());
    // Check last two segments for a 2-letter code
    for (let i = parts.length - 1; i >= 0 && i >= parts.length - 3; i--) {
      const segment = parts[i] || '';
      // Take first token of the segment and test for 2-letter code
      const token = (segment.split(/\s+/)[0] || '').toUpperCase();
      if (US_STATE_CODES.has(token)) return token;
      // Or try to find a 2-letter code inside the segment
      const match = segment.toUpperCase().match(/\b([A-Z]{2})\b/);
      if (match && US_STATE_CODES.has(match[1])) return match[1];
    }
    return null;
  }, [US_STATE_CODES]);
  const getLocationState = useCallback((location: PartnerLocation | undefined | null): string | null => {
    if (!location) return null;
    const explicit = ((location as any).state as string | undefined)?.toUpperCase();
    if (explicit && US_STATE_CODES.has(explicit)) return explicit;
    return extractStateFromAddress(location.address || '');
  }, [US_STATE_CODES, extractStateFromAddress]);
  const availableStates = useMemo(() => Array.from(new Set(
    partnerLocations.map(getLocationState).filter((state): state is string => Boolean(state))
  )).sort(), [partnerLocations, getLocationState]);

  // Form states
  const [squadForm, setSquadForm] = useState({
    title: '',
    notes: '',
    locationName: '',
    price: '',
    currency: 'USD',
    paymentMode: 'stripe',
    termId: defaultProgramTerm.id,
    status: 'draft',
    capacity: 12,
    minEnroll: 8,
    waitlistEnabled: true,
    schoolId: '',
    locationId: '',
    curriculumId: 'intro-ai-kids-2026',
    startGrade: '',
    endGrade: '',
    regularTeacher: '',
    timezone: DEFAULT_TZ,
    schedule: {
      dayOfWeek: 'MON',
      startTime: '',
      endTime: '',
      startDate: '',
      endDate: '',
      exceptions: [] as string[],
      makeups: [] as string[],
    },
  });

  useEffect(() => {
    getDocs(query(collection(db, 'locations'), orderBy('name')))
      .then((snapshot) => setPartnerLocations(snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as PartnerLocation))))
      .catch((error) => console.warn('[AdminPanel] Failed to load canonical locations', error));
    listPartners()
      .then(setPartners)
      .catch((error) => console.warn('[AdminPanel] Failed to load partners', error));
  }, []);
  
  const [studentForm, setStudentForm] = useState({
    displayName: '',
    museCash: 50,
    selectedMuseName: 'Songly',
  });
  
  const [teacherForm, setTeacherForm] = useState({
    email: '',
    displayName: '',
    password: 'teacher123', // Default password
    isAdmin: false // Admin privileges
  });

  const [partnerForm, setPartnerForm] = useState({
    email: '',
    displayName: '',
    phoneNumber: '',
    password: 'partner123', // Default password
  });
  const [achievementForm, setAchievementForm] = useState<Partial<AchievementDefinition>>({
    id: '',
    title: '',
    description: '',
    iconUrl: '🏆',
    museCashReward: 50,
    type: 'milestone',
    rarity: 'common',
    implemented: true,
    sortOrder: 1,
    category: 'progression'
  });

  const [downloadingTomlsKey, setDownloadingTomlsKey] = useState<string | null>(null);

  const handleDownloadAllAchievements = () => {
    if (Object.keys(achievements).length === 0) {
      setMessage('❌ No achievement data has been loaded from Firestore. Try refreshing the data first.');
      return;
    }

    // Filter out games that have no achievements to keep the JSON file clean.
    const achievementsToExport: { [gameId: string]: AchievementDefinition[] } = {};
    for (const gameId in achievements) {
      if (achievements[gameId] && achievements[gameId].length > 0) {
        achievementsToExport[gameId] = achievements[gameId];
      }
    }

    if (Object.keys(achievementsToExport).length === 0) {
      setMessage('ℹ️ No achievements were found in Firestore for any game.');
      return;
    }

    const jsonString = JSON.stringify(achievementsToExport, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'allAchievementsFromFirestore.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setMessage('✅ SUCCESS! allAchievementsFromFirestore.json has been downloaded.');
  };

  const downloadTomlsUnderPath = async (opts: {
    basePath: string;
    key: string;
    filePrefix: string;
    label: string;
  }) => {
    if (downloadingTomlsKey) return;
    setDownloadingTomlsKey(opts.key);
    setMessage(`⏳ Scanning ${opts.label} folders for .toml files…`);

    const baseRef = storageRef(storage, opts.basePath);

    try {
      // Recursively list Storage folders.
      const listAllRecursive = async (r: ReturnType<typeof storageRef>): Promise<any[]> => {
        const res = await listAll(r);
        const files = [...res.items];
        for (const p of res.prefixes) {
          const nested = await listAllRecursive(p);
          files.push(...nested);
        }
        return files;
      };

      const allItems = await listAllRecursive(baseRef);
      const tomlItems = allItems.filter((i: any) => String(i?.name || '').toLowerCase().endsWith('.toml'));

      if (tomlItems.length === 0) {
        setMessage(`ℹ️ No .toml files found under ${opts.label}.`);
        return;
      }

      setMessage(`⏳ Downloading ${tomlItems.length} .toml file${tomlItems.length === 1 ? '' : 's'}…`);

      // Download each file as a Blob so we can reliably save it.
      // (Filenames include path segments to avoid collisions.)
      const basePrefix = `${opts.basePath}/`;
      let ok = 0;
      let failed = 0;

      for (let idx = 0; idx < tomlItems.length; idx++) {
        const item: any = tomlItems[idx];
        try {
          const url = await getDownloadURL(item);
          const resp = await fetch(url);
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const blob = await resp.blob();
          const objectUrl = URL.createObjectURL(blob);

          const rel = String(item.fullPath || item.name || `file_${idx}.toml`).replace(basePrefix, '');
          const safeName = `${opts.filePrefix}_${rel.split('/').join('_')}`;

          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = safeName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(objectUrl);
          ok += 1;
        } catch (e) {
          console.warn('[AdminPanel] Failed downloading toml:', item?.fullPath, e);
          failed += 1;
        }
      }

      setMessage(
        failed === 0
          ? `✅ Downloaded ${ok} .toml file${ok === 1 ? '' : 's'} from ${opts.label}.`
          : `⚠️ Downloaded ${ok} file${ok === 1 ? '' : 's'}; ${failed} failed. (See console for details.)`
      );
    } catch (err) {
      console.error('[AdminPanel] .toml export failed:', err);
      setMessage(`❌ Failed to download ${opts.label} .toml files. (See console for details.)`);
    } finally {
      setDownloadingTomlsKey(null);
    }
  };

  const handleDownloadRocketRaceTomls = async () =>
    downloadTomlsUnderPath({
      basePath: 'gameData/rocket-race',
      key: 'rocket-race',
      filePrefix: 'rocket-race',
      label: 'rocket-race',
    });

  const handleDownloadRealOrAiTomls = async () =>
    downloadTomlsUnderPath({
      basePath: 'gameData/real-or-ai',
      key: 'real-or-ai',
      filePrefix: 'real-or-ai',
      label: 'real-or-ai',
    });

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSquads(),
        loadStudents(),
        loadTeachers(),
        loadCertificationWaitlist(),
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage('❌ Error loading data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const loadAchievements = async () => {
    try {
      const achievementsData: {[gameId: string]: AchievementDefinition[]} = {};

      // Load achievements in parallel (not sequential) to reduce startup latency.
      const gameIds = Object.keys(GAME_REGISTRY);
      const results = await Promise.all(
        gameIds.map(async (gameId) => {
          try {
            const gameAchievements = await gameAPI.getGameAchievementDefinitions(gameId);
            return [gameId, gameAchievements] as const;
          } catch (_error) {
            console.log(`No achievements found for game: ${gameId}`);
            return [gameId, [] as AchievementDefinition[]] as const;
          }
        })
      );
      results.forEach(([gameId, gameAchievements]) => {
        achievementsData[gameId] = gameAchievements;
      });

      setAchievements(achievementsData);
    } catch (error) {
      console.error('Error loading achievements:', error);
    }
  };

  const loadSquads = async () => {
    const squadsRef = collection(db, 'squads');
    const q = query(squadsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    const squadsData = snapshot.docs.map(doc => {
      const data = doc.data() as Omit<Squad, 'id'>;
      return { id: doc.id, ...data } as Squad;
    });
    setSquads(squadsData);
  };

  const loadOpsFlowsIndex = async () => {
    try {
      const snap = await getDocs(collection(db, 'partnerSeasonFlows'));
      const next: Record<string, boolean> = {};
      snap.docs.forEach((d) => {
        const squadIds = Array.isArray((d.data() as any)?.squadIds) ? (d.data() as any).squadIds : [];
        squadIds.forEach((squadId: any) => { next[String(squadId)] = true; });
      });
      setOpsFlowExistsBySquadId(next);
    } catch (e) {
      console.warn('[AdminPanel] Failed loading opsFlows index', e);
      setOpsFlowExistsBySquadId({});
    } finally {
      setHasLoadedOpsFlowsIndex(true);
    }
  };

  useEffect(() => {
    if (activeUserTab === 'squads' && !hasLoadedOpsFlowsIndex) {
      void loadOpsFlowsIndex();
    }
  }, [activeUserTab, hasLoadedOpsFlowsIndex]);

  useEffect(() => {
    if (activeTab === 'achievements' && !hasLoadedInitialAchievements) {
      void loadAchievements().finally(() => setHasLoadedInitialAchievements(true));
    }
  }, [activeTab, hasLoadedInitialAchievements]);

  const handleCreateFlowForSquad = async (squad: Squad) => {
    const squadId = String((squad as any)?.id || '').trim();
    if (!squadId) return;
    if (opsFlowExistsBySquadId[squadId]) return;

    setCreatingFlowSquadId(squadId);
    try {
      const partnerId = String((squad as any)?.partnerId || '').trim();
      const seasonId = String((squad as any)?.seasonId || (squad as any)?.termId || '').trim();
      const locationId = String((squad as any)?.locationId || '').trim();
      if (!partnerId || !seasonId) throw new Error('Squad must have partnerId and seasonId before creating an Ops flow.');
      const ref = doc(db, 'partnerSeasonFlows', `${partnerId}__${seasonId}`);
      const existing = await getDoc(ref);
      if (existing.exists() && Array.isArray((existing.data() as any)?.squadIds)
        && (existing.data() as any).squadIds.includes(squadId)) {
        setOpsFlowExistsBySquadId((prev) => ({ ...prev, [squadId]: true }));
        return;
      }

      const nowMs = Date.now();
      const createdBy = String(userData?.id || '');
      const intakeSnapshot = buildIntakeTemplate({ createdAt: nowMs, createdBy });

      const checkpointState: Record<string, any> = {};
      const taskState: Record<string, any> = {};
      for (const cp of intakeSnapshot.checkpoints || []) {
        checkpointState[cp.checkpointId] = { status: 'not_started' };
        for (const t of cp.tasks || []) {
          taskState[t.taskId] = { status: 'open' };
        }
      }

      await setDoc(ref, {
        partnerId,
        seasonId,
        squadIds: arrayUnion(squadId),
        locationIds: locationId ? arrayUnion(locationId) : arrayUnion(),
        intakeSnapshot,
        appliedSources: [],
        checkpointState,
        taskState,
        createdAt: nowMs,
        createdBy,
        updatedAt: nowMs,
        updatedBy: createdBy,
      } as any, { merge: true });

      setOpsFlowExistsBySquadId((prev) => ({ ...prev, [squadId]: true }));
      setMessage(`✅ Created flow for "${String((squad as any)?.title || squadId)}"`);
    } catch (e: any) {
      console.warn('[AdminPanel] Failed creating ops flow for squad', squadId, e);
      setMessage(`❌ Failed creating flow for "${String((squad as any)?.title || squadId)}": ${String(e?.message || '')}`);
    } finally {
      setCreatingFlowSquadId(null);
    }
  };

  const handleDuplicateSquad = async (source: Squad) => {
    const sourceId = String((source as any)?.id || '').trim();
    if (!sourceId) return;

    const partnerId = String((source as any)?.partnerId || '').trim();
    const locationId = String((source as any)?.locationId || '').trim();
    const seasonId = String((source as any)?.seasonId || '').trim();
    if (!partnerId || !locationId || !seasonId) {
      setMessage('❌ Cannot duplicate: active squads require partner, location, and season.');
      return;
    }

    setDuplicatingSquadId(sourceId);
    try {
      // Generate a new unique join code (best-effort, using local squads as a quick uniqueness check).
      let code = '';
      for (let i = 0; i < 30; i += 1) {
        const candidate = String(generateSquadCode() || '').toUpperCase();
        if (!candidate) continue;
        const taken = squads.some((s) => String((s as any)?.code || '').toUpperCase() === candidate);
        if (!taken) {
          code = candidate;
          break;
        }
      }
      if (!code) code = String(generateSquadCode() || '').toUpperCase();

      const sourceTitle = String((source as any)?.title || '').trim() || 'Squad';
      const newTitle = `${sourceTitle} (duplicate)`;

      const createdAt = Timestamp.now();

      const newSquadRef = doc(collection(db, 'squads'));
      const newSquadId = newSquadRef.id;

      const payload: any = { ...(source as any) };
      delete payload.id;

      // Identity + admin metadata
      payload.title = newTitle;
      payload.code = code;
      payload.createdAt = createdAt;

      // Canonical identity is copied exactly; schoolId, when historical, is
      // retained unchanged as provenance and never derived from the location.
      payload.partnerId = partnerId;
      payload.locationId = locationId;
      payload.seasonId = seasonId;

      // Reset roster + enrollment-related fields
      payload.studentIds = [];
      payload.onlineStudentIds = [];
      payload.enrolledKidNames = [];
      payload.enrolledParentNamesAndEmails = [];
      payload.seatsTaken = 0;
      delete payload.seatsRemaining;

      // Reset in-class/progress/session state so the duplicate starts clean.
      delete payload.attendance;
      delete payload.lastAttendanceUpdate;
      delete payload.attendanceMetadata;
      delete payload.liveSession;
      delete payload.activeLaunchSession;
      delete payload.missionProgress;
      delete payload.lastMissionUpdate;
      delete payload.completedLevels;
      delete payload.unlockedGames;
      delete payload.currentWorldId;
      delete payload.currentWorldTitle;
      delete payload.currentLessonLevel;
      delete payload.currentLessonId;
      delete payload.currentGoal;
      delete payload.classStatus;
      delete payload.firstDate;
      delete payload.lastDate;
      delete payload.nextSessionAt;
      delete payload.totalClasses;
      delete payload.detailsUpdatedAt;
      delete payload.detailsUpdatedBy;
      delete payload.teacherDetailsUpdatedAt;
      delete payload.teacherDetailsUpdatedBy;

      // Default to draft so we don't accidentally publish a cloned squad.
      payload.status = 'draft';

      // Normalize required arrays
      payload.teacherIds = Array.isArray(payload.teacherIds) ? payload.teacherIds.map((x: any) => String(x)).filter(Boolean) : [];

      await setDoc(newSquadRef, payload as any);

      // Create ops flow immediately for the duplicated squad.
      try {
        const nowMs = Date.now();
        const createdBy = String(userData?.id || '');
        const intakeSnapshot = buildIntakeTemplate({ createdAt: nowMs, createdBy });

        const checkpointState: Record<string, any> = {};
        const taskState: Record<string, any> = {};
        for (const cp of intakeSnapshot.checkpoints || []) {
          checkpointState[cp.checkpointId] = { status: 'not_started' };
          for (const t of cp.tasks || []) {
            taskState[t.taskId] = { status: 'open' };
          }
        }

        await setDoc(
          doc(db, 'partnerSeasonFlows', `${String(payload.partnerId)}__${String(payload.seasonId || payload.termId)}`),
          {
            partnerId: String(payload.partnerId),
            seasonId: String(payload.seasonId || payload.termId),
            squadIds: arrayUnion(newSquadId),
            locationIds: payload.locationId ? arrayUnion(String(payload.locationId)) : arrayUnion(),
            intakeSnapshot,
            appliedSources: [],
            checkpointState,
            taskState,
            createdAt: nowMs,
            createdBy,
            updatedAt: nowMs,
            updatedBy: createdBy,
          } as any,
          { merge: true }
        );

        setOpsFlowExistsBySquadId((prev) => ({ ...prev, [newSquadId]: true }));
      } catch (e) {
        console.warn('[AdminPanel] Failed creating ops flow for duplicated squad', newSquadId, e);
      }

      // Generate calendar events (today-forward) from details if schedule exists.
      try {
        await syncSquadCalendarFromDetails({
          id: newSquadId,
          ...payload,
        } as any);
      } catch (e) {
        console.warn('[AdminPanel] Failed syncing calendar for duplicated squad', newSquadId, e);
      }

      setMessage(`✅ Duplicated squad "${sourceTitle}"`);
      await loadSquads();
    } catch (e: any) {
      console.warn('[AdminPanel] Failed duplicating squad', sourceId, e);
      setMessage(`❌ Failed duplicating squad "${String((source as any)?.title || sourceId)}": ${String(e?.message || '')}`);
    } finally {
      setDuplicatingSquadId(null);
    }
  };

  const loadStudents = async () => {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
    const studentsData = usersData.filter(user => user.role === 'student');
    const parentsData = usersData.filter(user => user.role === 'parent');
    setStudents(studentsData);
    setParents(parentsData);

    // Load parent consents for all students
    const consents: Record<string, boolean | null> = {};
    for (const student of studentsData) {
      try {
        if (student.parentIds && student.parentIds.length > 0) {
          const parentId = student.parentIds[0];
          const parentRef = doc(db, 'users', parentId);
          const parentSnap = await getDoc(parentRef);

          if (parentSnap.exists()) {
            const parentData = parentSnap.data() as User;
            const photoRelease = parentData.consents?.photoRelease;
            const photoConsent = photoRelease?.accepted === true || photoRelease?.value === 'accept';
            const hasResponse = photoRelease?.accepted !== undefined || photoRelease?.value !== undefined;
            consents[student.id] = hasResponse ? photoConsent : null;
          } else {
            consents[student.id] = null;
          }
        } else {
          consents[student.id] = null;
        }
      } catch (error) {
        console.error(`Error loading parent consent for student ${student.id}:`, error);
        consents[student.id] = null;
      }
    }
    setParentConsents(consents);
  };

  // NOTE: Legacy utilities like backfilling OG levels and seeding tag definitions
  // have been removed from the Admin UI to keep the Students tab focused.

  const loadTeachers = async () => {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
    const teachersData = usersData.filter(user => user.role === 'teacher');

    // Merge in teacher compliance/training docs from /teachers/{teacherId}
    // (kept separate from /users so we don't widen teacher self-edit fields).
    let complianceById: Record<string, any> = {};
    try {
      const compSnap = await getDocs(collection(db, 'teachers'));
      compSnap.docs.forEach((d) => {
        complianceById[d.id] = d.data() || {};
      });
    } catch (e) {
      console.warn('[AdminPanel] Failed to load /teachers compliance docs (ok if none exist yet).', e);
    }

    const merged = teachersData.map((t) => ({ ...t, ...(complianceById[t.id] || {}) })) as User[];
    setTeachers(merged);
  };

  const loadCertificationWaitlist = async () => {
    const waitlistRef = collection(db, 'certificationWaitlist');
    const snapshot = await getDocs(query(waitlistRef, orderBy('createdAt', 'desc')));
    setCertificationWaitlist(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CertificationWaitlistEntry)));
  };

  const generateSquadCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleCreateSquad = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!squadForm.title.trim()) {
      setMessage('❌ Squad title is required');
      return;
    }
    if (!String(squadForm.locationId || '').trim()) {
      setMessage('❌ A canonical location is required.');
      return;
    }

    setLoading(true);
    try {
      const code = generateSquadCode();
      const selectedLocation = partnerLocations.find(location => location.id === squadForm.locationId);
      const canonicalPartnerId = String(selectedLocation?.partnerId || '').trim();
      const canonicalLocationId = String(selectedLocation?.id || '').trim();
      if (!canonicalPartnerId || !canonicalLocationId) {
        setMessage('❌ Select a canonical partner location.');
        setLoading(false);
        return;
      }
      let resolvedTermId = String((squadForm as any).termId || '').trim();
      const firstClassDate = String(squadForm.schedule?.startDate || '').slice(0, 10);
      if (firstClassDate) {
        try {
          const partnerSeasons = await listPartnerSeasons(canonicalPartnerId);
          resolvedTermId = inferSeasonFromStartDate(firstClassDate, partnerSeasons)?.termId || resolvedTermId;
        } catch (error) {
          console.warn('[AdminPanel] Could not infer season from squad start date', error);
        }
      }
      if (!resolvedTermId) {
        setMessage('❌ Add a start date or create this squad from a season.');
        setLoading(false);
        return;
      }

      // Check if code already exists
      const existingSquads = squads.filter(s => s.code === code);
      if (existingSquads.length > 0) {
        setMessage('❌ Squad code already exists');
        setLoading(false);
        return;
      }

      const priceInCents = squadForm.price ? Math.round(parseFloat(squadForm.price) * 100) : 0;
      if (isNaN(priceInCents)) {
        setMessage('❌ Invalid price format.');
        setLoading(false);
        return;
      }

      // Ensure default staff teacher(s) are assigned to every squad at creation time.
      let defaultTeacherId: string | null = null;
      try {
        const snap = await getDocs(
          query(collection(db, 'users'), where('email', '==', DEFAULT_SQUAD_TEACHER_EMAIL))
        );
        defaultTeacherId = snap.docs[0]?.id || null;
      } catch (err) {
        console.warn('[AdminPanel] Failed to look up default squad teacher by email', err);
      }

      const squadData: Partial<Squad> = {
        title: squadForm.title,
        code: code.toUpperCase(),
        teacherIds: Array.from(new Set([
          ...(userData?.id ? [userData.id] : []),
          ...(squadForm.regularTeacher ? [String(squadForm.regularTeacher)] : []),
          ...(defaultTeacherId ? [defaultTeacherId] : []),
        ])),
        studentIds: [],
        notes: squadForm.notes || '',
        createdAt: Timestamp.now(),
        location: {
          name: selectedLocation?.name || "N/A",
          address: selectedLocation?.address || '',
          latitude: selectedLocation?.latitude || 0,
          longitude: selectedLocation?.longitude || 0,
        },
        // Transitional snapshots for teacher/offline views. Canonical values live on Location.
        wifiUsername: selectedLocation?.wifiUsername || '',
        wifiPassword: selectedLocation?.wifiPassword || '',
        arrivalDismissalProcedure: selectedLocation?.arrivalProcedure || '',
        bathroomRules: selectedLocation?.bathroomRules || '',
        // Curriculum selection
        curriculumId: squadForm.curriculumId || 'intro-ai-kids-2026',
        priceCents: priceInCents,
        currency: squadForm.currency,
        paymentMode: ((partners.find((partner) => partner.id === canonicalPartnerId) as any)?.billingDefaults?.paymentMode || squadForm.paymentMode || 'stripe') as any,
        termId: resolvedTermId,
        // Keep legacy label field for backward compatibility with older code/queries.
        term: programTermLabelFromId(resolvedTermId),
        status: squadForm.status as any,
        visibility: 'public',
        capacity: squadForm.capacity,
        minEnroll: squadForm.minEnroll,
        waitlistEnabled: squadForm.waitlistEnabled,
        seatsTaken: 0,
        seasonId: resolvedTermId,
        seasonAssignmentSource: firstClassDate ? 'inferred_from_start_date' : 'creation_context',
        seasonAssignmentReason: firstClassDate ? `Assigned from first class date ${firstClassDate}.` : 'Assigned from the season where the squad was created.',
        schedule: squadForm.schedule,
        timezone: squadForm.timezone || DEFAULT_TZ,
        startGrade: squadForm.startGrade,
        endGrade: squadForm.endGrade,
      };

      // Conditionally include nullable fields to avoid undefined/null writes
      (squadData as any).locationName = selectedLocation?.name || '';
      if (canonicalPartnerId) {
        (squadData as any).partnerId = canonicalPartnerId;
      }
      if (canonicalLocationId) {
        (squadData as any).locationId = canonicalLocationId;
      }
      if (squadForm.regularTeacher) {
        (squadData as any).regularTeacher = squadForm.regularTeacher;
        (squadData as any).leadTeacherId = squadForm.regularTeacher;
      }

      const createdRef = await addDoc(collection(db, 'squads'), squadData);

      // Ensure an Ops flow exists for every squad created from the Admin panel path as well.
      // (This creation path does not go through the Cloud Function `createSquad`.)
      try {
        const nowMs = Date.now();
        const createdBy = String(userData?.id || '');
        const intakeSnapshot = buildIntakeTemplate({ createdAt: nowMs, createdBy });

        const checkpointState: Record<string, any> = {};
        const taskState: Record<string, any> = {};
        for (const cp of intakeSnapshot.checkpoints || []) {
          checkpointState[cp.checkpointId] = { status: 'not_started' };
          for (const t of cp.tasks || []) {
            taskState[t.taskId] = { status: 'open' };
          }
        }

        const partnerId = canonicalPartnerId;
        const locationId = canonicalLocationId;
        const seasonId = resolvedTermId;
        if (partnerId && seasonId) {
          await setDoc(doc(db, 'partnerSeasonFlows', `${partnerId}__${seasonId}`), {
            partnerId,
            seasonId,
            ...(locationId ? { locationIds: arrayUnion(locationId) } : {}),
            squadIds: arrayUnion(createdRef.id),
            intakeSnapshot,
            appliedSources: [],
            checkpointState,
            taskState,
            pipelineStage: 'lead',
            updatedAt: nowMs,
            updatedBy: createdBy,
          } as any, { merge: true });
        }
      } catch (e) {
        console.warn('[AdminPanel] Failed to create ops flow for new squad', e);
      }

      // Kick off calendar sync today-forward to generate events from details
      await syncSquadCalendarFromDetails({
        id: createdRef.id,
        ...squadData,
      } as any);
      
      setMessage(`✅ Squad "${squadForm.title}" created with code: ${code}`);
      setSquadForm({ 
        title: '', 
        notes: '', 
        locationName: '',
        price: '',
        currency: 'USD',
        paymentMode: 'stripe',
        termId: defaultProgramTerm.id,
        status: 'draft',
        capacity: 12,
        minEnroll: 8,
        waitlistEnabled: true,
        schoolId: '',
        locationId: '',
        curriculumId: 'intro-ai-kids-2026',
        startGrade: '',
        endGrade: '',
        regularTeacher: '',
        timezone: DEFAULT_TZ,
        schedule: {
          dayOfWeek: 'MON',
          startTime: '',
          endTime: '',
          startDate: '',
          endDate: '',
          exceptions: [],
          makeups: [],
        },
      });
      await loadSquads();
      setShowCreateSquadForm(false);
      setSquadCreationPartnerId(null);
      navigate(`/app/admin/squads/${createdRef.id}`);
    } catch (error) {
      console.error('Error creating squad:', error);
      setMessage('❌ Error creating squad');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!studentForm.displayName.trim()) {
      setMessage('❌ Display name is required');
      return;
    }

    setLoading(true);
    try {
      const selectedMuse = allMuses.find(m => m.name === studentForm.selectedMuseName);
      if (!selectedMuse) {
        console.error("Selected muse not found!");
        setMessage('❌ Selected muse not found. Please select a valid muse.');
        setLoading(false);
        return;
      }

      const studentData = {
        displayName: studentForm.displayName,
        role: 'student' as const,
        museCash: studentForm.museCash,
        selectedMuse: selectedMuse,
        muses: [selectedMuse],
        favoriteSquadIds: [],
        totalSongsCreated: 0,
        totalGamesPlayed: 0,
        createdAt: new Date()
      };

      await addDoc(collection(db, 'users'), studentData);
      
      setMessage(`✅ Student "${studentForm.displayName}" created successfully`);
      setStudentForm({ displayName: '', museCash: 50, selectedMuseName: 'Songly' });
      await loadStudents();
      setIsCreatingStudent(false);
    } catch (error: any) {
      console.error('Error creating student:', error);
      setMessage(`❌ Error creating student: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeacher = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teacherForm.email.trim() || !teacherForm.displayName.trim()) {
      setMessage('❌ Email and display name are required');
      return;
    }

    setLoading(true);
    try {
      // IMPORTANT:
      // Creating a user with the primary auth instance signs THIS browser into the new user,
      // which drops admin privileges mid-session and causes "Missing or insufficient permissions".
      // Use a secondary auth instance for account creation.
      const secondaryAppName = '__og_admin_create_user__';
      const secondaryApp =
        getApps().some(a => a.name === secondaryAppName)
          ? getApp(secondaryAppName)
          : initializeApp(firebaseConfig, secondaryAppName);
      const secondaryAuth = getAuth(secondaryApp);

      // Create Firebase Auth user (without affecting the main admin session)
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        teacherForm.email,
        teacherForm.password
      );
      const uid = userCredential.user.uid;
      
      // Create Firestore user document
      // Get default Songly muse for teachers (same as students)
      const defaultMuse = allMuses.find(m => m.name === 'Songly') || null;
      
      const userData = {
        email: teacherForm.email,
        displayName: teacherForm.displayName,
        role: 'teacher' as const,
        // New teachers should start restricted until explicitly activated by admin.
        // IMPORTANT: leaving this undefined will be treated as "active" across the app/rules for backward-compat.
        staffStatus: 'applicant' as const,
        isAdmin: false, // Default to false, not user-selectable
        museCash: 1000, // Teachers get more starting cash
        selectedMuse: defaultMuse,
        muses: defaultMuse ? [defaultMuse] : [],
        favoriteSquadIds: [],
        createdAt: new Date()
      };

      await setDoc(doc(db, 'users', uid), userData);

      // Clean up: don't leave the secondary auth logged in
      try {
        await signOut(secondaryAuth);
      } catch (_) {
        // ignore
      }
      
      setMessage(`✅ Teacher "${teacherForm.displayName}" created successfully`);
      setTeacherForm({ email: '', displayName: '', password: 'teacher123', isAdmin: false });
      await loadTeachers();
      setIsCreatingTeacher(false); // Close modal on success
    } catch (error: any) {
      console.error('Error creating teacher:', error);
      
      // Provide more specific error messages
      if (error.code === 'auth/email-already-in-use') {
        const createFirestoreOnly = await window.appConfirm(
          `❌ Email "${teacherForm.email}" already exists in Firebase Authentication.\n\n` +
          `This usually means there's an orphaned Auth user without a Firestore document.\n\n` +
          `Would you like to create ONLY the Firestore document for this user?\n` +
          `(Click OK to create Firestore doc, Cancel to try different email)`
        );
        
        if (createFirestoreOnly) {
          try {
            // We need to find the UID of the existing Auth user
            // Since we can't query Auth from client, we'll need to handle this differently
            setMessage(`ℹ️ To complete this process:\n1. Go to Firebase Console > Authentication > Users\n2. Find "${teacherForm.email}" and copy their UID\n3. Use the manual Firestore creation option below.`);
          } catch (firestoreError) {
            console.error('Error creating Firestore document:', firestoreError);
            setMessage(`❌ Error creating Firestore document: ${firestoreError}`);
          }
        } else {
          setMessage(`❌ Email "${teacherForm.email}" is already in use. Please use a different email address.`);
        }
      } else if (error.code === 'auth/invalid-email') {
        setMessage('❌ Invalid email format. Please enter a valid email address.');
      } else if (error.code === 'auth/weak-password') {
        setMessage('❌ Password is too weak. Please use a stronger password.');
      } else {
        setMessage(`❌ Error creating teacher: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePartner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!partnerForm.email.trim() || !partnerForm.displayName.trim()) {
      setMessage('❌ Username (email) and display name are required');
      return;
    }

    setLoading(true);
    try {
      // Use a secondary auth instance so we don't lose admin session.
      const secondaryAppName = '__og_admin_create_user__';
      const secondaryApp =
        getApps().some(a => a.name === secondaryAppName)
          ? getApp(secondaryAppName)
          : initializeApp(firebaseConfig, secondaryAppName);
      const secondaryAuth = getAuth(secondaryApp);

      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        partnerForm.email,
        partnerForm.password
      );
      const uid = userCredential.user.uid;

      const defaultMuse = allMuses.find(m => m.name === 'Songly') || null;

      const userDoc: any = {
        email: partnerForm.email,
        displayName: partnerForm.displayName,
        role: 'partner' as const,
        isAdmin: false,
        phoneNumber: partnerForm.phoneNumber ? String(partnerForm.phoneNumber).trim() : '',
        museCash: 1000,
        selectedMuse: defaultMuse,
        muses: defaultMuse ? [defaultMuse] : [],
        favoriteSquadIds: [],
        loginCount: 0,
        createdAt: new Date(),
      };

      // Avoid writing empty phone fields if the admin didn't provide one.
      if (!userDoc.phoneNumber) delete userDoc.phoneNumber;

      await setDoc(doc(db, 'users', uid), userDoc);

      try {
        await signOut(secondaryAuth);
      } catch (_) {
        // ignore
      }

      setMessage(`✅ Partner "${partnerForm.displayName}" created successfully`);
      setPartnerForm({ email: '', displayName: '', phoneNumber: '', password: 'partner123' });
      setIsCreatingPartner(false);
    } catch (error: any) {
      console.error('Error creating partner:', error);
      if (error.code === 'auth/email-already-in-use') {
        setMessage(`❌ Username "${partnerForm.email}" is already in use. Please use a different email address.`);
      } else if (error.code === 'auth/invalid-email') {
        setMessage('❌ Invalid email format. Please enter a valid email address.');
      } else if (error.code === 'auth/weak-password') {
        setMessage('❌ Password is too weak. Please use a stronger password.');
      } else {
        setMessage(`❌ Error creating partner: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSquad = async () => {
    if (!squadToDelete || deletingSquadId) return;

    const squadId = squadToDelete.id;
    const optimisticRemovedSquad = squadToDelete;
    setDeletingSquadId(squadId);
    // Close quickly and remove locally so the confirm modal isn't stuck in a loading state.
    setSquadToDelete(null);
    setSquads((prev) => prev.filter((s) => s.id !== squadId));
    setOpsFlowExistsBySquadId((prev) => {
      if (!prev[squadId]) return prev;
      const next = { ...prev };
      delete next[squadId];
      return next;
    });

    try {
      // Delete the main squad document
      await deleteDoc(doc(db, 'squads', squadId));

      const cleanupSquadEvents = async () => {
        const eventsRef = collection(db, 'squadEvents');
        while (true) {
          const eventsQuery = query(eventsRef, where('squadId', '==', squadId), limit(450));
          const snap = await getDocs(eventsQuery);
          if (snap.empty) break;
          const batch = writeBatch(db);
          snap.forEach(d => batch.delete(d.ref));
          await batch.commit();
          if (snap.size < 450) break;
        }
      };

      const cleanupResults = await Promise.allSettled([
        // Non-critical mirrors/related docs. Deleting non-existent docs is safe.
        deleteDoc(doc(db, 'publicSquads', squadId)),
        cleanupSquadEvents(),
      ]);
      cleanupResults.forEach((result, idx) => {
        if (result.status === 'fulfilled') return;
        const label = idx === 0 ? 'publicSquads' : 'squadEvents';
        console.warn(`${label} cleanup (non-fatal) error for`, squadId, result.reason);
      });

      setMessage('✅ Squad deleted successfully');
    } catch (error) {
      console.error('Error deleting squad:', error);
      setMessage('❌ Error deleting squad');
      // Restore quickly, then re-sync from Firestore in case optimistic update diverged.
      setSquads((prev) => [optimisticRemovedSquad, ...prev]);
      await Promise.allSettled([loadSquads(), loadOpsFlowsIndex()]);
    } finally {
      setDeletingSquadId(null);
    }
  };

  const handleUpdateStudent = async (studentData: Partial<User>) => {
    if (!studentToEdit) return;

    setLoading(true);
    try {
      const studentRef = doc(db, 'users', studentToEdit.id);
      await updateDoc(studentRef, studentData);
      setMessage('✅ Student updated successfully');
      setStudentToEdit(null); // Close the modal
      await loadStudents();
    } catch (error) {
      console.error('Error updating student:', error);
      setMessage('❌ Error updating student');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string, userType: 'student' | 'teacher' | 'partner') => {
    const warning = userType === 'teacher'
      ? 'Permanently delete this teacher from Auth, the platform, hiring/compliance records, active assignments, offer links, and owned files? The Airtable applicant record will be kept. This cannot be undone.'
      : `Are you sure you want to delete this ${userType}? This action cannot be undone.`;
    if (!await window.appConfirm(warning)) return;
    
    setLoading(true);
    setMessage('');

    try {
      // Student deletion requires additional cleanup:
      // - Remove from squads
      // - Unlink from parent accounts (parentStudentLinks + parent.studentIds)
      // - Delete the student user doc
      if (userType === 'student') {
        const batch = writeBatch(db);

        // 1) Remove student from any squads
        const squadsRef = collection(db, 'squads');
        const squadsQ = query(squadsRef, where('studentIds', 'array-contains', userId));
        const squadsSnapshot = await getDocs(squadsQ);
        squadsSnapshot.forEach((squadDoc) => {
          console.log(`Removing student ${userId} from squad ${squadDoc.id}`);
          batch.update(squadDoc.ref, { studentIds: arrayRemove(userId) });
        });

        // 2) Remove parent-student link docs + collect parent IDs
        const parentIdsToUnlink = new Set<string>();

        // Best-effort: from the student doc already loaded in state
        const localStudent = students.find((s) => s.id === userId) as any;
        const localParentIds: string[] = Array.isArray(localStudent?.parentIds) ? localStudent.parentIds : [];
        localParentIds.forEach((pid) => parentIdsToUnlink.add(pid));

        // Best-effort: from locally loaded parents state
        parents
          .filter((p: any) => Array.isArray(p?.studentIds) && p.studentIds.includes(userId))
          .forEach((p) => parentIdsToUnlink.add(p.id));

        // Source of truth: link documents
        const linksRef = collection(db, 'parentStudentLinks');
        const linksQ = query(linksRef, where('studentId', '==', userId));
        const linksSnap = await getDocs(linksQ);
        linksSnap.forEach((linkDoc) => {
          const data: any = linkDoc.data();
          if (data?.parentId) parentIdsToUnlink.add(String(data.parentId));
          batch.delete(linkDoc.ref);
        });

        // 3) Remove this studentId from any linked parent accounts
        parentIdsToUnlink.forEach((parentId) => {
          batch.update(doc(db, 'users', parentId), { studentIds: arrayRemove(userId) });
        });

        // 4) Finally, delete the student user document
        batch.delete(doc(db, 'users', userId));

        await batch.commit();
      } else if (userType === 'teacher') {
        // Server-side permanent cleanup: Auth, user/teacher Firestore trees,
        // active squad/clearance links, offer tokens, and owned Storage files.
        // The Airtable applicant row is preserved.
        const fn = httpsCallable(getFunctions(), 'adminDeleteTeacherCompletely');
        await fn({ uid: userId });
      } else {
        // Best-effort: delete Firebase Auth user (requires deployed Cloud Function).
        try {
          const fn = httpsCallable(getFunctions(), 'adminDeleteAuthUser');
          await fn({ uid: userId });
        } catch (e) {
          console.warn('[AdminPanel] Failed to delete Auth user (partner):', e);
        }
        // Partner: delete Firestore user doc
        await deleteDoc(doc(db, 'users', userId));
      }
      
      setMessage(`✅ ${userType} deleted successfully.`);
      
      // Refresh the local data
      if (userType === 'student') {
        await loadStudents();
        await loadSquads(); // Squads data might have changed
      } else if (userType === 'teacher') {
        await loadTeachers();
      }

    } catch (error: any) {
      console.error(`Error deleting ${userType}:`, error);
      setMessage(`❌ Error deleting ${userType}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenManageModal = (squad: Squad) => {
    setSelectedSquad(squad);
    setIsManageModalOpen(true);
  };

  const handleCloseManageModal = () => {
    setSelectedSquad(null);
    setIsManageModalOpen(false);
  };

  const handleSquadUpdate = (updatedSquad: Squad) => {
    setSquads(prevSquads =>
      prevSquads.map(s => (s.id === updatedSquad.id ? updatedSquad : s))
    );
    setSelectedSquad(updatedSquad); // Keep modal state in sync
    // Details changes (like lead teacher) can affect calendar events via sync helpers,
    // so bump the refresh key to ensure the admin calendar reloads from Firestore.
    setCalendarRefreshKey(prev => prev + 1);
  };

  const handleStatusChange = async (squadId: string, newStatus: Squad['status']) => {
    setEditingStatusSquadId(null);
    try {
      const existingSquad = squads.find((s) => s.id === squadId) || null;
      const prevStatus = normalizeSquadLifecycleStatus((existingSquad as any)?.status);
      const nextStatus = normalizeSquadLifecycleStatus(newStatus);
      const squadRef = doc(db, 'squads', squadId);
      await updateDoc(squadRef, { status: newStatus });
      setSquads(prevSquads =>
        prevSquads.map(s => (s.id === squadId ? { ...s, status: newStatus } : s))
      );

      // The server cancellation trigger reads occurrence-level staffing before
      // deleting calendar rows so it can notify every teacher involved. Do not
      // race that trigger by deleting the occurrences from the browser.
      if (nextStatus === 'canceled' || nextStatus === 'cancelled') {
        setCalendarRefreshKey(prev => prev + 1);
      }

      // If a squad is reactivated from canceled back to an active lifecycle state,
      // rebuild its calendar events from squad schedule details.
      const movedOffCanceled =
        (prevStatus === 'canceled' || prevStatus === 'cancelled')
        && nextStatus !== 'canceled'
        && nextStatus !== 'cancelled'
        && nextStatus !== 'archived';
      if (movedOffCanceled && existingSquad) {
        try {
          await syncSquadCalendarFromDetails({
            ...(existingSquad as any),
            status: newStatus,
          } as any);
          setCalendarRefreshKey(prev => prev + 1);
        } catch (syncError) {
          console.warn('[AdminPanel] Failed restoring calendar events after squad reactivation', {
            squadId,
            prevStatus,
            nextStatus,
            error: syncError,
          });
          setMessage('⚠️ Status updated, but calendar events could not be restored automatically.');
        }
      }
    } catch (error) {
      console.error('Error updating squad status:', error);
      setMessage('❌ Error updating status');
    }
  };

  const normalizePaymentMode = (
    raw: any
  ): 'stripe' | 'school_flat_fee' | 'school_enrollment' | 'external' => {
    const v = String(raw || '').trim().toLowerCase();
    if (v === 'school_flat_fee') return 'school_flat_fee';
    if (v === 'school_enrollment') return 'school_enrollment';
    if (v === 'external') return 'external';
    return 'stripe';
  };

  const formatUsdFromCents = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

  const parseUsdToCents = (value: string): number | undefined => {
    const raw = String(value || '').trim();
    if (!raw) return undefined;
    const n = Number(raw.replace(/[^0-9.\-]/g, ''));
    if (Number.isNaN(n)) return undefined;
    return Math.round(n * 100);
  };

  const flatFeeCentsForSquad = (squad: Squad): number | undefined => {
    const explicit = (squad as any)?.flatFeePerClassCents;
    if (typeof explicit === 'number' && Number.isFinite(explicit)) return explicit;
    const legacy = (squad as any)?.priceCents;
    if (typeof legacy === 'number' && Number.isFinite(legacy)) return legacy;
    return undefined;
  };

  const perStudentHrCentsForSquad = (squad: Squad): number | undefined => {
    const explicit = (squad as any)?.tuitionPerStudentPerClassCents;
    if (typeof explicit === 'number' && Number.isFinite(explicit)) return explicit;
    const legacy = (squad as any)?.priceCents;
    if (typeof legacy === 'number' && Number.isFinite(legacy)) return legacy;
    return undefined;
  };

  const enrollmentCountForPrice = (squad: Squad): number => {
    const seatsRaw = Number((squad as any)?.seatsTaken);
    const seats = Number.isFinite(seatsRaw) && seatsRaw >= 0 ? Math.trunc(seatsRaw) : 0;
    const ids = Array.isArray((squad as any)?.studentIds) ? (squad as any).studentIds.length : 0;
    return Math.max(0, seats, ids);
  };

  /** External (legacy): prefer lump-sum `priceCents`; else estimate total from per-class rate × students × scheduled classes. */
  const externalTotalTuitionCents = (squad: Squad): number | undefined => {
    const lump = Number((squad as any)?.priceCents);
    if (Number.isFinite(lump) && lump > 0) return Math.round(lump);
    const per = Number((squad as any)?.tuitionPerStudentPerClassCents);
    const students = enrollmentCountForPrice(squad);
    const classes = computeTotalClassesFromSchedule((squad as any)?.schedule);
    if (!Number.isFinite(per) || per <= 0 || students <= 0 || classes == null || classes <= 0) return undefined;
    return Math.round(per * students * classes);
  };

  const centsBeingEditedForSquad = (squad: Squad): number | undefined => {
    const mode = normalizePaymentMode((squad as any)?.paymentMode);
    if (mode === 'school_flat_fee') return flatFeeCentsForSquad(squad);
    if (mode === 'external') return externalTotalTuitionCents(squad);
    return perStudentHrCentsForSquad(squad);
  };

  const seedUsdStringForPriceEdit = (squad: Squad): string => {
    const c = centsBeingEditedForSquad(squad);
    if (c == null || !Number.isFinite(c)) return '';
    return (c / 100).toFixed(2);
  };

  const formatSquadPriceAdminCell = (squad: Squad): string => {
    const mode = normalizePaymentMode((squad as any)?.paymentMode);
    if (mode === 'school_flat_fee') {
      const c = flatFeeCentsForSquad(squad);
      return c != null ? `${formatUsdFromCents(c)} flat fee` : 'N/A';
    }
    if (mode === 'external') {
      const c = externalTotalTuitionCents(squad);
      return c != null ? `${formatUsdFromCents(c)} total tuition` : 'N/A';
    }
    const c = perStudentHrCentsForSquad(squad);
    return c != null ? `${formatUsdFromCents(c)}/student/hr` : 'N/A';
  };

  const formatSquadPriceAdminTitle = (squad: Squad): string => {
    const mode = normalizePaymentMode((squad as any)?.paymentMode);
    if (mode === 'school_flat_fee') return 'Flat fee per class';
    if (mode === 'external') return 'Total program tuition (legacy external)';
    return 'Tuition per student per hour of class';
  };

  const paymentModeLabel = (modeRaw: any): string => {
    const mode = normalizePaymentMode(modeRaw);
    if (mode === 'stripe') return 'Stripe';
    if (mode === 'school_enrollment') return 'School (Enrollment)';
    if (mode === 'school_flat_fee') return 'School (Flat fee)';
    return 'External (legacy)';
  };

  const handleCurriculumChange = async (squadId: string, nextCurriculumIdRaw: string) => {
    const nextCurriculumId = String(nextCurriculumIdRaw || '').trim();
    const squad = squads.find((s) => s.id === squadId);
    if (!squad) {
      setEditingCurriculumSquadId(null);
      return;
    }
    const prevCurriculumId = String((squad as any)?.curriculumId || 'music').trim() || 'music';
    const nextCurriculumFinal = nextCurriculumId || 'music';
    if (prevCurriculumId === nextCurriculumFinal) {
      setEditingCurriculumSquadId(null);
      return;
    }

    if (String((squad as any)?.classStatus || '').trim().toLowerCase() === 'current') {
      setMessage('❌ Cannot change curriculum while a class is live. Finish or cancel the class first.');
      setEditingCurriculumSquadId(null);
      return;
    }

    const ok = await window.appConfirm(
      'Changing curriculum will reset progress to avoid mixing progress between curricula.\n\n'
      + 'This resets:\n'
      + '- Current level -> 1\n'
      + '- Completed levels -> empty\n'
      + '- Mission progress -> cleared\n\n'
      + 'Proceed?'
    );
    if (!ok) {
      setEditingCurriculumSquadId(null);
      return;
    }

    try {
      const squadRef = doc(db, 'squads', squadId);
      await updateDoc(squadRef, {
        curriculumId: nextCurriculumFinal,
        currentLessonId: '1',
        completedLevels: [],
        missionProgress: {},
        classStatus: 'next',
        updatedAt: Timestamp.now(),
      } as any);
      setSquads((prev) =>
        prev.map((s) => (
          s.id === squadId
            ? {
                ...s,
                curriculumId: nextCurriculumFinal,
                currentLessonId: '1',
                completedLevels: [],
                missionProgress: {},
                classStatus: 'next',
              } as any
            : s
        ))
      );
      setMessage('✅ Curriculum updated and progress reset.');
    } catch (error) {
      console.error('Error updating squad curriculum:', error);
      setMessage('❌ Failed to update curriculum.');
    } finally {
      setEditingCurriculumSquadId(null);
    }
  };

  const handlePaymentModeChange = async (squadId: string, nextModeRaw: string) => {
    const nextMode = normalizePaymentMode(nextModeRaw);
    const squad = squads.find((s) => s.id === squadId);
    const prevMode = normalizePaymentMode((squad as any)?.paymentMode);
    if (!squad || prevMode === nextMode) {
      setEditingPaymentModeSquadId(null);
      return;
    }
    try {
      const partnerId = String((squad as any)?.partnerId || '').trim();
      const partner = partners.find((item) => item.id === partnerId);
      const partnerDefault = String((partner as any)?.billingDefaults?.paymentMode || '').trim();
      if (partnerId && nextMode !== partnerDefault) {
        const updateDefault = await window.appConfirm(
          `${partnerDefault ? `This partner's default is ${paymentModeLabel(partnerDefault)}.` : 'This partner has no default squad payment mode.'}\n\nSelect OK to make ${paymentModeLabel(nextMode)} the partner default for future squads. Select Cancel to use it for this squad only.`
        );
        if (updateDefault) {
          await updateDoc(doc(db, 'partners', partnerId), {
            'billingDefaults.paymentMode': nextMode,
            updatedAt: Timestamp.now(),
            updatedBy: String(userData?.id || '').trim() || null,
          } as any);
          setPartners((prev) => prev.map((item) => item.id === partnerId
            ? { ...item, billingDefaults: { ...(item.billingDefaults || {}), paymentMode: nextMode as any } }
            : item));
        }
      }
      const squadRef = doc(db, 'squads', squadId);
      await updateDoc(squadRef, {
        paymentMode: nextMode,
        updatedAt: Timestamp.now(),
      } as any);
      setSquads((prev) => prev.map((s) => (s.id === squadId ? { ...s, paymentMode: nextMode } : s)));
      setMessage('✅ Payment type updated.');
    } catch (error) {
      console.error('Error updating squad payment mode:', error);
      setMessage('❌ Failed to update payment type.');
    } finally {
      setEditingPaymentModeSquadId(null);
    }
  };

  const handleSquadPriceSave = async (squadId: string, rawUsd: string) => {
    const squad = squads.find((s) => s.id === squadId) || null;
    if (!squad) {
      setEditingPriceSquadId(null);
      setEditingPriceSquadValue('');
      return;
    }

    const trimmed = String(rawUsd || '').trim();
    if (!trimmed) {
      setEditingPriceSquadId(null);
      setEditingPriceSquadValue('');
      return;
    }

    const cents = parseUsdToCents(trimmed);
    if (cents === undefined || cents < 0) {
      setMessage('❌ Invalid price. Use a number like 12.50');
      setEditingPriceSquadId(squadId);
      setEditingPriceSquadValue(trimmed);
      return;
    }

    const prev = centsBeingEditedForSquad(squad);
    if (prev === cents) {
      setEditingPriceSquadId(null);
      setEditingPriceSquadValue('');
      return;
    }

    const mode = normalizePaymentMode((squad as any)?.paymentMode);
    const patch: Record<string, any> = { updatedAt: Timestamp.now(), priceSource: 'squad_override' };
    if (mode === 'school_flat_fee') {
      patch.flatFeePerClassCents = cents;
      patch.priceCents = cents;
    } else if (mode === 'external') {
      patch.priceCents = cents;
    } else {
      patch.tuitionPerStudentPerClassCents = cents;
      patch.priceCents = cents;
    }

    setSavingPriceSquadId(squadId);
    setMessage('');
    try {
      const squadRef = doc(db, 'squads', squadId);
      await updateDoc(squadRef, patch as any);
      setSquads((prev) =>
        prev.map((s) => {
          if (s.id !== squadId) return s;
          const next: any = { ...s, ...patch };
          delete next.updatedAt;
          return next as Squad;
        })
      );
      setEditingPriceSquadId(null);
      setEditingPriceSquadValue('');
      setMessage('✅ Price updated.');
    } catch (error) {
      console.error('Error updating squad price:', error);
      setMessage('❌ Failed to update price.');
      setEditingPriceSquadId(squadId);
      setEditingPriceSquadValue(trimmed);
    } finally {
      setSavingPriceSquadId(null);
    }
  };

  // Achievement management functions
  const handleCreateAchievement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGame || !achievementForm.id || !achievementForm.title) {
      setMessage('❌ Game, ID, and Title are required');
      return;
    }

    setLoading(true);
    try {
      const achievement: AchievementDefinition = {
        ...achievementForm,
        gameId: selectedGame,
        id: achievementForm.id!,
        title: achievementForm.title!,
        museCashReward: achievementForm.museCashReward || 50,
        implemented: achievementForm.implemented || true,
        rarity: achievementForm.rarity || 'common'
      };

      await gameAPI.setAchievementDefinition(selectedGame, achievement);
      
      setMessage(`✅ Achievement "${achievement.title}" created for ${GAME_REGISTRY[selectedGame]?.title}`);
      setAchievementForm({
        id: '',
        title: '',
        description: '',
        iconUrl: '🏆',
        museCashReward: 50,
        type: 'milestone',
        rarity: 'common',
        implemented: true,
        sortOrder: 1,
        category: 'progression'
      });
      
      // Reload achievements
      await loadAchievements();
    } catch (error) {
      console.error('Error creating achievement:', error);
      setMessage(`❌ Error creating achievement: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAchievement = (achievement: AchievementDefinition) => {
    setEditingAchievement(achievement);
    setAchievementForm(achievement);
    setSelectedGame(achievement.gameId);
  };

  const handleUpdateAchievement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAchievement || !selectedGame) return;

    setLoading(true);
    try {
      const updatedAchievement: AchievementDefinition = {
        ...achievementForm,
        gameId: selectedGame,
        id: editingAchievement.id, // Keep original ID
        title: achievementForm.title!,
        museCashReward: achievementForm.museCashReward || 50,
        implemented: achievementForm.implemented || true,
        rarity: achievementForm.rarity || 'common'
      };

      await gameAPI.setAchievementDefinition(selectedGame, updatedAchievement);
      
      setMessage(`✅ Achievement "${updatedAchievement.title}" updated`);
      setEditingAchievement(null);
      setAchievementForm({
        id: '',
        title: '',
        description: '',
        iconUrl: '🏆',
        museCashReward: 50,
        type: 'milestone',
        rarity: 'common',
        implemented: true,
        sortOrder: 1,
        category: 'progression'
      });
      
      // Reload achievements
      await loadAchievements();
    } catch (error) {
      console.error('Error updating achievement:', error);
      setMessage(`❌ Error updating achievement: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAchievement = async (gameId: string, achievementId: string) => {
    if (!await window.appConfirm(`Are you sure you want to delete the achievement "${achievementId}"? This cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      // Delete from Firestore
      const achievementRef = doc(db, 'achievementDefinitions', gameId, 'achievements', achievementId);
      await deleteDoc(achievementRef);
      
      setMessage(`✅ Achievement "${achievementId}" deleted`);
      
      // Reload achievements
      await loadAchievements();
    } catch (error) {
      console.error('Error deleting achievement:', error);
      setMessage(`❌ Error deleting achievement: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAddAchievements = async (gameId: string) => {
    setLoading(true);
    try {
      let achievementsToAdd: AchievementDefinition[] = [];
      
      switch (gameId) {
        case 'my-favorite-musicians':
          achievementsToAdd = MY_FAVORITE_MUSICIANS_ACHIEVEMENTS;
          break;
        case 'rhythm-rally':
          achievementsToAdd = RHYTHM_RALLY_ACHIEVEMENTS;
          break;
        case 'rhythm-puzzle':
          achievementsToAdd = RHYTHM_PUZZLE_ACHIEVEMENTS;
          break;
        case 'melody-shaper':
          achievementsToAdd = MELODY_SHAPER_ACHIEVEMENTS;
          break;
        case 'tune-testers':
          achievementsToAdd = TUNE_TESTERS_ACHIEVEMENTS;
          break;
        case 'step-skip-repeat':
          achievementsToAdd = STEP_SKIP_REPEAT_ACHIEVEMENTS;
          break;
        default:
          setMessage(`❌ No pre-defined achievements available for ${gameId}`);
          setLoading(false);
          return;
      }

      for (const achievement of achievementsToAdd) {
        await gameAPI.setAchievementDefinition(gameId, achievement);
      }
      
      setMessage(`✅ Added ${achievementsToAdd.length} achievements to ${GAME_REGISTRY[gameId]?.title}`);
      
      // Reload achievements
      await loadAchievements();
    } catch (error) {
      console.error('Error bulk adding achievements:', error);
      setMessage(`❌ Error bulk adding achievements: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const runSeeder = async (achievements: any[], gameId: string) => {
    setLoading(true);
    try {
      for (const achievement of achievements) {
        await gameAPI.setAchievementDefinition(gameId, achievement);
      }
      await loadAchievements();
    } catch (error) {
      console.error(`Error running seeder for ${gameId}:`, error);
      setMessage(`❌ Error running seeder: ${(error as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMyFavoriteMusiciansAchievements = async () => {
    await runSeeder(MY_FAVORITE_MUSICIANS_ACHIEVEMENTS, 'my-favorite-musicians');
  };

  const handleAddRhythmRallyAchievements = async () => {
    await runSeeder(RHYTHM_RALLY_ACHIEVEMENTS, 'rhythm-rally');
  };

  const handleAddStepSkipRepeatAchievements = async () => {
    await runSeeder(STEP_SKIP_REPEAT_ACHIEVEMENTS, 'step-skip-repeat');
  };

  const handleAddMelodyShaperAchievements = async () => {
    await runSeeder(MELODY_SHAPER_ACHIEVEMENTS, 'melody-shaper');
  };

  const handleAddFlappyMuseAchievements = async () => {
    await runSeeder(FLAPPY_MUSE_ACHIEVEMENTS, 'flappy-muse');
  };

  const seeders: Record<string, SeederFunction> = {
    'my-favorite-musicians': handleAddMyFavoriteMusiciansAchievements,
    'rhythm-rally': handleAddRhythmRallyAchievements,
    'step-skip-repeat': handleAddStepSkipRepeatAchievements,
    'melody-shaper': handleAddMelodyShaperAchievements,
    'flappy-muse': handleAddFlappyMuseAchievements,
  };

  const handleSeedAchievements = async () => {
    if (selectedGame && seeders[selectedGame]) {
      await seeders[selectedGame]();
      setMessage(`✅ ${selectedGame} achievements seeded successfully`);
    } else {
      setMessage('❌ No valid seeder function found');
    }
  };

  const tabs = [
    { id: 'todos', label: 'Todos', icon: '📝' },
    { id: 'ops', label: 'Ops', icon: '🧰' },
    { id: 'users', label: 'Users', icon: '👨‍👩‍👧‍👦' },
    { id: 'calendar', label: 'Calendar', icon: '🗓️' },
    { id: 'learning', label: 'Certifications', icon: '🎓' },
    { id: 'curriculum', label: 'Curriculum', icon: '📚' },
    { id: 'invoicing', label: 'Invoicing', icon: '🧾' },
    { id: 'statistics', label: 'Statistics', icon: '📊' },
    { id: 'exports', label: 'Exports', icon: '⬇️' },
    { id: 'achievements', label: 'Achievements', icon: '🏆' },
    { id: 'ab-testing', label: 'A/B Testing', icon: '🎲' }
  ];

  const userTabs = [
    { id: 'partners', label: 'Partners', icon: '🏢' },
    { id: 'squads', label: 'All Squads', icon: '🎯' },
    { id: 'students', label: 'Students', icon: '👨‍🎓' },
    { id: 'teachers', label: 'Teachers', icon: '👩‍🏫' },
    { id: 'waitlist', label: 'Waitlist', icon: '📝' },
    { id: 'parents', label: 'Parents', icon: '👪' },
    { id: 'applicants', label: 'Applicants', icon: '🧑‍💼' }
  ];


  const renderUsers = () => (
    <div>
      <div className="sub-nav">
        {userTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setUserTab(tab.id as any)}
            className={`tab-button ${activeUserTab === tab.id ? 'active' : ''}`}
          >
            <span className="tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>
      <div className="sub-nav-content">
        {activeUserTab === 'partners' && <PartnerListPage />}
        {activeUserTab === 'squads' && renderSquads()}
        {activeUserTab === 'students' && renderStudents()}
        {activeUserTab === 'teachers' && renderTeachers()}
        {activeUserTab === 'waitlist' && renderCertificationWaitlist()}
        {activeUserTab === 'parents' && <ManageParents />}
        {activeUserTab === 'applicants' && (
          <ApplicantsAdmin initialApplicantId={deepLinkApplicantId || undefined} />
        )}
      </div>
    </div>
  );

  const calculateNumberOfDays = (startDate?: string, endDate?: string, dayOfWeek?: string, exceptions?: string[], makeups?: string[]) => {
    if (!startDate || !endDate || !dayOfWeek) {
      return 0;
    }

    // Using UTC is crucial for correct date calculations across different timezones.
    // 'new Date("YYYY-MM-DD")' creates a date at UTC midnight, but methods like .getDay()
    // are based on the local timezone of where the code is running, causing day-of-the-week
    // mismatches. This helper ensures all dates are handled consistently as UTC.
    const getUTCDate = (dateString: string) => {
        const [year, month, day] = dateString.split('-').map(Number);
        // Month is 0-indexed in JavaScript Date (0=Jan, 1=Feb, etc.)
        return new Date(Date.UTC(year, month - 1, day));
    };

    const start = getUTCDate(startDate);
    const end = getUTCDate(endDate);

    const dayIndex = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].indexOf(dayOfWeek);

    // Create a Set of exception date timestamps for fast lookups.
    const exceptionTimeSet = new Set(exceptions?.map(ex => getUTCDate(ex).getTime()) || []);

    // Use a Set to dedupe regular + makeup dates
    const counted = new Set<number>();
    // Create a new date for iteration to avoid modifying the original 'start' date.
    let currentDate = new Date(start);

    while (currentDate.getTime() <= end.getTime()) {
      // Use getUTCDay() to get the day of the week in UTC.
      if (currentDate.getUTCDay() === dayIndex) {
        if (!exceptionTimeSet.has(currentDate.getTime())) {
          counted.add(currentDate.getTime());
        }
      }
      // Advance to the next day in UTC.
      currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }

    // Add makeup dates (regardless of schedule range), not exceptions
    (makeups || []).forEach(d => {
      const dt = getUTCDate(d);
      const t = dt.getTime();
      if (!exceptionTimeSet.has(t)) {
        counted.add(t);
      }
    });

    return counted.size;
  };

  const calculateClassesLeft = (startDate?: string, endDate?: string, dayOfWeek?: string, exceptions?: string[], makeups?: string[]) => {
    if (!startDate || !endDate || !dayOfWeek) {
      return 0;
    }

    // Using UTC is crucial for correct date calculations across different timezones.
    const getUTCDate = (dateString: string) => {
        const [year, month, day] = dateString.split('-').map(Number);
        // Month is 0-indexed in JavaScript Date (0=Jan, 1=Feb, etc.)
        return new Date(Date.UTC(year, month - 1, day));
    };

    // Get today's date in UTC (start of day)
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const start = getUTCDate(startDate);
    const end = getUTCDate(endDate);

    // If the program hasn't started yet, return total classes
    if (today.getTime() < start.getTime()) {
      return calculateNumberOfDays(startDate, endDate, dayOfWeek, exceptions, makeups);
    }

    // If the program has already ended, return 0
    if (today.getTime() > end.getTime()) {
      return 0;
    }

    const dayIndex = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].indexOf(dayOfWeek);

    // Create a Set of exception date timestamps for fast lookups.
    const exceptionTimeSet = new Set(exceptions?.map(ex => getUTCDate(ex).getTime()) || []);

    let count = 0;
    // Start counting from today (or the program start date if today is before it)
    let currentDate = new Date(Math.max(today.getTime(), start.getTime()));

    while (currentDate.getTime() <= end.getTime()) {
      // Use getUTCDay() to get the day of the week in UTC.
      if (currentDate.getUTCDay() === dayIndex) {
        // Check if the current date is not an exception day.
        if (!exceptionTimeSet.has(currentDate.getTime())) {
          count++;
        }
      }
      // Advance to the next day in UTC.
      currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }

    // Include future makeup dates regardless of schedule end, exclude exceptions
    (makeups || []).forEach(d => {
      const dt = getUTCDate(d);
      const t = dt.getTime();
      if (t >= today.getTime() && !exceptionTimeSet.has(t)) {
        count++;
      }
    });

    return count;
  };

  const calculateDaysAlreadyTaught = (startDate?: string, endDate?: string, dayOfWeek?: string, exceptions?: string[], makeups?: string[]) => {
    if (!startDate || !endDate || !dayOfWeek) {
      return 0;
    }

    // Using UTC is crucial for correct date calculations across different timezones.
    const getUTCDate = (dateString: string) => {
        const [year, month, day] = dateString.split('-').map(Number);
        // Month is 0-indexed in JavaScript Date (0=Jan, 1=Feb, etc.)
        return new Date(Date.UTC(year, month - 1, day));
    };

    // Get today's date in UTC (start of day)
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const start = getUTCDate(startDate);
    const end = getUTCDate(endDate);

    // If the program hasn't started yet, return 0
    if (today.getTime() < start.getTime()) {
      return 0;
    }

    const dayIndex = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].indexOf(dayOfWeek);

    // Create a Set of exception date timestamps for fast lookups.
    const exceptionTimeSet = new Set(exceptions?.map(ex => getUTCDate(ex).getTime()) || []);

    let count = 0;
    // Start counting from the program start date
    let currentDate = new Date(start);
    // End counting at yesterday (or program end date if it ended)
    const countUntil = new Date(Math.min(today.getTime() - 24 * 60 * 60 * 1000, end.getTime()));

    while (currentDate.getTime() <= countUntil.getTime()) {
      // Use getUTCDay() to get the day of the week in UTC.
      if (currentDate.getUTCDay() === dayIndex) {
        // Check if the current date is not an exception day.
        if (!exceptionTimeSet.has(currentDate.getTime())) {
          count++;
        }
      }
      // Advance to the next day in UTC.
      currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }

    // Include past makeup dates (<= yesterday), exclude exceptions
    (makeups || []).forEach(d => {
      const dt = getUTCDate(d);
      const t = dt.getTime();
      if (t <= countUntil.getTime() && !exceptionTimeSet.has(t)) {
        count++;
      }
    });

    return count;
  };

  const renderSquads = () => {
    const filteredSquads = squads.filter(squad => {
      // Status filter
      if (squadStatusFilter !== 'all' && (squad.status || 'published') !== squadStatusFilter) {
        return false;
      }
      // State filter by canonical squad location.
      if (squadStateFilter) {
        const location = partnerLocations.find(item => item.id === squad.locationId);
        const code = getLocationState(location);
        if (code !== squadStateFilter) return false;
      }
      // Start date filter: include only if squad starts on/after selected date
      if (squadStartFilter) {
        const sd = squad.schedule?.mainDate || squad.schedule?.startDate || '';
        if (!sd || sd < squadStartFilter) return false;
      }
      // End date filter: include only if squad ends on/before selected date
      if (squadEndFilter) {
        const ed = squad.schedule?.mainDate || squad.schedule?.endDate || '';
        if (!ed || ed > squadEndFilter) return false;
      }
      return true;
    });

    return (
      <div className="squads-tab-container">
        <div className="admin-content-card">
          <div className="squad-list-container">
            <div className="squad-actions" style={{ display: 'flex', gap: '12px' }}>
              {!showCreateSquadForm && (
                <>
                  <button
                    onClick={() => {
                      setSquadCreationPartnerId(null);
                      setShowCreateSquadForm(true);
                    }}
                    className="add-squad-btn"
                  >
                    ✨ Add New Squad
                  </button>
                  <button onClick={bulkPublishAllSquads} className="add-squad-btn" style={{ background: '#f59e0b' }}>
                    🚀 Bulk Publish All Squads
                  </button>
                </>
              )}
            </div>
            {showCreateSquadForm && renderSquadCreationForm(() => {
              setShowCreateSquadForm(false);
              setSquadCreationPartnerId(null);
            })}

            {!showCreateSquadForm && (
              <>
                <div className="student-filter" style={{ marginBottom: '1rem', justifyContent: 'flex-start', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label>Filter by Status:</label>
                    <select value={squadStatusFilter} onChange={(e) => setSquadStatusFilter(e.target.value)} className="admin-select" style={{width: '160px'}}>
                      <option value="all">All</option>
                      <option value="lead">Lead</option>
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                      <option value="archived">Archived</option>
                      <option value="canceled">Canceled</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label>State:</label>
                    <select
                      value={squadStateFilter}
                      onChange={(e) => setSquadStateFilter(e.target.value)}
                      className="admin-select"
                      style={{ width: '120px' }}
                    >
                      <option value="">All</option>
                      {availableStates.map(st => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label>Start After:</label>
                    <input
                      type="date"
                      value={squadStartFilter}
                      onChange={(e) => setSquadStartFilter(e.target.value)}
                      className="admin-input"
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label>End Before:</label>
                    <input
                      type="date"
                      value={squadEndFilter}
                      onChange={(e) => setSquadEndFilter(e.target.value)}
                      className="admin-input"
                    />
                  </div>
                </div>
                <div className="table-wrapper">
                  <table className="squad-table">
                    <thead>
                      <tr>
                        <th className="sticky-col">Squad ({filteredSquads.length})</th>
                        <th>Curriculum</th>
                        <th>Status</th>
                        <th>Details</th>
                        <th>Users</th>
                        <th>Price</th>
                        <th>Payment Type</th>
                        <th># Kids</th>
                        <th># Days</th>
                        <th>Classes Left</th>
                        <th>Schedule</th>
                        <th>Dates</th>
                        <th>Grade Range</th>
                        <th>Delete</th>
                        <th>Duplicate</th>
                        <th>Create flow</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSquads
                        .slice()
                        .sort((a, b) => {
                          const aIsArchived = a.status === 'archived';
                          const bIsArchived = b.status === 'archived';
                          if (aIsArchived && !bIsArchived) return 1;
                          if (!aIsArchived && bIsArchived) return -1;
                          return (a.title || '').localeCompare(b.title || '');
                        })
                        .map((squad, index) => (
                          <tr key={squad.id} className={index % 2 === 0 ? 'even' : 'odd'}>
                            <td className="sticky-col">
                              {editingTitleSquadId === squad.id ? (
                                <input
                                  type="text"
                                  value={editingTitleSquadValue}
                                  onChange={(e) => setEditingTitleSquadValue(e.target.value)}
                                  onBlur={() => void handleSquadTitleUpdate(squad.id, editingTitleSquadValue)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') void handleSquadTitleUpdate(squad.id, editingTitleSquadValue);
                                    if (e.key === 'Escape') {
                                      setEditingTitleSquadId(null);
                                      setEditingTitleSquadValue('');
                                    }
                                  }}
                                  autoFocus
                                  disabled={savingTitleSquadId === squad.id}
                                  className="inline-edit-input"
                                />
                              ) : (
                                <div
                                  className="squad-title-cell editable-field"
                                  style={{ cursor: 'pointer' }}
                                  title="Click to rename squad"
                                  onClick={() => {
                                    setEditingTitleSquadId(squad.id);
                                    setEditingTitleSquadValue(String(squad.title || ''));
                                  }}
                                >
                                  {squad.title}
                                </div>
                              )}
                              <div className="squad-location-cell">{squadPlaceLabel(squad)}</div>
                            </td>
                            <td onClick={() => { setEditingPriceSquadId(null); setEditingPriceSquadValue(''); setEditingCurriculumSquadId(squad.id); }} style={{ cursor: 'pointer' }}>
                              {editingCurriculumSquadId === squad.id ? (
                                <select
                                  className="inline-edit-select"
                                  value={String((squad as any).curriculumId || 'music')}
                                  onChange={(e) => void handleCurriculumChange(squad.id, e.target.value)}
                                  onBlur={() => setEditingCurriculumSquadId(null)}
                                  autoFocus
                                >
                                  {curriculumOptions.map((id) => (
                                    <option key={id} value={id}>{id}</option>
                                  ))}
                                </select>
                              ) : (
                                <span className="editable-field">{String((squad as any).curriculumId || 'music')}</span>
                              )}
                            </td>
                            <td onClick={() => setEditingStatusSquadId(squad.id)} style={{ cursor: 'pointer' }}>
                              {editingStatusSquadId === squad.id ? (
                                <select
                                  value={squad.status || 'published'}
                                  onChange={(e) => handleStatusChange(squad.id, e.target.value as Squad['status'])}
                                  onBlur={() => setEditingStatusSquadId(null)}
                                  autoFocus
                                >
                                  <option value="lead">Lead</option>
                                  <option value="published">Published</option>
                                  <option value="draft">Draft</option>
                                  <option value="archived">Archived</option>
                                  <option value="canceled">Canceled</option>
                                </select>
                              ) : (
                                <span className={`status-badge status-${squad.status || 'published'}`}>
                                  {squad.status || 'published'}
                                </span>
                              )}
                            </td>
                            <td>
                              <button
                                className="manage-squad-details-btn"
                                onClick={() => navigate(`/app/admin/squads/${squad.id}`)}
                                title="Open full-screen squad details"
                              >
                                Squad Details
                              </button>
                            </td>
                            <td>
                              <button className="manage-users-btn" onClick={() => handleOpenManageModal(squad)}>
                                Users
                              </button>
                            </td>
                            <td
                              title={`${formatSquadPriceAdminTitle(squad)} · Click to edit`}
                              style={{ cursor: 'pointer', minWidth: 140 }}
                              onClick={() => {
                                if (editingPriceSquadId === squad.id) return;
                                setEditingPaymentModeSquadId(null);
                                setEditingCurriculumSquadId(null);
                                setEditingPriceSquadId(squad.id);
                                setEditingPriceSquadValue(seedUsdStringForPriceEdit(squad));
                              }}
                            >
                              {editingPriceSquadId === squad.id ? (
                                <input
                                  type="text"
                                  className="inline-edit-input"
                                  inputMode="decimal"
                                  autoFocus
                                  value={editingPriceSquadValue}
                                  onChange={(e) => setEditingPriceSquadValue(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  onBlur={(e) => void handleSquadPriceSave(squad.id, e.currentTarget.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      void handleSquadPriceSave(squad.id, (e.currentTarget as HTMLInputElement).value);
                                    }
                                    if (e.key === 'Escape') {
                                      setEditingPriceSquadId(null);
                                      setEditingPriceSquadValue('');
                                    }
                                  }}
                                  disabled={savingPriceSquadId === squad.id}
                                  placeholder="0.00"
                                  aria-label="Edit price"
                                />
                              ) : (
                                <span className="editable-field">{formatSquadPriceAdminCell(squad)}</span>
                              )}
                            </td>
                            <td onClick={() => { setEditingPriceSquadId(null); setEditingPriceSquadValue(''); setEditingPaymentModeSquadId(squad.id); }} style={{ cursor: 'pointer' }}>
                              {editingPaymentModeSquadId === squad.id ? (
                                <select
                                  className="inline-edit-select"
                                  value={normalizePaymentMode((squad as any).paymentMode)}
                                  onChange={(e) => void handlePaymentModeChange(squad.id, e.target.value)}
                                  onBlur={() => setEditingPaymentModeSquadId(null)}
                                  autoFocus
                                >
                                  <option value="stripe">Stripe</option>
                                  <option value="school_enrollment">School (Enrollment)</option>
                                  <option value="school_flat_fee">School (Flat fee)</option>
                                  <option value="external">External (legacy)</option>
                                </select>
                              ) : (
                                <span className="editable-field">{paymentModeLabel((squad as any).paymentMode)}</span>
                              )}
                            </td>
                            <td>{squad.studentIds?.length || 0}</td>
                            <td>
                              {(() => {
                                const mainDate = squad.schedule?.mainDate;
                                const startDate = mainDate || squad.schedule?.startDate;
                                const endDate = mainDate || squad.schedule?.endDate;
                                const dayOfWeek = mainDate ? dayCodeForYmd(mainDate) : squad.schedule?.dayOfWeek;
                                const allExceptions = [...(squad.schedule?.exceptions || []), ...(squad.schedule?.inheritedExceptions || [])];
                                const totalDays = calculateNumberOfDays(startDate, endDate, dayOfWeek, allExceptions);
                                const daysTaught = calculateDaysAlreadyTaught(startDate, endDate, dayOfWeek, allExceptions);
                                return `${daysTaught}/${totalDays}`;
                              })()}
                            </td>
                            <td>
                              {calculateClassesLeft(
                                squad.schedule?.mainDate || squad.schedule?.startDate,
                                squad.schedule?.mainDate || squad.schedule?.endDate,
                                squad.schedule?.mainDate ? dayCodeForYmd(squad.schedule.mainDate) : squad.schedule?.dayOfWeek,
                                [...(squad.schedule?.exceptions || []), ...(squad.schedule?.inheritedExceptions || [])],
                              )}
                            </td>
                            <td>
                              {(squad.schedule?.mainDate || squad.schedule?.dayOfWeek) && squad.schedule?.startTime && squad.schedule?.endTime
                                ? `${(squad.schedule?.mainDate ? dayCodeForYmd(squad.schedule.mainDate) : squad.schedule.dayOfWeek)?.substring(0,3)} ${formatTime(squad.schedule.startTime)} - ${formatTime(squad.schedule.endTime)} CT`
                                : 'Not Set'}
                            </td>
                            <td className="wrap">
                              <div>
                                {squad.schedule?.mainDate
                                  ? squad.schedule.mainDate
                                  : squad.schedule?.startDate && squad.schedule?.endDate
                                  ? `${squad.schedule.startDate} to ${squad.schedule.endDate}`
                                  : 'Not Set'}
                              </div>
                              {((squad.schedule?.exceptions?.length || 0) + (squad.schedule?.inheritedExceptions?.length || 0)) > 0 && (
                                <div className="exceptions-list">
                                  No Class: {Array.from(new Set([...(squad.schedule?.exceptions || []), ...(squad.schedule?.inheritedExceptions || [])])).sort().join(', ')}
                                </div>
                              )}
                              {squad.schedule?.makeups && squad.schedule.makeups.length > 0 && (
                                <div className="exceptions-list" style={{ color: '#000' }}>
                                  Makeups: {squad.schedule.makeups.join(', ')}
                                </div>
                              )}
                            </td>
                            <td>{squad.startGrade && squad.endGrade ? `${squad.startGrade} - ${squad.endGrade}` : 'N/A'}</td>
                            <td className="squad-list-actions">
                              <button onClick={() => setSquadToDelete(squad)} className="delete-btn">
                                DELETE
                              </button>
                            </td>
                            <td className="squad-list-actions">
                              {(() => {
                                const isDuplicating = duplicatingSquadId === String((squad as any)?.id || '');
                                return (
                                  <button
                                    type="button"
                                    className="manage-users-btn"
                                    onClick={() => void handleDuplicateSquad(squad)}
                                    disabled={isDuplicating}
                                    title="Duplicate this squad and create a new ops flow"
                                    style={{
                                      opacity: isDuplicating ? 0.7 : 1,
                                      cursor: isDuplicating ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    {isDuplicating ? 'Duplicating…' : 'Duplicate'}
                                  </button>
                                );
                              })()}
                            </td>
                            <td className="squad-list-actions">
                              {(() => {
                                const hasFlow = Boolean(opsFlowExistsBySquadId[String(squad.id)]);
                                const isCreating = creatingFlowSquadId === String(squad.id);
                                return (
                                  <button
                                    type="button"
                                    className="manage-users-btn"
                                    onClick={() => void handleCreateFlowForSquad(squad)}
                                    disabled={hasFlow || isCreating}
                                    title={hasFlow ? 'Flow already exists for this squad' : 'Create ops flow for this squad'}
                                    style={{
                                      opacity: hasFlow ? 0.45 : 1,
                                      cursor: hasFlow ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    {hasFlow ? 'Flow exists' : (isCreating ? 'Creating…' : 'Create flow')}
                                  </button>
                                );
                              })()}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSquadCreationForm = (onCancel: () => void) => (
    <div className="form-section">
      <h3>🎯 Create New Squad</h3>
      <form onSubmit={handleCreateSquad} className="admin-form">
        <div className="form-group">
          <label>Squad Title *</label>
          <input type="text" value={squadForm.title} onChange={(e) => setSquadForm({ ...squadForm, title: e.target.value })} placeholder="e.g., Fall 2023 Beginners" required />
        </div>
        <div className="form-group">
          <label>Partner location *</label>
          <select
            value={squadForm.locationId}
            onChange={(e) => {
              const value = e.target.value;
              setSquadForm({
                ...squadForm,
                locationId: value,
                schoolId: '',
              });
            }}
            className="admin-select"
            required
          >
            <option value="">Select a partner location</option>
            {partnerLocations
              .filter((location) => !squadCreationPartnerId || location.partnerId === squadCreationPartnerId)
              .map(location => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>
        </div>

        <div className="form-actions" style={{ marginTop: -4 }}>
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? '⏳ Creating...' : '➕ Create Squad Now'}
          </button>
          <button type="button" className="cancel-btn" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );

  const handleTeacherCellClick = (teacherId: string, field: string, currentValue: any) => {
    setEditingTeacherCell({ teacherId, field });
    if (field === 'emergencyContact') {
      setEditingTeacherValue(currentValue || { name: '', phone: '', relationship: '' });
    } else if (field === 'subjects') {
      setEditingTeacherValue(Array.isArray(currentValue) ? currentValue : []);
    } else if (field === 'hasCar' || field === 'hasDriversLicense' || field === 'backgroundCheckCleared') {
      // No default selection; only save once user explicitly picks Yes/No.
      setEditingTeacherValue(currentValue === true ? 'true' : currentValue === false ? 'false' : '');
    } else if (field === 'timeZone') {
      // No default selection; only save once user explicitly picks an option.
      setEditingTeacherValue(currentValue || '');
    } else {
      setEditingTeacherValue(currentValue ?? '');
    }
  };

  const handleTeacherSave = async (teacherId: string, field: string) => {
    return saveTeacherField(teacherId, field, editingTeacherValue);
  };

  const saveTeacherField = async (teacherId: string, field: string, rawValue: any) => {
    setSavingTeacher(true);
    try {
      const teacherRef = doc(db, 'users', teacherId);
      let valueToSave = rawValue;
      
      if (field === 'subjects') {
        const allowed = new Set(['music', 'ai']);
        const asArray = Array.isArray(rawValue)
          ? rawValue
          : String(rawValue || '')
              .split(',')
              .map((s: string) => s.trim());

        valueToSave = Array.from(
          new Set(
            asArray
              .map((s: any) => String(s).trim().toLowerCase())
              .filter((s: string) => allowed.has(s))
          )
        );
      } else if (field === 'displayName') {
        // Keep names clean; allow clearing if needed.
        valueToSave = String(rawValue || '').trim();
      } else if (field === 'teacherType') {
        const v = String(rawValue || '').trim().toLowerCase();
        valueToSave = v === 'lead' ? 'lead' : v === 'partner' ? 'partner' : '';
      } else if (field === 'phoneNumber') {
        const digits = String(rawValue || '').replace(/\D/g, '');
        // Allow clearing the field
        if (digits.length === 0) {
          valueToSave = '';
        } else if (digits.length === 11 && digits.startsWith('1')) {
          valueToSave = digits.slice(1); // drop country code
        } else if (digits.length !== 10) {
          void window.appAlert('Phone number must have exactly 10 digits (including area code).');
          return;
        } else {
          valueToSave = digits;
        }
      } else if (field === 'hasCar' || field === 'hasDriversLicense' || field === 'backgroundCheckCleared') {
        valueToSave = rawValue === 'true' || rawValue === true;
      } else if (field === 'deviceCount' || field === 'hourlyRate') {
        valueToSave = parseFloat(String(rawValue)) || 0;
      }
      
      await updateDoc(teacherRef, { [field]: valueToSave });
      
      setTeachers(prev => prev.map(t => 
        t.id === teacherId ? { ...t, [field]: valueToSave } : t
      ));
      setEditingTeacherCell(null);
    } catch (error) {
      console.error('Error updating teacher field:', error);
      void window.appAlert('Failed to update field');
    } finally {
      setSavingTeacher(false);
    }
  };

  const handleTeacherCancel = () => {
    setEditingTeacherCell(null);
    setEditingTeacherValue('');
  };

  const renderEditableTeacherCell = (
    teacher: User,
    field: string,
    value: any,
    type: 'text' | 'boolean' | 'number' | 'emergencyContact' | 'subjects' | 'staffStatus' | 'timezone' | 'teacherType' | 'neighborhood' | 'phone' | 'date' | 'textarea' = 'text'
  ) => {
    const isEditing = editingTeacherCell?.teacherId === teacher.id && editingTeacherCell?.field === field;

    const formatUsPhoneDisplay = (input: unknown): string => {
      const raw = typeof input === 'string' ? input : String(input ?? '');
      const digits = raw.replace(/\D/g, '');
      if (!digits) return '';
      const hasUsCountryCode = digits.length >= 11 && digits.startsWith('1');
      const main = hasUsCountryCode ? digits.slice(1) : digits;
      if (main.length !== 10) return raw;
      const prefix = hasUsCountryCode ? '1 ' : '';
      return `${prefix}(${main.slice(0, 3)}) ${main.slice(3, 6)}-${main.slice(6)}`;
    };
      
      if (isEditing) {
        if (type === 'staffStatus') {
          const options: Array<{ value: string; label: string }> = [
            { value: 'active', label: 'Active' },
            { value: 'sub', label: 'Sub' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'applicant', label: 'Applicant' },
            { value: 'offboarded', label: 'Offboarded' },
          ];
          const current = editingTeacherValue == null ? '' : String(editingTeacherValue);
          const currentLabel = options.find(o => o.value === current)?.label || 'Select…';
          return (
            <div
              tabIndex={0}
              onBlur={() => handleTeacherCancel()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') handleTeacherCancel();
              }}
              style={{ position: 'relative', width: '100%' }}
            >
              <button
                type="button"
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '4px',
                  fontSize: '13px',
                  background: '#fff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              autoFocus
            >
                {currentLabel}
              </button>
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  left: 0,
                  zIndex: 50,
                  background: '#fff',
                  border: '1px solid rgba(15, 23, 42, 0.18)',
                  borderRadius: 10,
                  boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
                  overflow: 'hidden',
                  minWidth: 220,
                }}
              >
                {options.map((opt) => {
                  const isSelected = opt.value === current;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onMouseDown={(e) => {
                        // Prevent blur/cancel before we save.
                        e.preventDefault();
                        setEditingTeacherValue(opt.value);
                        saveTeacherField(teacher.id, field, opt.value);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        fontSize: '13px',
                        border: 'none',
                        background: isSelected ? 'rgba(59, 130, 246, 0.12)' : '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      {isSelected ? '✓ ' : ''}{opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        } else if (type === 'boolean') {
          const options: Array<{ value: string; label: string }> = [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ];
          const current = editingTeacherValue == null ? '' : String(editingTeacherValue);
          const currentLabel = options.find(o => o.value === current)?.label || 'Select…';
          return (
            <div
              tabIndex={0}
              onBlur={() => handleTeacherCancel()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') handleTeacherCancel();
              }}
              style={{ position: 'relative', width: '100%' }}
            >
              <button
                type="button"
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '4px',
                  fontSize: '13px',
                  background: '#fff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
                autoFocus
              >
                {currentLabel}
              </button>
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  left: 0,
                  zIndex: 50,
                  background: '#fff',
                  border: '1px solid rgba(15, 23, 42, 0.18)',
                  borderRadius: 10,
                  boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
                  overflow: 'hidden',
                  minWidth: 160,
                }}
              >
                {options.map((opt) => {
                  const isSelected = opt.value === current;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setEditingTeacherValue(opt.value);
                        saveTeacherField(teacher.id, field, opt.value);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        fontSize: '13px',
                        border: 'none',
                        background: isSelected ? 'rgba(59, 130, 246, 0.12)' : '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      {isSelected ? '✓ ' : ''}{opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        } else if (type === 'timezone') {
          const options: Array<{ value: string; label: string }> = [
            { value: 'America/New_York', label: 'Eastern (America/New_York)' },
            { value: 'America/Chicago', label: 'Central (America/Chicago)' },
            { value: 'America/Denver', label: 'Mountain (America/Denver)' },
            { value: 'America/Phoenix', label: 'Arizona (America/Phoenix)' },
            { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
            { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
            { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
          ];
          const current = editingTeacherValue == null ? '' : String(editingTeacherValue);
          const currentLabel = options.find(o => o.value === current)?.label || 'Select…';
          return (
            <div
              tabIndex={0}
              onBlur={() => handleTeacherCancel()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') handleTeacherCancel();
              }}
              style={{ position: 'relative', width: '100%' }}
            >
              <button
                type="button"
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '4px',
                  fontSize: '13px',
                  background: '#fff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
                autoFocus
              >
                {currentLabel}
              </button>
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  left: 0,
                  zIndex: 50,
                  background: '#fff',
                  border: '1px solid rgba(15, 23, 42, 0.18)',
                  borderRadius: 10,
                  boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
                  overflow: 'hidden',
                  minWidth: 280,
                }}
              >
                {options.map((opt) => {
                  const isSelected = opt.value === current;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setEditingTeacherValue(opt.value);
                        saveTeacherField(teacher.id, field, opt.value);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        fontSize: '13px',
                        border: 'none',
                        background: isSelected ? 'rgba(59, 130, 246, 0.12)' : '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      {isSelected ? '✓ ' : ''}{opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        } else if (type === 'teacherType') {
          const options: Array<{ value: string; label: string }> = [
            { value: 'lead', label: 'Lead' },
            { value: 'partner', label: 'On-site Partner' },
          ];
          const current = editingTeacherValue == null ? '' : String(editingTeacherValue);
          const currentLabel = options.find(o => o.value === current)?.label || 'Select…';

          return (
            <div
              tabIndex={0}
              onBlur={() => handleTeacherCancel()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') handleTeacherCancel();
              }}
              style={{ position: 'relative', width: '100%' }}
            >
              <button
                type="button"
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '4px',
                  fontSize: '13px',
                  background: '#fff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
                autoFocus
              >
                {currentLabel}
              </button>
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  left: 0,
                  zIndex: 50,
                  background: '#fff',
                  border: '1px solid rgba(15, 23, 42, 0.18)',
                  borderRadius: 10,
                  boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
                  overflow: 'hidden',
                  minWidth: 220,
                }}
              >
                {options.map((opt) => {
                  const isSelected = opt.value === current;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setEditingTeacherValue(opt.value);
                        saveTeacherField(teacher.id, field, opt.value);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        fontSize: '13px',
                        border: 'none',
                        background: isSelected ? 'rgba(59, 130, 246, 0.12)' : '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      {isSelected ? '✓ ' : ''}{opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        } else if (type === 'neighborhood') {
          const query = String(editingTeacherValue || '').trim();
          const googleSuggestions = neighborhoodGooglePredictions
            .map(p => ({
              key: `g:${p.placeId}`,
              label: normalizeGoogleNeighborhoodLabel(p.description),
              source: 'google' as const,
              placeId: p.placeId,
              rawDescription: p.description,
            }))
            .filter(s => s.label.length > 0);

          const typedSuggestion = query.length > 0
            ? [{
                key: `t:${query}`,
                label: `Use “${query}”`,
                source: 'typed' as const,
              }]
            : [];

          // Dedupe by label (case-insensitive), prefer Google results first.
          const seen = new Set<string>();
          const suggestions = [...typedSuggestion, ...googleSuggestions].filter((s) => {
            const k = s.label.toLowerCase();
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
          });

          return (
            <div
              tabIndex={0}
              onBlur={() => handleTeacherCancel()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') handleTeacherCancel();
              }}
              style={{ position: 'relative', width: '100%' }}
            >
              <input
                type="text"
                value={String(editingTeacherValue || '')}
                onChange={(e) => setEditingTeacherValue(e.target.value)}
                placeholder="Type neighborhood, city, or ZIP…"
              style={{ padding: '4px', fontSize: '13px', width: '100%' }}
              autoFocus
              />
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  left: 0,
                  zIndex: 60,
                  background: '#fff',
                  border: '1px solid rgba(15, 23, 42, 0.18)',
                  borderRadius: 10,
                  boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
                  overflow: 'hidden',
                  minWidth: 260,
                  maxWidth: 360,
                }}
              >
                {neighborhoodGoogleLoading && (
                  <div style={{ padding: '10px 12px', fontSize: 13, color: '#64748b' }}>
                    Searching…
                  </div>
                )}
                {neighborhoodGoogleError && (
                  <div style={{ padding: '10px 12px', fontSize: 12, color: '#b45309', borderTop: '1px solid rgba(15, 23, 42, 0.08)' }}>
                    {neighborhoodGoogleError} (you can still type anything)
                  </div>
                )}
                {suggestions.length === 0 ? (
                  <div style={{ padding: '10px 12px', fontSize: 13, color: '#64748b' }}>
                    No matches
                  </div>
                ) : (
                  suggestions.map((sug: any) => (
                    <button
                      key={sug.key}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const selected =
                          sug.source === 'typed'
                            ? query
                            : sug.source === 'google'
                              ? normalizeGoogleNeighborhoodLabel(sug.rawDescription || sug.label)
                              : sug.label;
                        setEditingTeacherValue(selected);
                        saveTeacherField(teacher.id, field, selected);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        fontSize: '13px',
                        border: 'none',
                        background: '#fff',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {sug.label}
                    </button>
                  ))
                )}
              </div>
            </div>
          );
        } else if (type === 'subjects') {
          const options: Array<{ value: 'music' | 'ai'; label: string }> = [
            { value: 'music', label: 'Music' },
            { value: 'ai', label: 'AI' },
          ];

          const current: string[] = Array.isArray(editingTeacherValue) ? editingTeacherValue : [];
          const currentLabels = options
            .filter(o => current.includes(o.value))
            .map(o => o.label);
          const currentLabel = currentLabels.length > 0 ? currentLabels.join(', ') : 'Select…';

          const toggle = (v: 'music' | 'ai') => {
            const next = current.includes(v)
              ? current.filter(x => x !== v)
              : [...current, v];
            setEditingTeacherValue(next);
          };

          return (
            <div
              tabIndex={0}
              onBlur={() => handleTeacherCancel()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') handleTeacherCancel();
              }}
              style={{ position: 'relative', width: '100%' }}
            >
              <button
                type="button"
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '4px',
                  fontSize: '13px',
                  background: '#fff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
                autoFocus
              >
                {currentLabel}
              </button>

              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  left: 0,
                  zIndex: 50,
                  background: '#fff',
                  border: '1px solid rgba(15, 23, 42, 0.18)',
                  borderRadius: 10,
                  boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
                  overflow: 'hidden',
                  minWidth: 220,
                  padding: 10,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {options.map((opt) => {
                    const checked = current.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onMouseDown={(e) => {
                          // Prevent blur/cancel before toggling.
                          e.preventDefault();
                          toggle(opt.value);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 10px',
                          fontSize: '13px',
                          border: '1px solid rgba(15, 23, 42, 0.12)',
                          borderRadius: 10,
                          background: checked ? 'rgba(59, 130, 246, 0.10)' : '#fff',
                          cursor: 'pointer',
                        }}
                      >
                        <span
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 5,
                            border: '2px solid rgba(15, 23, 42, 0.28)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: checked ? '#2563eb' : 'transparent',
                            color: '#fff',
                            fontWeight: 900,
                            fontSize: 12,
                          }}
                          aria-hidden
                        >
                          {checked ? '✓' : ''}
                        </span>
                        <span style={{ fontWeight: 700 }}>{opt.label}</span>
                      </button>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleTeacherCancel();
                    }}
                    style={{
                      padding: '8px 10px',
                      borderRadius: 10,
                      border: '1px solid rgba(15, 23, 42, 0.12)',
                      background: '#fff',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      saveTeacherField(teacher.id, field, current);
                    }}
                    style={{
                      padding: '8px 10px',
                      borderRadius: 10,
                      border: '1px solid rgba(37, 99, 235, 0.40)',
                      background: 'rgba(37, 99, 235, 0.10)',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 800,
                      color: '#1d4ed8',
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          );
        } else if (type === 'textarea') {
          return (
            <div style={{ position: 'relative' }}>
              <textarea
                value={editingTeacherValue}
                onChange={(e) => setEditingTeacherValue(e.target.value)}
                style={{ 
                  padding: '8px', 
                  fontSize: '13px', 
                  width: '100%', 
                  minWidth: '220px',
                  minHeight: '120px',
                  resize: 'vertical',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontFamily: 'inherit',
                  lineHeight: '1.4',
                }}
                autoFocus
                placeholder="Enter bio..."
                onKeyDown={(e) => {
                  if (e.key === 'Escape') handleTeacherCancel();
                }}
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleTeacherCancel();
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleTeacherSave(teacher.id, field);
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: '#3b82f6',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          );
        } else if (type === 'emergencyContact') {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <input
                type="text"
                placeholder="Name"
                value={editingTeacherValue.name || ''}
                onChange={(e) => setEditingTeacherValue({ ...editingTeacherValue, name: e.target.value })}
                onBlur={() => handleTeacherSave(teacher.id, field)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTeacherSave(teacher.id, field);
                  if (e.key === 'Escape') handleTeacherCancel();
                }}
                style={{ padding: '4px', fontSize: '13px' }}
              />
              <input
                type="text"
                placeholder="Phone"
                value={editingTeacherValue.phone || ''}
                onChange={(e) => setEditingTeacherValue({ ...editingTeacherValue, phone: e.target.value })}
                onBlur={() => handleTeacherSave(teacher.id, field)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTeacherSave(teacher.id, field);
                  if (e.key === 'Escape') handleTeacherCancel();
                }}
                style={{ padding: '4px', fontSize: '13px' }}
              />
              <input
                type="text"
                placeholder="Relationship"
                value={editingTeacherValue.relationship || ''}
                onChange={(e) => setEditingTeacherValue({ ...editingTeacherValue, relationship: e.target.value })}
                onBlur={() => handleTeacherSave(teacher.id, field)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTeacherSave(teacher.id, field);
                  if (e.key === 'Escape') handleTeacherCancel();
                }}
                style={{ padding: '4px', fontSize: '13px' }}
              />
            </div>
          );
        } else {
          return (
            <input
              type={type === 'phone' ? 'tel' : type === 'date' ? 'date' : type}
              value={editingTeacherValue}
              onChange={(e) => setEditingTeacherValue(e.target.value)}
              onBlur={() => handleTeacherSave(teacher.id, field)}
              style={{ padding: '4px', fontSize: '13px', width: '100%' }}
              autoFocus
              inputMode={type === 'phone' ? 'numeric' : undefined}
              placeholder={type === 'phone' ? '1234567890' : undefined}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTeacherSave(teacher.id, field);
                if (e.key === 'Escape') handleTeacherCancel();
              }}
            />
          );
        }
      }
      
      let displayValue = (value === '' ? '—' : value ?? '—');
      if (type === 'staffStatus') {
        const statusMap: Record<string, string> = {
          'active': 'Active',
          'sub': 'Sub',
          'inactive': 'Inactive',
          'applicant': 'Applicant',
          'offboarded': 'Offboarded'
        };
        const raw = typeof value === 'string' ? value : '';
        // Display the real stored status. If missing/invalid, show "undefined"
        // so admins can spot and correct records (do not mask it as "Active").
        displayValue = statusMap[raw] || 'undefined';
      } else if (type === 'boolean') {
        displayValue =
          value === true ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '18px',
                  fontWeight: 900,
                  lineHeight: 1,
                  color: '#16a34a', // green
                }}
                aria-label="Yes"
                title="Yes"
              >
                ✓
              </span>
              <span style={{ fontSize: 12, fontWeight: 800, color: '#111827' }}>Yes</span>
            </span>
          ) : value === false ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '18px',
                  fontWeight: 900,
                  lineHeight: 1,
                  color: '#dc2626', // red
                }}
                aria-label="No"
                title="No"
              >
                ✕
              </span>
              <span style={{ fontSize: 12, fontWeight: 800, color: '#111827' }}>No</span>
            </span>
          ) : (
            '—'
          );
      } else if (type === 'timezone') {
        const tzMap: Record<string, string> = {
          'America/New_York': 'ET',
          'America/Chicago': 'CT',
          'America/Denver': 'MT',
          'America/Phoenix': 'AZ',
          'America/Los_Angeles': 'PT',
          'America/Anchorage': 'AK',
          'Pacific/Honolulu': 'HI',
        };
        displayValue = (typeof value === 'string' && tzMap[value]) ? tzMap[value] : (value ?? '—');
      } else if (field === 'teacherType') {
        const typeMap: Record<string, string> = { lead: 'Lead', partner: 'On-site Partner' };
        displayValue = (typeof value === 'string' && typeMap[value]) ? typeMap[value] : (value ?? '—');
      } else if (field === 'phoneNumber') {
        const formatted = formatUsPhoneDisplay(value);
        displayValue = formatted ? formatted : (value ?? '—');
      } else if (type === 'emergencyContact' && value) {
        const phone = formatUsPhoneDisplay(value.phone);
        displayValue = `${value.name || '—'} (${phone || value.phone || '—'}, ${value.relationship || '—'})`;
      } else if (type === 'subjects' && Array.isArray(value)) {
        const map: Record<string, string> = { music: 'Music', ai: 'AI' };
        displayValue = value.map(v => map[v] || v).join(', ') || '—';
      } else if (type === 'textarea') {
        const bioText = typeof value === 'string' ? value : '';
        const truncated = bioText.length > 60 ? bioText.slice(0, 60) + '...' : bioText;
        displayValue = truncated || '—';
      }
      
      return (
        <div 
          onClick={() => handleTeacherCellClick(teacher.id, field, value)}
          style={{ 
            cursor: 'pointer', 
            padding: '4px',
            minHeight: '24px',
            borderRadius: '4px',
            ...(type === 'textarea' ? { 
              fontSize: '12px', 
              lineHeight: '1.3',
              color: value ? '#374151' : '#9ca3af',
              maxWidth: '220px',
            } : {})
          }}
          title={type === 'textarea' ? (value || 'Click to add bio') : 'Click to edit'}
        >
          {displayValue}
        </div>
      );
    };

  const [syncingPublicTeachers, setSyncingPublicTeachers] = useState(false);
  const [uploadingHeadshotForTeacher, setUploadingHeadshotForTeacher] = useState<string | null>(null);
  const [showCoriBatchExport, setShowCoriBatchExport] = useState(false);

  const handleSyncAllPublicTeachers = async () => {
    if (!userData?.isAdmin) {
      setMessage('❌ Only admins can sync public teachers');
      return;
    }
    setSyncingPublicTeachers(true);
    setMessage('');
    try {
      const fn = httpsCallable(getFunctions(), 'syncAllPublicTeachers');
      const result: any = await fn({});
      if (result?.data?.success) {
        setMessage(`✅ Public teachers synced: ${result.data.synced} synced, ${result.data.skipped} skipped, ${result.data.removed} removed`);
      } else {
        setMessage('⚠️ Sync completed but no confirmation received');
      }
    } catch (err: any) {
      console.error('Failed to sync public teachers:', err);
      setMessage(`❌ Failed to sync: ${err?.message || 'Unknown error'}`);
    } finally {
      setSyncingPublicTeachers(false);
    }
  };

  const handleToggleTeacherPublic = async (teacherId: string, currentValue: boolean) => {
    try {
      const teacherRef = doc(db, 'users', teacherId);
      await updateDoc(teacherRef, { isPublic: !currentValue });
      setTeachers(prev => prev.map(t => t.id === teacherId ? { ...t, isPublic: !currentValue } : t));
      setMessage(`✅ Teacher ${!currentValue ? 'marked as public' : 'removed from public'}`);
    } catch (err: any) {
      console.error('Failed to toggle public status:', err);
      setMessage(`❌ Failed to update: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleTeacherHeadshotUpload = async (teacherId: string, file: File) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setMessage('❌ Invalid file type. Please upload a JPG, PNG, GIF, or WebP image.');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage('❌ File too large. Maximum size is 5MB.');
      return;
    }
    
    setUploadingHeadshotForTeacher(teacherId);
    try {
      const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      const storage = getStorage();
      
      // Get file extension
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const storagePath = `teachers/${teacherId}/headshot.${ext}`;
      const storageRef = ref(storage, storagePath);
      
      // Upload file
      await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadUrl = await getDownloadURL(storageRef);
      
      // Update teacher document with the URL
      const teacherRef = doc(db, 'users', teacherId);
      await updateDoc(teacherRef, { 
        headshotUrl: downloadUrl,
        headshotUpdatedAt: Timestamp.now()
      });
      
      // Update local state
      setTeachers(prev => prev.map(t => t.id === teacherId ? { ...t, headshotUrl: downloadUrl } : t));
      setMessage('✅ Headshot uploaded successfully');
    } catch (err: any) {
      console.error('Failed to upload headshot:', err);
      setMessage(`❌ Failed to upload: ${err?.message || 'Unknown error'}`);
    } finally {
      setUploadingHeadshotForTeacher(null);
    }
  };

  // ----------------------
  // Teacher trainings / certifications / clearances
  // Stored in Firestore: /teachers/{teacherId}
  // Stored in Storage under: /teachers/{teacherId}/...
  // ----------------------
  const [uploadingTeacherComplianceKey, setUploadingTeacherComplianceKey] = useState<string | null>(null);

  const saveTeacherCompliancePatch = useCallback(async (teacherId: string, patch: Record<string, any>) => {
    const updatedBy = userData?.id || '';
    await setDoc(
      doc(db, 'teachers', teacherId),
      { ...patch, updatedAt: Timestamp.now(), updatedBy } as any,
      { merge: true }
    );
    setTeachers(prev => prev.map(t => (t.id === teacherId ? ({ ...t, ...patch } as any) : t)));
  }, [userData?.id]);

  const uploadTeacherComplianceFile = useCallback(async (
    teacherId: string,
    kind:
      | 'overtureBgCheckFile'
      | 'mandatoryReportingCertificationFile'
      | 'signedContract',
    file: File
  ) => {
    if (!file) return;

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isImage = file.type.startsWith('image/');
    if (!isPdf && !isImage) {
      setMessage('❌ Please upload a PDF or an image file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage('❌ File too large. Maximum size is 10MB.');
      return;
    }

    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const rand = Math.random().toString(16).slice(2, 8);
    const ext = (file.name.split('.').pop() || (isPdf ? 'pdf' : 'png')).toLowerCase().replace(/[^a-z0-9]/g, '');

    const folder =
      kind === 'overtureBgCheckFile'
        ? `teachers/${teacherId}/compliance/overture-bg-check`
        : kind === 'mandatoryReportingCertificationFile'
          ? `teachers/${teacherId}/compliance/mandatory-reporting`
          : `teachers/${teacherId}/contracts`;

    const storagePath = `${folder}/${stamp}-${rand}.${ext || (isPdf ? 'pdf' : 'png')}`;
    const key = `${teacherId}:${kind}:${storagePath}`;
    setUploadingTeacherComplianceKey(key);
    try {
      const { uploadBytes, getDownloadURL, ref: storageRef } = await import('firebase/storage');
      const r = storageRef(storage, storagePath);
      await uploadBytes(r, file, file.type ? { contentType: file.type } : undefined);
      const url = await getDownloadURL(r);

      const meta: UploadedFileMeta = {
        storagePath,
        url,
        filename: file.name || undefined,
        contentType: file.type || undefined,
        sizeBytes: file.size || undefined,
        uploadedAt: Timestamp.now(),
        uploadedBy: userData?.id || '',
      };

      if (kind === 'signedContract') {
        const teacher = teachers.find(t => t.id === teacherId) as any;
        const prev: UploadedFileMeta[] = Array.isArray(teacher?.signedContracts) ? teacher.signedContracts : [];
        await saveTeacherCompliancePatch(teacherId, { signedContracts: [meta, ...prev] });
      } else {
        await saveTeacherCompliancePatch(teacherId, { [kind]: meta });
      }
      setMessage('✅ Uploaded');
    } catch (e: any) {
      console.error('Failed to upload teacher compliance file', e);
      setMessage(`❌ Upload failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setUploadingTeacherComplianceKey(null);
    }
  }, [saveTeacherCompliancePatch, storage, teachers, userData?.id]);

  const renderFileCell = (
    teacher: User,
    meta: UploadedFileMeta | null | undefined,
    uploadKind: 'overtureBgCheckFile' | 'mandatoryReportingCertificationFile'
  ) => {
    const uploading = uploadingTeacherComplianceKey?.startsWith(`${teacher.id}:${uploadKind}:`);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {meta?.url ? (
          <a href={meta.url} target="_blank" rel="noreferrer" style={{ fontWeight: 800, fontSize: 12 }}>
            View
          </a>
        ) : (
          <span style={{ color: '#6b7280', fontSize: 12 }}>—</span>
        )}
        <label style={{ fontSize: 11, color: '#2563eb', cursor: uploading ? 'wait' : 'pointer', textDecoration: 'underline' }}>
          {uploading ? 'Uploading…' : 'Upload'}
          <input
            type="file"
            accept="application/pdf,image/*"
            style={{ display: 'none' }}
            disabled={!!uploadingTeacherComplianceKey}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadTeacherComplianceFile(teacher.id, uploadKind, f);
              e.target.value = '';
            }}
          />
        </label>
      </div>
    );
  };

  const renderSignedContractsCell = (teacher: User) => {
    const arr: UploadedFileMeta[] = Array.isArray((teacher as any).signedContracts) ? (teacher as any).signedContracts : [];
    const uploading = uploadingTeacherComplianceKey?.startsWith(`${teacher.id}:signedContract:`);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 800 }}>{arr.length} file{arr.length === 1 ? '' : 's'}</div>
        {arr.slice(0, 2).map((m, idx) => (
          <a key={`${m.storagePath || idx}`} href={m.url} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 800 }}>
            {m.filename || `Contract ${idx + 1}`}
          </a>
        ))}
        {arr.length > 2 && <div style={{ fontSize: 11, color: '#6b7280' }}>+ {arr.length - 2} more</div>}
        <label style={{ fontSize: 11, color: '#2563eb', cursor: uploading ? 'wait' : 'pointer', textDecoration: 'underline' }}>
          {uploading ? 'Uploading…' : 'Add'}
          <input
            type="file"
            accept="application/pdf,image/*"
            style={{ display: 'none' }}
            disabled={!!uploadingTeacherComplianceKey}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadTeacherComplianceFile(teacher.id, 'signedContract', f);
              e.target.value = '';
            }}
          />
        </label>
      </div>
    );
  };

  const renderBooleanToggleCell = (teacher: User, field: 'cycCleared' | 'ogTrainingComplete') => {
    const v = (teacher as any)[field];
    if (v !== true && v !== false) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={() => saveTeacherCompliancePatch(teacher.id, { [field]: true })}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              borderRadius: 10,
              border: '1px solid rgba(22, 163, 74, 0.35)',
              background: 'rgba(22, 163, 74, 0.08)',
              cursor: 'pointer',
            }}
            title="Set to Yes"
          >
            <span style={{ fontSize: 18, fontWeight: 900, lineHeight: 1, color: '#16a34a' }}>✓</span>
            <span style={{ fontSize: 12, fontWeight: 900, color: '#111827' }}>Yes</span>
          </button>
          <button
            type="button"
            onClick={() => saveTeacherCompliancePatch(teacher.id, { [field]: false })}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              borderRadius: 10,
              border: '1px solid rgba(220, 38, 38, 0.35)',
              background: 'rgba(220, 38, 38, 0.06)',
              cursor: 'pointer',
            }}
            title="Set to No"
          >
            <span style={{ fontSize: 18, fontWeight: 900, lineHeight: 1, color: '#dc2626' }}>✕</span>
            <span style={{ fontSize: 12, fontWeight: 900, color: '#111827' }}>No</span>
          </button>
        </span>
      );
    }
    return v === true ? (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            display: 'inline-block',
            fontSize: '18px',
            fontWeight: 900,
            lineHeight: 1,
            color: '#16a34a',
            cursor: 'pointer',
          }}
          aria-label="Yes"
          title="Yes"
          onClick={() => saveTeacherCompliancePatch(teacher.id, { [field]: false })}
        >
          ✓
        </span>
        <span style={{ fontSize: 12, fontWeight: 800, color: '#111827' }}>Yes</span>
      </span>
    ) : (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            display: 'inline-block',
            fontSize: '18px',
            fontWeight: 900,
            lineHeight: 1,
            color: '#dc2626',
            cursor: 'pointer',
          }}
          aria-label="No"
          title="No"
          onClick={() => saveTeacherCompliancePatch(teacher.id, { [field]: true })}
        >
          ✕
        </span>
        <span style={{ fontSize: 12, fontWeight: 800, color: '#111827' }}>No</span>
      </span>
    );
  };

  const renderFirstClassObservedCell = (teacher: User) => {
    const f: TeacherFirstClassObserved | null = (teacher as any).firstClassObserved || null;
    const label = f?.date
      ? `${f.date}${f.observer ? ` — ${f.observer}` : ''}`
      : '—';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: f?.date ? '#111827' : '#6b7280' }}>{label}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="add-squad-btn"
            style={{ padding: '4px 8px', fontSize: 11 }}
            onClick={async (e) => {
              e.stopPropagation();
              const date = await window.appPrompt('First class observed date (YYYY-MM-DD)', f?.date || '');
              if (date === null) return;
              const observer = await window.appPrompt('Observer name', f?.observer || '');
              if (observer === null) return;
              await saveTeacherCompliancePatch(teacher.id, { firstClassObserved: { date: String(date || '').trim() || null, observer: String(observer || '').trim() || null } });
            }}
          >
            Edit
          </button>
          {(f?.date || f?.observer) && (
            <button
              type="button"
              className="delete-btn"
              style={{ padding: '4px 8px', fontSize: 11, background: '#1f2937' }}
              onClick={(e) => {
                e.stopPropagation();
                saveTeacherCompliancePatch(teacher.id, { firstClassObserved: null });
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>
    );
  };

  // Partner-scoped clearances modal. Legacy school records are read-only history.
  const [schoolClearanceModalTeacherId, setSchoolClearanceModalTeacherId] = useState<string | null>(null);
  const [schoolClearanceModalLoading, setSchoolClearanceModalLoading] = useState(false);
  const [schoolClearanceModalError, setSchoolClearanceModalError] = useState<string | null>(null);
  const [schoolClearanceModalRows, setSchoolClearanceModalRows] = useState<
    Array<{ schoolId: string; schoolName?: string | null; items: TeacherSchoolClearanceItem[] }>
  >([]);
  const [schoolClearanceUploadSchoolId, setSchoolClearanceUploadSchoolId] = useState<string>('');
  const [schoolClearanceUploadType, setSchoolClearanceUploadType] = useState<TeacherBgCheckType>('cori');
  const [schoolClearanceUploadOtherLabel, setSchoolClearanceUploadOtherLabel] = useState<string>('');

  const loadTeacherSchoolClearances = useCallback(async (teacherId: string) => {
    setSchoolClearanceModalLoading(true);
    setSchoolClearanceModalError(null);
    try {
      const partnerIds = Array.from(new Set(partnerLocations.map(location => location.partnerId).filter(Boolean)));
      const canonicalSnaps = await Promise.all(partnerIds.map(partnerId =>
        getDoc(doc(db, 'partners', partnerId, 'teacherClearances', teacherId))
      ));
      const canonicalRows = canonicalSnaps.filter(snapshot => snapshot.exists()).map(snapshot => {
        const value: any = snapshot.data() || {};
        const items = Object.entries(value.requirementStatuses || {}).map(([requirementId, raw]: [string, any]) => ({
          id: `${value.partnerId}:${requirementId}`,
          type: requirementId as TeacherBgCheckType,
          typeLabel: `${requirementId.replace(/_/g, ' ')} — ${String(raw?.status || 'not started')}`,
          uploadedAt: raw?.completedAt || null,
        } as TeacherSchoolClearanceItem));
        return { schoolId: String(value.partnerId || ''), schoolName: String(value.partnerId || ''), items };
      });
      const snap = await getDocs(collection(db, 'teachers', teacherId, 'schoolClearances'));
      const historicalRows = snap.docs.map((d) => {
        const v: any = d.data() || {};
        const items = Array.isArray(v.items) ? (v.items as TeacherSchoolClearanceItem[]) : [];
        return {
          schoolId: String(v.schoolId || d.id),
          schoolName: `${v.schoolName ? String(v.schoolName) : d.id} (historical)`,
          items,
        };
      });
      const rows = [...canonicalRows, ...historicalRows];
      rows.sort((a, b) => String(a.schoolName || a.schoolId).localeCompare(String(b.schoolName || b.schoolId)));
      setSchoolClearanceModalRows(rows);
    } catch (e: any) {
      console.warn('Failed to load teacher school clearances:', e);
      setSchoolClearanceModalError(e?.message ? String(e.message) : 'Failed to load school clearances.');
      setSchoolClearanceModalRows([]);
    } finally {
      setSchoolClearanceModalLoading(false);
    }
  }, [partnerLocations]);

  const uploadTeacherSchoolClearance = useCallback(async (teacherId: string, file: File) => {
    if (!file) return;
    const partnerId = String(schoolClearanceUploadSchoolId || '').trim();
    if (!partnerId) {
      setSchoolClearanceModalError('Choose a partner.');
      return;
    }
    const type = schoolClearanceUploadType;
    const otherLabel = String(schoolClearanceUploadOtherLabel || '').trim();
    if (type === 'other' && !otherLabel) {
      setSchoolClearanceModalError('Enter a label for “Other”.');
      return;
    }

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isImage = file.type.startsWith('image/');
    if (!isPdf && !isImage) {
      setSchoolClearanceModalError('Upload a PDF or an image.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setSchoolClearanceModalError('File too large (max 10MB).');
      return;
    }

    setSchoolClearanceModalError(null);
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const rand = Math.random().toString(16).slice(2, 8);
    const ext = (file.name.split('.').pop() || (isPdf ? 'pdf' : 'png')).toLowerCase().replace(/[^a-z0-9]/g, '');
    const storagePath = `partners/${partnerId}/teacherClearances/${teacherId}/${type}/${stamp}-${rand}.${ext || (isPdf ? 'pdf' : 'png')}`;
    const key = `${teacherId}:schoolClearance:${storagePath}`;
    setUploadingTeacherComplianceKey(key);
    try {
      const { uploadBytes, getDownloadURL, ref: storageRef } = await import('firebase/storage');
      const r = storageRef(storage, storagePath);
      await uploadBytes(r, file, file.type ? { contentType: file.type } : undefined);
      const url = await getDownloadURL(r);

      const fileMeta: UploadedFileMeta = {
        storagePath,
        url,
        filename: file.name || undefined,
        contentType: file.type || undefined,
        sizeBytes: file.size || undefined,
        uploadedAt: Timestamp.now(),
        uploadedBy: userData?.id || '',
      };
      const evidenceId = `${stamp}-${rand}`;
      const requirementId = type === 'other'
        ? `other_${otherLabel.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`
        : type;
      const clearanceRef = doc(db, 'partners', partnerId, 'teacherClearances', teacherId);
      const prevSnap = await getDoc(clearanceRef);
      const prev: any = prevSnap.exists() ? prevSnap.data() : {};
      await setDoc(doc(collection(clearanceRef, 'evidence'), evidenceId), {
        id: evidenceId,
        partnerId,
        teacherId,
        requirementId,
        file: fileMeta,
        submittedAt: Timestamp.now(),
        submittedBy: userData?.id || '',
      });
      await setDoc(
        clearanceRef,
        {
          partnerId,
          teacherId,
          cleared: false,
          missingRequirementIds: Array.isArray(prev.missingRequirementIds) ? prev.missingRequirementIds : [],
          expiringRequirementIds: Array.isArray(prev.expiringRequirementIds) ? prev.expiringRequirementIds : [],
          requirementStatuses: {
            ...(prev.requirementStatuses || {}),
            [requirementId]: {
              requirementId,
              requirementVersion: 1,
              status: 'submitted',
              latestEvidenceId: evidenceId,
              completedAt: Timestamp.now(),
            },
          },
          updatedAt: Timestamp.now(),
          updatedBy: userData?.id || '',
        } as any,
        { merge: true }
      );

      await loadTeacherSchoolClearances(teacherId);
      setMessage('✅ Uploaded partner clearance');
    } catch (e: any) {
      console.error('Failed to upload school clearance', e);
      setSchoolClearanceModalError(e?.message ? String(e.message) : 'Upload failed.');
    } finally {
      setUploadingTeacherComplianceKey(null);
    }
  }, [
    storage,
    userData?.id,
    schoolClearanceUploadSchoolId,
    schoolClearanceUploadType,
    schoolClearanceUploadOtherLabel,
    loadTeacherSchoolClearances,
  ]);

  const renderCertificationWaitlist = () => {
    const formatSignupDate = (v: any) => {
      if (!v) return '—';
      try {
        const d = typeof v?.toDate === 'function' ? v.toDate() : (v instanceof Date ? v : new Date(v));
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleString();
      } catch (_) {
        return '—';
      }
    };

    const certificationCounts = certificationWaitlist.reduce<Record<string, number>>((acc, entry) => {
      const titles = waitlistCertificationTitles(entry);
      (titles.length ? titles : ['Unknown certification']).forEach((title) => {
        acc[title] = (acc[title] || 0) + 1;
      });
      return acc;
    }, {});

    return (
      <div className="teachers-tab-container">
        <div className="admin-content-card">
          <div
            className="teacher-actions"
            style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
          >
            <button onClick={loadCertificationWaitlist} className="add-squad-btn">
              🔄 Refresh Waitlist
            </button>
          </div>

          <p style={{ color: '#6b7280', marginTop: '1rem', marginBottom: '1rem' }}>
            Certification waitlist signups from the public website. Each signup includes the selected certification, contact information, and the applicant's reason for joining.
          </p>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: '1rem' }}>
            {Object.entries(certificationCounts).map(([certification, count]) => (
              <span
                key={certification}
                style={{
                  background: '#eef2ff',
                  color: '#3730a3',
                  borderRadius: 999,
                  padding: '6px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {certification}: {count}
              </span>
            ))}
            {certificationWaitlist.length === 0 && (
              <span style={{ color: '#6b7280', fontSize: 13 }}>No certification signups yet.</span>
            )}
          </div>

          <div className="table-wrapper">
            <table className="reservations-table" style={{ minWidth: 1280, fontSize: '13px' }}>
              <thead>
                <tr>
                  <th className="sticky-col" style={{ minWidth: 260, position: 'sticky', left: 0, background: 'white', zIndex: 10 }}>
                    Certifications
                  </th>
                  <th style={{ minWidth: 120 }}>Type</th>
                  <th style={{ minWidth: 220 }}>
                    Name
                  </th>
                  <th style={{ minWidth: 260 }}>Email</th>
                  <th style={{ minWidth: 420 }}>Why they signed up</th>
                  <th style={{ minWidth: 220 }}>Signed Up</th>
                  <th style={{ minWidth: 120 }}>Source</th>
                </tr>
              </thead>
              <tbody>
                {certificationWaitlist.map((entry, index) => (
                  <tr key={entry.id} className={index % 2 === 0 ? 'even' : 'odd'}>
                    <td
                      className="sticky-col"
                      style={{
                        fontWeight: 600,
                        position: 'sticky',
                        left: 0,
                        background: index % 2 === 0 ? '#f9fafb' : 'white',
                        zIndex: 9,
                      }}
                    >
                      {waitlistCertificationTitles(entry).join(', ') || '—'}
                    </td>
                    <td style={{ textTransform: 'capitalize' }}>{entry.certificationType || '—'}</td>
                    <td>{entry.name || '—'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{entry.email || '—'}</td>
                    <td style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{entry.reason || '—'}</td>
                    <td>{formatSignupDate(entry.createdAt)}</td>
                    <td>{entry.source || '—'}</td>
                  </tr>
                ))}
                {certificationWaitlist.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: '1rem', color: '#6b7280' }}>
                      No certification waitlist signups found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderTeachers = () => {
    const formatCreatedDate = (v: any) => {
      if (!v) return '—';
      try {
        const d = typeof v?.toDate === 'function' ? v.toDate() : (v instanceof Date ? v : new Date(v));
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleDateString();
      } catch (_) {
        return '—';
      }
    };

    return (
      <div className="teachers-tab-container">
        <div className="admin-content-card">
          <div className="teacher-actions">
            <button onClick={() => setIsCreatingTeacher(true)} className="add-squad-btn">
              ✨ Add New Teacher
            </button>
            <button
              onClick={() => navigate('/app/admin/teacher-availability')}
              className="add-squad-btn"
              style={{ marginLeft: 10 }}
            >
              📅 Availability
            </button>
            <button
              onClick={() => navigate('/app/admin/teacher-map')}
              className="add-squad-btn"
              style={{ marginLeft: 10 }}
            >
              🗺️ Map
            </button>
            {userData?.isAdmin && (
              <button
                onClick={() => setShowCoriBatchExport(true)}
                className="add-squad-btn"
                style={{ marginLeft: 10, background: '#7c3aed' }}
              >
                🛡️ Export Pending CORI
              </button>
            )}
            {userData?.isAdmin && (
              <button
                onClick={handleSyncAllPublicTeachers}
                className="add-squad-btn"
                style={{ marginLeft: 10, background: syncingPublicTeachers ? '#9ca3af' : '#10b981' }}
                disabled={syncingPublicTeachers}
              >
                {syncingPublicTeachers ? '⏳ Syncing...' : '🌐 Sync Public Profiles'}
              </button>
            )}
          </div>
          {showCoriBatchExport && <CoriBatchExportModal onClose={() => setShowCoriBatchExport(false)} onExported={setMessage} />}
          <p style={{ color: '#6b7280', marginTop: '1rem', marginBottom: '1rem' }}>
            Click any cell to edit. All fields are optional and will be added to teacher profiles if not already present.
          </p>
          <div className="filters-container" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Staff status</label>
              <select value={teacherStaffStatusFilter} onChange={(e) => setTeacherStaffStatusFilter(e.target.value as any)}>
                <option value="active">Active</option>
                <option value="sub">Sub</option>
                <option value="inactive">Inactive</option>
                <option value="applicant">Applicant</option>
                <option value="offboarded">Offboarded</option>
                <option value="all">All</option>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>State</label>
              <select value={teacherStateFilter} onChange={(e) => setTeacherStateFilter(e.target.value)}>
                <option value="all">All</option>
                {teacherStateOptions.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Has car</label>
              <select value={teacherHasCarFilter} onChange={(e) => setTeacherHasCarFilter(e.target.value as any)}>
                <option value="all">All</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Subject</label>
              <select value={teacherSubjectFilter} onChange={(e) => setTeacherSubjectFilter(e.target.value)}>
                <option value="all">All</option>
                {teacherSubjectOptions.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Showing</label>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700 }}>
                {filteredTeachers.length} / {teachers.length}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>&nbsp;</label>
              <button
                type="button"
                className="add-squad-btn"
                style={{ background: '#9ca3af' }}
                onClick={() => {
                  setTeacherStateFilter('all');
                  setTeacherHasCarFilter('all');
                  setTeacherSubjectFilter('all');
                  setTeacherStaffStatusFilter('active');
                }}
              >
                Clear
              </button>
            </div>
          </div>
          <div className="table-wrapper">
            <table className="reservations-table" style={{ minWidth: 2600, fontSize: '13px' }}>
              <thead>
                <tr>
                  <th className="sticky-col" style={{ minWidth: 200, position: 'sticky', left: 0, background: 'white', zIndex: 10 }}>Name</th>
                  <th style={{ minWidth: 70 }}>Public</th>
                  <th style={{ minWidth: 100 }}>Headshot</th>
                  <th style={{ minWidth: 250 }}>Bio</th>
                  <th style={{ minWidth: 100 }}>Email</th>
                  <th style={{ minWidth: 120 }}>Staff Status</th>
                  <th style={{ minWidth: 120 }}>Created</th>
                  <th style={{ minWidth: 120 }}>Phone</th>
                  <th style={{ minWidth: 260 }}>Shipping Address</th>
                  <th style={{ minWidth: 220 }}>Org</th>
                  <th style={{ minWidth: 100 }}>Hourly Rate</th>
                  <th style={{ minWidth: 80 }}>Has Car</th>
                  <th style={{ minWidth: 80 }}>Devices</th>
                  <th style={{ minWidth: 200 }}>Emergency Contact</th>
                  <th style={{ minWidth: 120 }}>Time Zone</th>
                  <th style={{ minWidth: 100 }}>Start Date</th>
                  <th style={{ minWidth: 100 }}>Teacher Type</th>
                  <th style={{ minWidth: 150 }}>Subjects</th>
                  <th style={{ minWidth: 160 }}>Slack Email</th>
                  <th style={{ minWidth: 200 }}>LinkedIn</th>
                  <th style={{ minWidth: 150 }}>Overture BG Check</th>
                  <th style={{ minWidth: 170 }}>Mandatory Reporting</th>
                  <th style={{ minWidth: 120 }}>CYC Cleared</th>
                  <th style={{ minWidth: 180 }}>Signed Contracts</th>
                  <th style={{ minWidth: 170 }}>Teaching Practice Complete</th>
                  <th style={{ minWidth: 240 }}>First Class Observed</th>
                  <th style={{ minWidth: 170 }}>Partner Clearances</th>
                  <th style={{ minWidth: 160 }}>Gusto Employee ID</th>
                  <th style={{ minWidth: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...filteredTeachers].sort((a, b) => {
                  // Sort offboarded teachers to the bottom
                  const aOffboarded = a.staffStatus === 'offboarded';
                  const bOffboarded = b.staffStatus === 'offboarded';
                  if (aOffboarded && !bOffboarded) return 1;
                  if (!aOffboarded && bOffboarded) return -1;
                  // Within each group, sort alphabetically by name
                  return (a.displayName || '').localeCompare(b.displayName || '');
                }).map((teacher, index) => (
                  <tr key={teacher.id} className={index % 2 === 0 ? 'even' : 'odd'}>
                    <td className="sticky-col" style={{ fontWeight: 600, position: 'sticky', left: 0, background: index % 2 === 0 ? '#f9fafb' : 'white', zIndex: 9 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: '100%' }}>
                          <div style={{ minWidth: 0, whiteSpace: 'nowrap' }}>
                            {renderEditableTeacherCell(teacher, 'displayName', teacher.displayName, 'text')}
                          </div>
                          {teacher.isAdmin && (
                            <span style={{
                              background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                              color: '#fff',
                              fontSize: '0.65rem',
                              padding: '0.18rem 0.4rem',
                              borderRadius: '999px',
                              fontWeight: 800,
                              letterSpacing: '0.02em',
                              whiteSpace: 'nowrap',
                              flex: '0 0 auto'
                            }}>
                              ADMIN
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          style={{
                            border: '1px solid rgba(79, 70, 229, 0.18)',
                            background: 'rgba(79, 70, 229, 0.08)',
                            color: '#4f46e5',
                            borderRadius: 999,
                            padding: '4px 9px',
                            fontSize: 11,
                            fontWeight: 800,
                            lineHeight: 1.1,
                            cursor: 'pointer',
                            boxShadow: 'none',
                            whiteSpace: 'nowrap'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/app/admin/teachers/${teacher.id}`, {
                              state: {
                                from: 'admin-teachers',
                                backTo: '/app/admin',
                                backState: { adminTab: 'users', adminUserTab: 'teachers' },
                              },
                            });
                          }}
                        >
                          Open profile
                        </button>
                      </div>
                    </td>
                    {/* Public checkbox */}
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!teacher.isPublic}
                        onChange={() => handleToggleTeacherPublic(teacher.id, !!teacher.isPublic)}
                        disabled={teacher.staffStatus === 'offboarded' || teacher.staffStatus === 'applicant'}
                        title={teacher.staffStatus === 'offboarded' || teacher.staffStatus === 'applicant' 
                          ? 'Cannot make offboarded/applicant teachers public' 
                          : 'Toggle public visibility on marketing website'}
                        style={{ 
                          width: 18, 
                          height: 18, 
                          cursor: teacher.staffStatus === 'offboarded' || teacher.staffStatus === 'applicant' ? 'not-allowed' : 'pointer',
                          opacity: teacher.staffStatus === 'offboarded' || teacher.staffStatus === 'applicant' ? 0.5 : 1
                        }}
                      />
                    </td>
                    {/* Headshot upload */}
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        {teacher.headshotUrl ? (
                          <img 
                            src={teacher.headshotUrl} 
                            alt={teacher.displayName || 'Headshot'} 
                            style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: '50%', 
                            background: 'linear-gradient(135deg, #667eea, #764ba2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: 16
                          }}>
                            {(teacher.displayName || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <label style={{ 
                          fontSize: 10, 
                          color: '#6366f1', 
                          cursor: uploadingHeadshotForTeacher === teacher.id ? 'wait' : 'pointer',
                          textDecoration: 'underline'
                        }}>
                          {uploadingHeadshotForTeacher === teacher.id ? '⏳' : '📷 Upload'}
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleTeacherHeadshotUpload(teacher.id, file);
                              e.target.value = '';
                            }}
                            disabled={uploadingHeadshotForTeacher === teacher.id}
                          />
                        </label>
                      </div>
                    </td>
                    {/* Bio - textarea for long text */}
                    <td>{renderEditableTeacherCell(teacher, 'bio', (teacher as any).bio, 'textarea')}</td>
                    <td style={{ fontSize: '12px' }}>{teacher.email || '—'}</td>
                    <td>{renderEditableTeacherCell(teacher, 'staffStatus', teacher.staffStatus, 'staffStatus')}</td>
                    <td style={{ fontSize: 12 }}>{formatCreatedDate((teacher as any).createdAt)}</td>
                    <td>{renderEditableTeacherCell(teacher, 'phoneNumber', teacher.phoneNumber, 'phone')}</td>
                    <td style={{ fontSize: 12 }}>
                      {(() => {
                        const ship = (teacher as any)?.shippingAddress || null;
                        const line1 = String(ship?.line1 || '').trim();
                        const line2 = String(ship?.line2 || '').trim();
                        const city = String(ship?.city || '').trim();
                        const state = String(ship?.state || '').trim();
                        const zip = String(ship?.zip || '').trim();
                        const country = String(ship?.country || '').trim();
                        const parts1 = [line1, line2].filter(Boolean).join(' ');
                        const parts2 = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                        const label = [parts1, parts2, country].filter(Boolean).join(' • ');
                        return label || '—';
                      })()}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {String((teacher as any)?.organization || '').trim() || '—'}
                    </td>
                    <td>{renderEditableTeacherCell(teacher, 'hourlyRate', teacher.hourlyRate, 'number')}</td>
                    <td>{renderEditableTeacherCell(teacher, 'hasCar', teacher.hasCar, 'boolean')}</td>
                    <td>{renderEditableTeacherCell(teacher, 'deviceCount', teacher.deviceCount, 'number')}</td>
                    <td>{renderEditableTeacherCell(teacher, 'emergencyContact', teacher.emergencyContact, 'emergencyContact')}</td>
                    <td>{renderEditableTeacherCell(teacher, 'timeZone', teacher.timeZone, 'timezone')}</td>
                    <td>{renderEditableTeacherCell(teacher, 'startDate', teacher.startDate, 'date')}</td>
                    <td>{renderEditableTeacherCell(teacher, 'teacherType', teacher.teacherType, 'teacherType')}</td>
                    <td>{renderEditableTeacherCell(teacher, 'subjects', teacher.subjects, 'subjects')}</td>
                    <td>{renderEditableTeacherCell(teacher, 'slackEmail', (teacher as any).slackEmail, 'text')}</td>
                    <td>{renderEditableTeacherCell(teacher, 'linkedinUrl', (teacher as any).linkedinUrl, 'text')}</td>
                    <td>{renderFileCell(teacher, (teacher as any).overtureBgCheckFile, 'overtureBgCheckFile')}</td>
                    <td>{renderFileCell(teacher, (teacher as any).mandatoryReportingCertificationFile, 'mandatoryReportingCertificationFile')}</td>
                    <td>{renderBooleanToggleCell(teacher, 'cycCleared')}</td>
                    <td>{renderSignedContractsCell(teacher)}</td>
                    <td>{renderBooleanToggleCell(teacher, 'ogTrainingComplete')}</td>
                    <td>{renderFirstClassObservedCell(teacher)}</td>
                    <td>
                      <button
                        type="button"
                        className="add-squad-btn"
                        style={{ padding: '6px 10px', fontSize: 12 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSchoolClearanceModalTeacherId(teacher.id);
                          setSchoolClearanceUploadSchoolId('');
                          setSchoolClearanceUploadType('cori');
                          setSchoolClearanceUploadOtherLabel('');
                          loadTeacherSchoolClearances(teacher.id);
                        }}
                      >
                        Manage
                      </button>
                    </td>
                    <td>{renderEditableTeacherCell(teacher, 'gustoEmployeeId', (teacher as any).gustoEmployeeId, 'text')}</td>
                    <td className="squad-list-actions">
                      <button
                        onClick={() => handleDeleteUser(teacher.id, 'teacher')}
                        className="delete-btn"
                      >
                        DELETE
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {schoolClearanceModalTeacherId && (
          <div
            className="modal-overlay"
            onClick={() => setSchoolClearanceModalTeacherId(null)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 9998,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px'
            }}
          >
            <div
              className="admin-content-card"
              style={{
                width: '95%',
                maxWidth: '1100px',
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                borderRadius: '16px',
                padding: '18px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>
                  Partner clearances
                </div>
                <button className="delete-btn" onClick={() => setSchoolClearanceModalTeacherId(null)} style={{ background: '#1f2937' }}>
                  Close
                </button>
              </div>

              {schoolClearanceModalError && (
                <div style={{ marginBottom: 10, color: '#b45309', fontWeight: 700 }}>
                  {schoolClearanceModalError}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 1fr', gap: 10, alignItems: 'end', marginBottom: 14 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#374151' }}>Partner</span>
                  <select value={schoolClearanceUploadSchoolId} onChange={(e) => setSchoolClearanceUploadSchoolId(e.target.value)}>
                    <option value="">Select partner…</option>
                    {Array.from(new Set(partnerLocations.map(location => location.partnerId).filter(Boolean))).map((partnerId) => (
                      <option key={partnerId} value={partnerId}>
                        {partnerId}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#374151' }}>Type</span>
                  <select value={schoolClearanceUploadType} onChange={(e) => setSchoolClearanceUploadType(e.target.value as any)}>
                    <option value="cori">CORI</option>
                    <option value="fingerprint">Fingerprint</option>
                    <option value="tb_test">TB test</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#374151' }}>Upload</span>
                  <label style={{ fontSize: 13, fontWeight: 800, color: '#2563eb', cursor: uploadingTeacherComplianceKey ? 'wait' : 'pointer', textDecoration: 'underline' }}>
                    {uploadingTeacherComplianceKey?.includes(':schoolClearance:') ? 'Uploading…' : 'Upload PDF or image'}
                    <input
                      type="file"
                      accept="application/pdf,image/*"
                      style={{ display: 'none' }}
                      disabled={!!uploadingTeacherComplianceKey}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f && schoolClearanceModalTeacherId) uploadTeacherSchoolClearance(schoolClearanceModalTeacherId, f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
              </div>

              {schoolClearanceUploadType === 'other' && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: '#374151' }}>Other label</span>
                    <input
                      value={schoolClearanceUploadOtherLabel}
                      onChange={(e) => setSchoolClearanceUploadOtherLabel(e.target.value)}
                      placeholder="e.g., District portal clearance"
                    />
                  </label>
                </div>
              )}

              <div style={{ overflowY: 'auto', paddingRight: 6, flex: 1 }}>
                {schoolClearanceModalLoading ? (
                  <div style={{ color: '#6b7280', fontWeight: 700 }}>Loading…</div>
                ) : schoolClearanceModalRows.length === 0 ? (
                  <div style={{ color: '#6b7280', fontWeight: 700 }}>No uploads yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {schoolClearanceModalRows.map((row) => (
                      <div key={row.schoolId} style={{ border: '1px solid rgba(15, 23, 42, 0.12)', borderRadius: 12, padding: 12 }}>
                        <div style={{ fontWeight: 900 }}>
                          {row.schoolName || row.schoolId}
                        </div>
                        {Array.isArray(row.items) && row.items.length > 0 ? (
                          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {row.items.map((it) => {
                              const label =
                                it.type === 'cori' ? 'CORI'
                                  : it.type === 'fingerprint' ? 'Fingerprint'
                                    : it.type === 'tb_test' ? 'TB test'
                                      : it.type === 'other' ? (it.typeLabel || 'Other')
                                        : it.type;
                              return (
                                <a key={it.id} href={it.file?.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 800 }}>
                                  {label}{it.file?.filename ? ` — ${it.file.filename}` : ''}
                                </a>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ marginTop: 8, color: '#6b7280', fontWeight: 700 }}>No items</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- Student duplicate detection (first name + last initial) ---
  type ParsedName = {
    first: string;
    lastInitial: string;
    fullLast: string;
    hasFullLast: boolean;
  };

  const parseName = useCallback((displayName?: string | null): ParsedName | null => {
    if (!displayName) return null;
    const parts = displayName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return null;
    const first = parts[0].toLowerCase();
    const lastRaw = parts.length > 1 ? parts[parts.length - 1] : '';
    const lettersOnly = lastRaw.replace(/[^a-z]/gi, '');
    if (!lettersOnly) return null;
    const hasFullLast = lettersOnly.length > 1;
    const lastInitial = lettersOnly[0].toLowerCase();
    const fullLast = hasFullLast ? lettersOnly.toLowerCase() : '';
    return { first, lastInitial, fullLast, hasFullLast };
  }, []);

  const duplicateGroups = useMemo(() => {
    const groups: Record<string, { parsed: ParsedName; student: User }[]> = {};
    students.forEach((student) => {
      const parsed = parseName(student.displayName);
      if (!parsed || !parsed.lastInitial) return;
      const nameLower = (student.displayName || '').toLowerCase();
      if (nameLower.includes('test')) return;
      const key = `${parsed.first}|${parsed.lastInitial}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push({ parsed, student });
    });

    const result: { key: string; label: string; members: User[] }[] = [];
    Object.entries(groups).forEach(([key, arr]) => {
      if (arr.length < 2) return;
      const hasInitialOnly = arr.some((a) => !a.parsed.hasFullLast);
      const fullLasts = arr.filter((a) => a.parsed.hasFullLast).map((a) => a.parsed.fullLast);
      const hasSameFull = new Set(fullLasts).size < fullLasts.length && fullLasts.length > 0;
      // Duplicates if there is an initial-only entry OR two entries share the same full last name.
      if (hasInitialOnly || hasSameFull) {
        const [first, initial] = key.split('|');
        result.push({
          key,
          label: `${first.charAt(0).toUpperCase()}${first.slice(1)} ${initial.toUpperCase()}.`,
          members: arr.map((a) => a.student),
        });
      }
    });
    // Sort for stable display
    return result.sort((a, b) => a.label.localeCompare(b.label));
  }, [students, parseName]);

  const activeStudentMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    squads
      .filter((s) => s.status !== 'archived')
      .forEach((squad) => {
        (squad.studentIds || []).forEach((sid) => {
          map[sid] = true;
        });
      });
    return map;
  }, [squads]);

  const PICKUP_LABELS: Record<string, string> = {
    aftercare: 'Aftercare',
    guardian_pickup: 'Guardian Pick-up',
    bike_walk: 'Bike/Walk',
    other: 'Other',
  };

  const formatPickupPreference = (pp: any): string => {
    if (!pp) return '';
    // Back-compat: some records may store as string[]
    const options: string[] = Array.isArray(pp)
      ? pp
      : Array.isArray(pp?.options)
        ? pp.options
        : [];

    const cleaned = options
      .filter((v) => typeof v === 'string')
      .map((v) => v.trim())
      .filter(Boolean);

    const unique = Array.from(new Set(cleaned));
    if (unique.length === 0) return '';

    const labels = unique.map((k) => PICKUP_LABELS[k] || k);
    const otherText = typeof pp?.otherText === 'string' ? pp.otherText.trim() : '';
    if (unique.includes('other') && otherText) {
      return `${labels.join(', ')} (Other: ${otherText})`;
    }
    return labels.join(', ');
  };

  const beginEditPickupForStudent = (student: User) => {
    setEditingPickupStudentId(student.id);
    const pp: any = (student as any).pickupPreference;
    const options: string[] = Array.isArray(pp)
      ? pp
      : Array.isArray(pp?.options)
        ? pp.options
        : [];
    setPickupDraftOptions(
      options
        .filter((v) => typeof v === 'string')
        .map((v) => v.trim())
        .filter(Boolean)
    );
    setPickupDraftOtherText(typeof pp?.otherText === 'string' ? pp.otherText : '');
  };

  const cancelEditPickup = () => {
    setEditingPickupStudentId(null);
    setPickupDraftOptions([]);
    setPickupDraftOtherText('');
    setSavingPickup(false);
  };

  const togglePickupDraftOption = (key: string) => {
    setPickupDraftOptions((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      if (!next.includes('other')) setPickupDraftOtherText('');
      return next;
    });
  };

  const savePickupForStudent = async (studentId: string) => {
    setSavingPickup(true);
    try {
      const options = Array.from(new Set(pickupDraftOptions.map((v) => String(v).trim()).filter(Boolean)));
      const otherText = pickupDraftOtherText.trim();
      const payload =
        options.length > 0
          ? {
              options,
              ...(options.includes('other') && otherText ? { otherText } : {}),
            }
          : null;

      await updateDoc(doc(db, 'users', studentId), { pickupPreference: payload });

      setStudents((prev) =>
        prev.map((s) =>
          s.id === studentId ? ({ ...(s as any), pickupPreference: payload } as any) : s
        )
      );

      cancelEditPickup();
    } catch (e) {
      console.error('Failed to update pickup preference', e);
      void window.appAlert('Failed to update pickup preference. Please try again.');
      setSavingPickup(false);
    }
  };

  const renderStudents = () => {
    // Count active students (excluding test students)
    const activeStudentsCount = students.filter(student => {
      const isActive = !!activeStudentMap[student.id];
      const isTestStudent = (student.displayName || '').toLowerCase().includes('test');
      return isActive && !isTestStudent;
    }).length;

    const resolveNonArchivedStudentSquads = (student: User): Squad[] => {
      // Primary source of truth: squads.studentIds membership
      const byMembership = squads.filter(s => (s.studentIds || []).includes(student.id));
      const byIdsFallback = (() => {
        const ids = Array.isArray((student as any).squadIds) ? ((student as any).squadIds as string[]) : [];
        if (!ids || ids.length === 0) return [];
        return squads.filter(s => ids.includes(s.id));
      })();
      const merged = byMembership.length > 0 ? byMembership : byIdsFallback;
      return merged.filter(s => s.status !== 'archived');
    };

    const filteredStudents = students.filter(student => {
      // Filter by squad
      if (studentSquadFilter !== 'all') {
        const squad = squads.find(s => s.id === studentSquadFilter);
        if (!squad || !squad.studentIds.includes(student.id)) {
          return false;
        }
      }

      // Filter by parent name (type-ahead)
      if (studentParentFilter.trim()) {
        const filterLower = studentParentFilter.toLowerCase();
        const hasMatchingParent = student.parentIds?.some(parentId => {
          const parentUser = parents.find(u => u.id === parentId);
          if (!parentUser) return false;
          const displayName = (parentUser.displayName || '').toLowerCase();
          const email = (parentUser.email || '').toLowerCase();
          return displayName.includes(filterLower) || email.includes(filterLower);
        });
        const parentEmailMatch = (student.parentEmail || '').toLowerCase().includes(filterLower);
        if (!hasMatchingParent && !parentEmailMatch) {
          return false;
        }
      }

      // Filter by canonical location (type-ahead).
      if (studentSchoolFilter.trim()) {
        const studentSquads = squads.filter(s => s.studentIds?.includes(student.id));
        const hasMatchingLocation = studentSquads.some(squad => {
          const location = partnerLocations.find(item => item.id === squad.locationId);
          return location && (location.name || '').toLowerCase().includes(studentSchoolFilter.toLowerCase());
        });
        if (!hasMatchingLocation) {
          return false;
        }
      }

      return true;
    });

    return (
      <div className="students-tab-container">
        <div className="admin-content-card">
          <div className="student-actions">
            <button onClick={() => setIsCreatingStudent(true)} className="add-squad-btn">
              ✨ Add New Student
            </button>
            <button
              onClick={() => setShowDuplicateModal(true)}
              className="add-squad-btn"
              style={{ marginLeft: '10px', background: '#1f2937' }}
            >
              👀 View Duplicates
            </button>
          </div>
          <div className="data-section">
            <h3>📋 All Students ({filteredStudents.length}) | Active: {activeStudentsCount}</h3>
            <div className="student-filter" style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label>Squad:</label>
                <select 
                  value={studentSquadFilter} 
                  onChange={(e) => setStudentSquadFilter(e.target.value)}
                  className="admin-select"
                  style={{ minWidth: '200px' }}
                >
                  <option value="all">All Squads</option>
                  {squads.map(squad => (
                    <option key={squad.id} value={squad.id}>{squad.title}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label>Parent:</label>
                <input
                  type="text"
                  value={studentParentFilter}
                  onChange={(e) => setStudentParentFilter(e.target.value)}
                  placeholder="Type to filter by parent name/email..."
                  className="admin-input"
                  style={{ minWidth: '250px' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label>Location:</label>
                <input
                  type="text"
                  value={studentSchoolFilter}
                  onChange={(e) => setStudentSchoolFilter(e.target.value)}
                  placeholder="Type to filter by location..."
                  className="admin-input"
                  style={{ minWidth: '200px' }}
                  list="location-options"
                />
                <datalist id="location-options">
                  {partnerLocations.map(location => (
                    <option key={location.id} value={location.name} />
                  ))}
                </datalist>
              </div>
              {(studentSquadFilter !== 'all' || studentParentFilter || studentSchoolFilter) && (
                <button
                  onClick={() => {
                    setStudentSquadFilter('all');
                    setStudentParentFilter('');
                    setStudentSchoolFilter('');
                  }}
                  className="add-squad-btn"
                  style={{ padding: '6px 12px', fontSize: '12px', background: '#6b7280' }}
                >
                  Clear Filters
                </button>
              )}
            </div>
            <table className="reservations-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Squads</th>
                  <th>Parent</th>
                  <th>Photo</th>
                  <th>Parent Email</th>
                  <th>Pickup Preference</th>
                  <th>Muse Cash</th>
                  <th>Selected Muse</th>
                  <th>OG</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents
                  .sort((a, b) => {
                    const aActive = activeStudentMap[a.id] ? 1 : 0;
                    const bActive = activeStudentMap[b.id] ? 1 : 0;
                    if (aActive !== bActive) return bActive - aActive; // active first
                    return (a.displayName || '').localeCompare(b.displayName || '');
                  })
                  .map((student, index) => {
                    const isActive = !!activeStudentMap[student.id];
                    const isEditingPickup = editingPickupStudentId === student.id;
                    const studentSquads = resolveNonArchivedStudentSquads(student);
                    return (
                  <tr key={student.id} className={index % 2 === 0 ? 'even' : 'odd'} onClick={() => setStudentToEdit(student)}>
                    <td>{student.displayName}</td>
                    <td>
                      <span style={{ fontWeight: 700, color: isActive ? '#16a34a' : '#f97316' }}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        if (studentSquads.length === 0) return <span style={{ color: '#9ca3af', fontSize: '11px' }}>No squads</span>;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {studentSquads.map(squad => {
                              const truncatedTitle = squad.title.length > 12 ? squad.title.substring(0, 12) : squad.title;
                              return (
                                <div 
                                  key={squad.id} 
                                  style={{ fontSize: '11px', color: '#2563eb', lineHeight: '1.4', cursor: 'pointer', textDecoration: 'underline' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/app/squad/${squad.id}`);
                                  }}
                                  title={squad.title}
                                >
                                  {truncatedTitle}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {(() => {
                        const hasParent = !!(student.parentIds && student.parentIds.length > 0);
                        if (hasParent) {
                          return (
                            <span style={{ fontSize: '18px', position: 'relative', display: 'inline-block' }} title="Parent account linked">
                              🏠<span style={{ position: 'absolute', bottom: '-2px', right: '-2px', fontSize: '10px', fontWeight: 900, width: '12px', height: '12px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#22c55e', color: 'white', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>✓</span>
                            </span>
                          );
                        } else {
                          return (
                            <span style={{ fontSize: '18px', position: 'relative', display: 'inline-block' }} title="No linked parent account">
                              🏠<span style={{ position: 'absolute', bottom: '-2px', right: '-2px', fontSize: '10px', fontWeight: 900, width: '12px', height: '12px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ef4444', color: 'white', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>✕</span>
                            </span>
                          );
                        }
                      })()}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {(() => {
                        const hasParent = !!(student.parentIds && student.parentIds.length > 0);
                        const photoConsent = parentConsents[student.id];
                        if (photoConsent === true) {
                          return <span style={{ fontSize: '18px' }} title="Photo consent on file">📷</span>;
                        }
                        return <span style={{ color: '#9ca3af', fontSize: '12px' }} title={hasParent ? "No photo consent" : "No parent account"}>—</span>;
                      })()}
                    </td>
                    <td>{student.parentEmail || 'N/A'}</td>
                    <td
                      onClick={(e) => {
                        // keep click from opening StudentInfoModal while editing
                        if (isEditingPickup) e.stopPropagation();
                      }}
                      style={{ minWidth: 260 }}
                    >
                      {!isEditingPickup ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span>{formatPickupPreference((student as any).pickupPreference) || '—'}</span>
                          <button
                            className="add-squad-btn"
                            style={{ padding: '4px 8px', fontSize: 12 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              beginEditPickupForStudent(student);
                            }}
                            title="Edit pickup preference"
                          >
                            Edit
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gap: 6 }}>
                          <div style={{ display: 'grid', gap: 4 }}>
                            {Object.entries(PICKUP_LABELS).map(([key, label]) => (
                              <label key={key} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                                <input
                                  type="checkbox"
                                  checked={pickupDraftOptions.includes(key)}
                                  onChange={() => togglePickupDraftOption(key)}
                                  disabled={savingPickup}
                                />
                                <span>{label}</span>
                              </label>
                            ))}
                          </div>
                          {pickupDraftOptions.includes('other') && (
                            <input
                              type="text"
                              value={pickupDraftOtherText}
                              onChange={(e) => setPickupDraftOtherText(e.target.value)}
                              placeholder="Other (please specify)"
                              disabled={savingPickup}
                              style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 12 }}
                            />
                          )}
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              className="add-squad-btn"
                              style={{ padding: '4px 8px', fontSize: 12, background: '#16a34a' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                savePickupForStudent(student.id);
                              }}
                              disabled={savingPickup}
                            >
                              {savingPickup ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              className="add-squad-btn"
                              style={{ padding: '4px 8px', fontSize: 12, background: '#1f2937' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelEditPickup();
                              }}
                              disabled={savingPickup}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                    <td>{student.museCash || 0}</td>
                    <td>{student.selectedMuse?.name || 'None'}</td>
                    <td>{student.ogLevel ?? studentSquads.length}</td>
                    <td className="squad-list-actions">
                <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteUser(student.id, 'student');
                        }}
                        className="delete-btn"
                      >
                        DELETE
                </button>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
        </div>
        {showDuplicateModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowDuplicateModal(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 9998,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px'
            }}
          >
            <div
              className="admin-content-card"
              style={{
                width: '95%',
                maxWidth: '1200px',
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                borderRadius: '16px',
                padding: '18px',
                background: '#0b1224',
                color: '#f8fafc'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 700 }}>🔍 Potential Duplicates (First + Last Initial)</div>
                  <div style={{ color: '#cbd5e1', fontSize: '12px', marginTop: '4px' }}>
                    Showing groups where names share the same first name + last initial. Initial-only names are compared to full last names.
                  </div>
                </div>
                <button className="delete-btn" onClick={() => setShowDuplicateModal(false)} style={{ background: '#1f2937' }}>
                  Close
                </button>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <div style={{ background: '#111827', borderRadius: '10px', padding: '10px 12px', minWidth: '140px' }}>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>Groups</div>
                  <div style={{ fontWeight: 700, fontSize: '18px' }}>{duplicateGroups.length}</div>
                </div>
                <div style={{ background: '#111827', borderRadius: '10px', padding: '10px 12px', minWidth: '140px' }}>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>Students in groups</div>
                  <div style={{ fontWeight: 700, fontSize: '18px' }}>
                    {duplicateGroups.reduce((sum, g) => sum + g.members.length, 0)}
                  </div>
                </div>
              </div>

              <div
                style={{
                  overflowY: 'auto',
                  paddingRight: '6px',
                  flex: 1
                }}
              >
                {duplicateGroups.length === 0 ? (
                  <p style={{ marginTop: '1rem', color: '#cbd5e1' }}>No potential duplicates found.</p>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gap: '12px',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))'
                    }}
                  >
                    {duplicateGroups.map((group) => (
                      <div
                        key={group.key}
                        style={{
                          background: '#111827',
                          borderRadius: '12px',
                          padding: '12px',
                          border: '1px solid #1f2937',
                          minHeight: '120px'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <div style={{ fontWeight: 700 }}>{group.label}</div>
                          <div style={{ fontSize: '12px', color: '#9ca3af' }}>{group.members.length} match{group.members.length !== 1 ? 'es' : ''}</div>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <thead>
                            <tr style={{ color: '#9ca3af', textAlign: 'left' }}>
                              <th style={{ paddingBottom: '6px' }}>Name</th>
                          <th style={{ paddingBottom: '6px' }}>Parent?</th>
                          <th style={{ paddingBottom: '6px' }}>Active?</th>
                          <th style={{ paddingBottom: '6px' }}>OG</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.members
                              .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
                              .map((student) => {
                                const hasParent =
                                  !!(student as any).parentIds?.length ||
                                  !!(student as any).parentId ||
                                  !!student.parentEmail;
                                const ogLevel =
                                  (student as any).ogLevel ??
                                  student.ogLevel ??
                                  (Array.isArray((student as any).squadIds) ? (student as any).squadIds.length : 0);
                                const isActiveDup = !!activeStudentMap[student.id];
                                return (
                                  <tr key={student.id} style={{ borderTop: '1px solid #1f2937' }}>
                                    <td style={{ padding: '6px 4px', fontWeight: 600 }}>{student.displayName || 'Unnamed'}</td>
                                    <td style={{ padding: '6px 4px' }}>
                                      {hasParent ? (
                                        <span style={{ color: '#10b981', fontWeight: 700 }}>Yes</span>
                                      ) : (
                                        <span style={{ color: '#f97316', fontWeight: 700 }}>No</span>
                                      )}
                                    </td>
                                    <td style={{ padding: '6px 4px' }}>
                                      <span style={{ fontWeight: 700, color: isActiveDup ? '#16a34a' : '#f97316' }}>
                                        {isActiveDup ? 'Active' : 'Inactive'}
                                      </span>
                                    </td>
                                    <td style={{ padding: '6px 4px', fontWeight: 700, color: '#e5e7eb' }}>{ogLevel ?? '—'}</td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
  };

  const renderAchievements = () => (
    <div className="achievements-tab">
      <div className="form-section">
        <h3>📂 Export All Achievements from Firestore</h3>
        <p>Download a single JSON file containing all achievement definitions currently stored in your Firestore database.</p>
        <button
          onClick={handleDownloadAllAchievements}
          className="admin-button"
        >
          💾 Download Achievements from Firestore
        </button>
      </div>
      <div className="form-section">
        <h3>🗂️ Export Rocket Race TOMLs</h3>
        <p>Downloads every <code>.toml</code> under <code>gameData/rocket-race/</code> (including subfolders) from Firebase Storage.</p>
        <button
          onClick={handleDownloadRocketRaceTomls}
          className="admin-button"
          disabled={downloadingTomlsKey != null}
        >
          {downloadingTomlsKey === 'rocket-race' ? '⏳ Downloading…' : '⬇️ Download Rocket Race .toml Files'}
        </button>
      </div>
      <div className="form-section">
        <h3>🗂️ Export Real or AI TOMLs</h3>
        <p>Downloads every <code>.toml</code> under <code>gameData/real-or-ai/</code> (including subfolders) from Firebase Storage.</p>
        <button
          onClick={handleDownloadRealOrAiTomls}
          className="admin-button"
          disabled={downloadingTomlsKey != null}
        >
          {downloadingTomlsKey === 'real-or-ai' ? '⏳ Downloading…' : '⬇️ Download Real or AI .toml Files'}
        </button>
      </div>
      {/* Game Selection */}
      <div className="form-section">
        <h3>🎮 Select Game</h3>
        <select
          value={selectedGame}
          onChange={(e) => setSelectedGame(e.target.value)}
          className="admin-select"
          style={{ marginBottom: '1rem', padding: '10px' }}
        >
          <option value="">Select a game...</option>
          {Object.entries(GAME_REGISTRY).map(([gameId, game]) => (
            <option key={gameId} value={gameId}>
              {game.title} ({achievements[gameId]?.length || 0} achievements)
            </option>
          ))}
        </select>

        {/* Bulk Actions */}
        {selectedGame && (
          <div style={{ marginTop: '1rem', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {(selectedGame === 'my-favorite-musicians' || selectedGame === 'tune-testers' || selectedGame === 'step-skip-repeat' || selectedGame === 'rhythm-rally' || selectedGame === 'rhythm-puzzle' || selectedGame === 'melody-shaper') && (
              <button
                onClick={() => handleBulkAddAchievements(selectedGame)}
                disabled={loading}
                style={{
                  background: selectedGame === 'rhythm-puzzle' ? '#ff5722' : 
                              selectedGame === 'melody-shaper' ? '#00bcd4' :
                              selectedGame === 'tune-testers' ? '#8e44ad' :
                              selectedGame === 'step-skip-repeat' ? '#27ae60' : '#4caf50',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? '⏳ Adding...' : `🚀 Add All ${
                  selectedGame === 'my-favorite-musicians' ? 'My Favorite Musicians' : 
                  selectedGame === 'rhythm-rally' ? 'Rhythm Rally' :
                  selectedGame === 'rhythm-puzzle' ? 'Rhythm Puzzle' :
                  selectedGame === 'melody-shaper' ? 'Melody Shaper' :
                  selectedGame === 'tune-testers' ? 'Tune Testers' :
                  selectedGame === 'step-skip-repeat' ? 'Step, Skip, or Repeat' : 'Game'
                } Achievements`}
              </button>
            )}
            <button
              onClick={() => loadAchievements()}
              disabled={loading}
              style={{
                background: '#2196f3',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              🔄 Refresh
            </button>
            {seeders[selectedGame] && (
              <button
                onClick={handleSeedAchievements}
                className="admin-button"
                disabled={loading}
              >
                <span role="img" aria-label="seed">🌱</span> Seed Achievements
              </button>
            )}
          </div>
        )}
      </div>

      {/* Achievement Creation/Editing Form */}
      {selectedGame && (
        <div className="form-section">
          <h3>{editingAchievement ? '✏️ Edit Achievement' : '➕ Create New Achievement'}</h3>
          <form onSubmit={editingAchievement ? handleUpdateAchievement : handleCreateAchievement} className="admin-form">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Achievement ID * {editingAchievement && '(cannot be changed)'}</label>
                <input
                  type="text"
                  value={achievementForm.id}
                  onChange={(e) => setAchievementForm({ ...achievementForm, id: e.target.value })}
                  placeholder="e.g., first_completion"
                  required
                  disabled={!!editingAchievement}
                />
              </div>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={achievementForm.title}
                  onChange={(e) => setAchievementForm({ ...achievementForm, title: e.target.value })}
                  placeholder="Achievement Title"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={achievementForm.description || ''}
                onChange={(e) => setAchievementForm({ ...achievementForm, description: e.target.value })}
                placeholder="Describe how to unlock this achievement"
                rows={3}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Icon (Emoji)</label>
                <input
                  type="text"
                  value={achievementForm.iconUrl || '🏆'}
                  onChange={(e) => setAchievementForm({ ...achievementForm, iconUrl: e.target.value })}
                  placeholder="🏆"
                />
              </div>
              <div className="form-group">
                <label>Muse Cash Reward</label>
                <input
                  type="number"
                  value={achievementForm.museCashReward || 50}
                  onChange={(e) => setAchievementForm({ ...achievementForm, museCashReward: Number(e.target.value) })}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Rarity</label>
                <select
                  value={achievementForm.rarity || 'common'}
                  onChange={(e) => setAchievementForm({ ...achievementForm, rarity: e.target.value as any })}
                >
                  <option value="common">Common</option>
                  <option value="rare">Rare</option>
                  <option value="epic">Epic</option>
                  <option value="legendary">Legendary</option>
                </select>
              </div>
              <div className="form-group">
                <label>Sort Order</label>
                <input
                  type="number"
                  value={achievementForm.sortOrder || 1}
                  onChange={(e) => setAchievementForm({ ...achievementForm, sortOrder: Number(e.target.value) })}
                  min="1"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Type</label>
                <input
                  type="text"
                  value={achievementForm.type || 'milestone'}
                  onChange={(e) => setAchievementForm({ ...achievementForm, type: e.target.value })}
                  placeholder="milestone, completion, etc."
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  value={achievementForm.category || 'progression'}
                  onChange={(e) => setAchievementForm({ ...achievementForm, category: e.target.value })}
                  placeholder="progression, skill, knowledge"
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={achievementForm.implemented || false}
                    onChange={(e) => setAchievementForm({ ...achievementForm, implemented: e.target.checked })}
                  />
                  {' '}Implemented (functional in game)
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '1rem' }}>
              <button type="submit" disabled={loading} className="submit-button">
                {loading ? '⏳ Saving...' : editingAchievement ? '💾 Update Achievement' : '➕ Create Achievement'}
              </button>
              
              {editingAchievement && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingAchievement(null);
                    setAchievementForm({
                      id: '',
                      title: '',
                      description: '',
                      iconUrl: '🏆',
                      museCashReward: 50,
                      type: 'milestone',
                      rarity: 'common',
                      implemented: true,
                      sortOrder: 1,
                      category: 'progression'
                    });
                  }}
                  style={{
                    background: '#757575',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  ❌ Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Achievements List */}
      {selectedGame && achievements[selectedGame] && (
        <div className="data-section">
          <h3>🏆 {GAME_REGISTRY[selectedGame]?.title} Achievements ({achievements[selectedGame].length})</h3>
          
          {achievements[selectedGame].length === 0 ? (
            <div style={{ 
              padding: '2rem', 
              textAlign: 'center', 
              background: '#f5f5f5', 
              borderRadius: '10px',
              color: '#666'
            }}>
              <p>🚫 No achievements found for this game.</p>
              <p>Create some achievements using the form above!</p>
            </div>
          ) : (
            <div className="data-grid">
              {achievements[selectedGame]
                .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                .map(achievement => (
                  <div key={achievement.id} className="data-card">
                    <div className="card-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '1.5rem' }}>{achievement.iconUrl || '🏆'}</span>
                        <div>
                          <h4 style={{ margin: 0 }}>{achievement.title}</h4>
                          <small style={{ color: '#666' }}>ID: {achievement.id}</small>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button
                          onClick={() => handleEditAchievement(achievement)}
                          style={{
                            background: '#2196f3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            padding: '5px 10px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                          title="Edit achievement"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteAchievement(selectedGame, achievement.id)}
                          style={{
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            padding: '5px 10px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                          title="Delete achievement"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <div className="card-body">
                      {achievement.description && (
                        <p style={{ margin: '0 0 10px 0', fontStyle: 'italic' }}>
                          "{achievement.description}"
                        </p>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                        <p><strong>💰 Reward:</strong> {achievement.museCashReward} Muse Cash</p>
                        <p><strong>🏅 Rarity:</strong> <span style={{ 
                          textTransform: 'capitalize',
                          color: achievement.rarity === 'legendary' ? '#ff9800' : 
                                achievement.rarity === 'epic' ? '#9c27b0' :
                                achievement.rarity === 'rare' ? '#2196f3' : '#4caf50'
                        }}>{achievement.rarity || 'common'}</span></p>
                        <p><strong>📂 Category:</strong> {achievement.category || 'N/A'}</p>
                        <p><strong>🔧 Status:</strong> {achievement.implemented ? '✅ Active' : '⚠️ Not Implemented'}</p>
                      </div>
                      <p><strong>🔢 Sort Order:</strong> {achievement.sortOrder || 'N/A'}</p>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      
    </div>
  );

  const renderCalendar = () => (
    <div className="calendar-admin-tab">
      <div className="admin-content-card">
        <div className="calendar-actions">
            <button 
              className="add-event-btn"
              onClick={() => {
                setSelectedDateForModal(new Date());
                setIsScheduleModalOpen(true);
              }}
            >
              📅 Add Events
            </button>
            {userData?.isAdmin && (
              <button
                className="calculate-payroll-btn"
                onClick={() => setIsPayrollModalOpen(true)}
                title="Calculate teacher payroll for a date range"
              >
                💵 Calculate Payroll
              </button>
            )}
            {userData?.isAdmin && (
              <button
                className="get-report-btn"
                disabled={isSendingWeeklyReport}
                onClick={async () => {
                  try {
                    setIsSendingWeeklyReport(true);
                    const fn = httpsCallable(getFunctions(), 'sendWeeklyOpsReportNow');
                    const res: any = await fn({ weekOffset: 0 });
                    if (res?.data?.ok) {
                      setMessage('✅ Weekly report sent to Slack.');
                    } else {
                      const reason = res?.data?.reason ? ` (reason: ${String(res.data.reason)})` : '';
                      setMessage(`⚠️ Report attempted but did not confirm success.${reason} Check function logs.`);
                    }
                  } catch (e: any) {
                    console.error('Error sending weekly report:', e);
                    setMessage('❌ Failed to send weekly report. Check function logs.');
                  } finally {
                    setIsSendingWeeklyReport(false);
                  }
                }}
                title="Send the weekly schedule + sub report now"
              >
                {isSendingWeeklyReport ? '⏳ Sending…' : '📣 Get Report'}
              </button>
            )}
            {userData?.isAdmin && (
              <button
                className="get-report-btn"
                disabled={isSendingWeeklyReportNextWeek}
                onClick={async () => {
                  try {
                    setIsSendingWeeklyReportNextWeek(true);
                    const fn = httpsCallable(getFunctions(), 'sendWeeklyOpsReportNow');
                    const res: any = await fn({ weekOffset: 1 });
                    if (res?.data?.ok) {
                      setMessage('✅ Weekly report (next week) sent to Slack.');
                    } else {
                      const reason = res?.data?.reason ? ` (reason: ${String(res.data.reason)})` : '';
                      setMessage(`⚠️ Report (next week) attempted but did not confirm success.${reason} Check function logs.`);
                    }
                  } catch (e: any) {
                    console.error('Error sending next week weekly report:', e);
                    setMessage('❌ Failed to send weekly report (next week). Check function logs.');
                  } finally {
                    setIsSendingWeeklyReportNextWeek(false);
                  }
                }}
                title="Send the report for next week's classes"
              >
                {isSendingWeeklyReportNextWeek ? '⏳ Sending…' : '📣 Get Report (Next Week)'}
              </button>
            )}
        </div>
        <SquadCalendar 
          refreshKey={calendarRefreshKey}
          onDateClick={(date) => {
            setSelectedDateForModal(date);
            setIsScheduleModalOpen(true);
          }}
          onEventDelete={userData?.isAdmin ? async (eventId) => {
            try {
              await deleteDoc(doc(db, 'squadEvents', eventId));
              setMessage('✅ Event deleted successfully.');
              setCalendarRefreshKey(prev => prev + 1); // Refresh the calendar
            } catch (error) {
              console.error("Error deleting event:", error);
              setMessage('❌ Failed to delete event.');
            }
          } : undefined}
          onEventUpdate={async (event) => {
            try {
              const { id, mainTeacherId, start, end, originalEvent } = event;
              
              const startDate = new Date(originalEvent.start);
              const [startHour, startMinute] = start.split(':').map(Number);
              startDate.setHours(startHour, startMinute, 0, 0);

              const endDate = new Date(originalEvent.start);
              const [endHour, endMinute] = end.split(':').map(Number);
              endDate.setHours(endHour, endMinute, 0, 0);

              const eventRef = doc(db, 'squadEvents', id);
              await updateDoc(eventRef, {
                mainTeacherId: mainTeacherId,
                start: startDate,
                end: endDate
              });
              setMessage('✅ Event updated successfully.');
              setCalendarRefreshKey(prev => prev + 1);
            } catch (error) {
              console.error("Error updating event:", error);
              setMessage('❌ Failed to update event.');
            }
          }}
          teachers={teachers}
          squads={squads}
          eventClickAction="edit"
        />
      </div>
    </div>
  );

  const renderAbTesting = () => (
    <div>
      <div className="sub-nav">
        <button onClick={() => setActiveSubTab('forms')} className={activeSubTab === 'forms' ? 'active' : ''}>Manage Forms</button>
        <button onClick={() => setActiveSubTab('feedback')} className={activeSubTab === 'feedback' ? 'active' : ''}>Feedback</button>
        <button onClick={() => setActiveSubTab('campaigns')} className={activeSubTab === 'campaigns' ? 'active' : ''}>Campaigns</button>
        <button onClick={() => setActiveSubTab('results')} className={activeSubTab === 'results' ? 'active' : ''}>Results</button>
      </div>
      <div className="sub-nav-content">
        {activeSubTab === 'forms' && <FormEditor />}
        {activeSubTab === 'feedback' && <FeedbackViewer />}
        {activeSubTab === 'campaigns' && <CampaignsTab />}
        {activeSubTab === 'results' && <CampaignResults />}
      </div>
    </div>
  );

  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const h12 = h % 12 || 12;
    return `${h12}:${minutes}`;
  }

  return (
    <div className="admin-panel admin-panel-fullscreen" data-active-tab={activeTab}>
      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div 
          className="admin-sidebar-overlay"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Slide-out Sidebar for Admin */}
      <div className={`admin-sidebar ${isMobileSidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <h2>🛠️ Admin Menu</h2>
          <button 
            className="admin-sidebar-close"
            onClick={() => setIsMobileSidebarOpen(false)}
          >
            ×
          </button>
        </div>
        <nav className="admin-sidebar-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                if (tab.id === 'ops') {
                  navigate('/app/admin/ops');
                  setIsMobileSidebarOpen(false);
                  return;
                }
                setActiveTab(tab.id as any);
                setIsMobileSidebarOpen(false);
              }}
              className={`admin-sidebar-item ${activeTab === tab.id ? 'active' : ''}`}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
        <div className="admin-sidebar-footer">
          <button 
            className="admin-sidebar-back-btn"
            onClick={() => navigate('/app/squads')}
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {/* Compact Ribbon Header */}
      <div className="admin-ribbon">
        {/* Hamburger Menu Button */}
        <button
          className="admin-hamburger"
          onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
          aria-label="Toggle menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        {/* Home Button */}
        <button 
          className="admin-back-btn"
          onClick={() => navigate('/app/squads')}
          title="Home"
        >
          ← Home
        </button>

        {/* Tab Navigation - Desktop (now on left) */}
        <div className="admin-tabs-ribbon">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                if (tab.id === 'ops') {
                  navigate('/app/admin/ops');
                  return;
                }
                setActiveTab(tab.id as any);
              }}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Title (now on right) */}
        <h1 className="admin-ribbon-title">🛠️ Admin Control Panel</h1>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`admin-message ${message.includes('❌') ? 'error' : 'success'}`}>
          {message}
          <button onClick={() => setMessage('')} className="message-close">×</button>
        </div>
      )}

      {/* Tab Content */}
      <div className="admin-content">
        {activeTab === 'todos' && <AdminTodosPage />}
        {activeTab === 'statistics' && <StatisticsTab squads={squads} students={students} />}

        {activeTab === 'invoicing' && (
          <AdminInvoicesPageAny
            schools={partnerLocations as any}
            squads={squads}
            initialSchoolId={invoicingSchoolId}
            onConsumedInitialSchoolId={() => setInvoicingSchoolId(null)}
          />
        )}

        {activeTab === 'users' && renderUsers()}
        
        {activeTab === 'calendar' && renderCalendar()}

        {activeTab === 'learning' && <AdminLearningCenterTab />}

        {activeTab === 'curriculum' && <AdminCurriculumTab />}

        {activeTab === 'exports' && <AdminExportsTab />}

        {activeTab === 'achievements' && renderAchievements()}

        {activeTab === 'ab-testing' && renderAbTesting()}
      </div>

      <ManageSquadUsersModal
        isOpen={isManageModalOpen}
        onClose={handleCloseManageModal}
        squad={selectedSquad}
        onSquadUpdate={handleSquadUpdate}
      />

      <ScheduleEventModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onSave={async (eventData) => {
          try {
            // Ensure teacher is in the squad
            if (eventData.mainTeacherId && eventData.squadId) {
              const squad = squads.find(s => s.id === eventData.squadId);
              if (squad && !squad.teacherIds.includes(eventData.mainTeacherId)) {
                const squadRef = doc(db, 'squads', eventData.squadId);
                await updateDoc(squadRef, {
                  teacherIds: arrayUnion(eventData.mainTeacherId)
                });
              }
            }
            
            if (eventData.isRecurring) {
              const {
                squadId,
                title,
                startTime,
                endTime,
                seriesStart,
                seriesEnd,
                daysOfWeek,
                mainTeacherId,
              } = eventData;

              const batch = writeBatch(db);
              const eventsCollection = collection(db, 'squadEvents');

              const [startYear, startMonth, startDay] = seriesStart.split('-').map(Number);
              let currentDate = new Date(startYear, startMonth - 1, startDay);
              
              const [endYear, endMonth, endDay] = seriesEnd.split('-').map(Number);
              const lastDate = new Date(endYear, endMonth - 1, endDay);

              while (currentDate <= lastDate) {
                if (daysOfWeek.includes(currentDate.getDay())) {
                  const dateStr = currentDate.toISOString().split('T')[0];
                  
                  // Create events in Chicago timezone
                  const startDateTime = createChicagoDate(dateStr, startTime);
                  const endDateTime = createChicagoDate(dateStr, endTime);

                  const newEventRef = doc(eventsCollection);
                  batch.set(newEventRef, {
                    squadId,
                    title,
                    start: startDateTime,
                    end: endDateTime,
                    mainTeacherId: mainTeacherId || null,
                    // Events scheduled from the admin panel are considered manual teacher
                    // assignments when a main teacher is specified.
                    ...(mainTeacherId ? { mainTeacherOverridden: true } : {}),
                    assignmentKind: 'recurring_series',
                  });
                }
                currentDate.setDate(currentDate.getDate() + 1);
              }

              await batch.commit();
            } else {
              const { isRecurring, ...singleEventData } = eventData;
              const eventWithSquadId: any = {
                ...singleEventData,
                squadId: eventData.squadId, // Ensure squadId is included
                // One-off events scheduled from here are manual assignments when a main teacher is provided.
                ...(eventData.mainTeacherId ? { mainTeacherOverridden: true } : {}),
                assignmentKind: 'one_time',
              };
              // Remove undefined fields to satisfy Firestore constraints
              Object.keys(eventWithSquadId).forEach((k) => {
                if ((eventWithSquadId as any)[k] === undefined) delete (eventWithSquadId as any)[k];
              });
              await addDoc(collection(db, 'squadEvents'), eventWithSquadId);
            }
            setIsScheduleModalOpen(false);
            setCalendarRefreshKey(prev => prev + 1);
          } catch (error) {
            console.error("Error saving event:", error);
            void window.appAlert("Failed to save event. See console for details.");
          }
        }}
        squads={squads}
        teachers={teachers}
        selectedDate={selectedDateForModal}
      />

      <PayrollCalculatorModal
        isOpen={isPayrollModalOpen}
        onClose={() => setIsPayrollModalOpen(false)}
        teachers={teachers}
      />

      {studentToEdit && (
        <StudentInfoModal 
          student={studentToEdit}
          isOpen={!!studentToEdit}
          onClose={() => setStudentToEdit(null)}
          onStudentUpdated={handleUpdateStudent}
        />
      )}

      {isCreatingStudent && (
        <div className="confirm-delete-modal-overlay" onClick={() => setIsCreatingStudent(false)}>
          <div className="confirm-delete-modal" onClick={e => e.stopPropagation()}>
            <h3>Create New Student</h3>
            <form onSubmit={handleCreateStudent} className="admin-form">
              <div className="form-group">
                <label>Display Name *</label>
                <input
                  type="text"
                  value={studentForm.displayName}
                  onChange={(e) => setStudentForm({ ...studentForm, displayName: e.target.value })}
                  placeholder="Student Name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Starting Muse Cash</label>
                <input
                  type="number"
                  value={studentForm.museCash}
                  onChange={(e) => setStudentForm({ ...studentForm, museCash: Number(e.target.value) })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Starting Muse</label>
                <select
                  value={studentForm.selectedMuseName}
                  onChange={(e) => setStudentForm({ ...studentForm, selectedMuseName: e.target.value })}
                  required
                  className="admin-select"
                >
                  {allMuses.map(muse => (
                    <option key={muse.name} value={muse.name}>
                      {muse.name} ({muse.rarity})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setIsCreatingStudent(false)}>
                  Cancel
                </button>
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Student'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isCreatingTeacher && (
        <div className="confirm-delete-modal-overlay" onClick={() => setIsCreatingTeacher(false)}>
          <div className="confirm-delete-modal" onClick={e => e.stopPropagation()}>
            <h3>Create New Teacher</h3>
            <form onSubmit={handleCreateTeacher} className="admin-form">
              <div className="form-group">
                <label>Display Name *</label>
                <input type="text" value={teacherForm.displayName} onChange={e => setTeacherForm({...teacherForm, displayName: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" value={teacherForm.email} onChange={e => setTeacherForm({...teacherForm, email: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="text" value={teacherForm.password} onChange={e => setTeacherForm({...teacherForm, password: e.target.value})} />
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setIsCreatingTeacher(false)}>Cancel</button>
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Teacher'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isCreatingPartner && (
        <div className="confirm-delete-modal-overlay" onClick={() => setIsCreatingPartner(false)}>
          <div className="confirm-delete-modal" onClick={e => e.stopPropagation()}>
            <h3>Create New Partner</h3>
            <form onSubmit={handleCreatePartner} className="admin-form">
              <div className="form-group">
                <label>Display Name *</label>
                <input
                  type="text"
                  value={partnerForm.displayName}
                  onChange={e => setPartnerForm({ ...partnerForm, displayName: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Username (Email) *</label>
                <input
                  type="email"
                  value={partnerForm.email}
                  onChange={e => setPartnerForm({ ...partnerForm, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={partnerForm.phoneNumber}
                  onChange={e => setPartnerForm({ ...partnerForm, phoneNumber: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="text"
                  value={partnerForm.password}
                  onChange={e => setPartnerForm({ ...partnerForm, password: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setIsCreatingPartner(false)}>Cancel</button>
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Partner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {squadToDelete && (
        <div className="confirm-delete-modal-overlay">
          <div className="confirm-delete-modal">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete the squad "{squadToDelete.title}"?</p>
            <div className="confirm-delete-actions">
              <button onClick={() => setSquadToDelete(null)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleDeleteSquad} className="delete-confirm-btn" disabled={deletingSquadId === squadToDelete.id}>
                {deletingSquadId === squadToDelete.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel; 
